import App from './application-container';
import { uil_add_joint_smoothness, uil_remove_joint_smoothness } from './uil_continuity';
import {
    animate,
    disable_edit_reference_body_mode,
    hide_reference_partstudio_model,
    hide_reference_stl_model,
    toggle_grid,
    view_all,
} from './scene';
import * as THREE from 'three';
import HashTable from './hashtable';
import { next_frame, sleep } from '@vaaas/fpts/promise';
import { on_next_frame } from '@vaaas/fpts/timing';
import { unique, first } from '@vaaas/fpts/array'
import { pipe } from '@vaaas/fpts/function';
import { display_advanced_message_to_user } from './logging';
import { LOG, display_message_to_user, uid_messages_suppressed } from '@phi/logging/frontend';
import {
    hide_shadows,
    set_default_face_color,
    set_default_vertex_color,
    uid_unhighlight_all_objects,
    update_shadow_light_dimensions,
} from './colours_materials_appearance';
import { show_modal, hide_modal } from './modals';
import * as maths from './maths';
import * as v3 from './vector3';
import { vertices_of_face, find_all_pairs_in_unlistified_edge_list } from './lattice';
import {
    uil_get_edges_of_face,
    uil_get_edges_of_vertex,
    uil_is_joint_geom_smooth,
    uil_is_object_fixed,
    uil_is_edge_linear_geometry,
    uil_fix_objects,
    uil_unfix_objects,
    uil_is_vertex_of_face,
    uil_end_drag_manipulator,
    uil_get_edge_curvatures,
    uil_get_world_scaling_factor,
    uil_is_object_visible,
    uil_get_point_at_edge_param,
    uil_set_face_tessel,
    uil_move_vertex_to_coords,
    uil_delete_vertices,
    uil_is_joint_smooth,
  } from './ui_logic';
import { set_button_states } from './non_graphical_ui_actions';
import { FAINT_STRENGTH } from './vars_and_data_structures';
import { uid_calculate_viewport_object_bounding_box,
  point_is_in_viewport,
  uid_calculate_global_bounding_box,
  uid_show_rigid_move_manipulator,
  uid_deselect_all_objects,
  uid_select_object,
  uid_hide_rigid_move_manipulator,
} from './ui_display';
import { from_2d_canvas_coordinates_to_3d_world_coordinates, plane_ray_intersect } from './geometry_and_math';
import { pointer_normalised } from './pointer';
import { force_refresh } from './state/force-refresh';
import { Uid_tmp_ref_edge } from './graph_objects';

// ---------------------------------------------------
// Console binding
// ---------------------------------------------------

export function console_log(...xs){
    if (CONSOLE_LOGGING)
        console.log(xs.join(''));
};

export function console_log_xml() {
  // Add to the console.everything object
  var str = "";
  for (var i = 0; i < arguments.length; i++) {
    str += arguments[i];
  }
  console_log(str);
  if (console.everything)
    console.everything.push({"type":"log", "datetime":Date().toLocaleString(), "value":str})
}

export function onerror(msg, url, lineNo, columnNo, error) {
  var message = [
    'Message: ' + msg,
    'URL: ' + url,
    'Line: ' + lineNo,
    'Column: ' + columnNo,
    //'Error object: ' + JSON.stringify(error)
    'Error object: ' + error.message + error.stack
  ].join(' - ');
  if (console.everything)
    console.everything.push({"type":"error", "datetime":Date().toLocaleString(), "value":message});

  display_message_to_user(LOG.ERROR, "An error occured. Please hit the little envelope icon on the left side of the window to report this.");
};

export function bind_console_output() {
  if (console.everything === undefined)
  {
      console.everything = [];

      console.defaultLog = console.log.bind(console);
      console.log = function(){
          console.everything.push({"type":"log", "datetime":Date().toLocaleString(), "value":Array.from(arguments)});
          console.defaultLog.apply(console, arguments);
      }
      console.defaultError = console.error.bind(console);
      console.error = function(){
          console.everything.push({"type":"error", "datetime":Date().toLocaleString(), "value":Array.from(arguments)});
          console.defaultError.apply(console, arguments);
      }
      console.defaultWarn = console.warn.bind(console);
      console.warn = function(){
          console.everything.push({"type":"warn", "datetime":Date().toLocaleString(), "value":Array.from(arguments)});
          console.defaultWarn.apply(console, arguments);
      }
      console.defaultDebug = console.debug.bind(console);
      console.debug = function(){
          console.everything.push({"type":"debug", "datetime":Date().toLocaleString(), "value":Array.from(arguments)});
          console.defaultDebug.apply(console, arguments);
      }
  }
}

// ---------------------------------------------------
// Text
// ---------------------------------------------------

window.fontstring =
  '{"glyphs":{"ο":{"x_min":0,"x_max":712,"ha":815,"o":"m 356 -25 q 96 88 192 -25 q 0 368 0 201 q 92 642 0 533 q 356 761 192 761 q 617 644 517 761 q 712 368 712 533 q 619 91 712 201 q 356 -25 520 -25 m 356 85 q 527 175 465 85 q 583 369 583 255 q 528 562 583 484 q 356 651 466 651 q 189 560 250 651 q 135 369 135 481 q 187 177 135 257 q 356 85 250 85 "},"S":{"x_min":0,"x_max":788,"ha":890,"o":"m 788 291 q 662 54 788 144 q 397 -26 550 -26 q 116 68 226 -26 q 0 337 0 168 l 131 337 q 200 152 131 220 q 384 85 269 85 q 557 129 479 85 q 650 270 650 183 q 490 429 650 379 q 194 513 341 470 q 33 739 33 584 q 142 964 33 881 q 388 1041 242 1041 q 644 957 543 1041 q 756 716 756 867 l 625 716 q 561 874 625 816 q 395 933 497 933 q 243 891 309 933 q 164 759 164 841 q 325 609 164 656 q 625 526 475 568 q 788 291 788 454 "},"¦":{"x_min":343,"x_max":449,"ha":792,"o":"m 449 462 l 343 462 l 343 986 l 449 986 l 449 462 m 449 -242 l 343 -242 l 343 280 l 449 280 l 449 -242 "},"/":{"x_min":183.25,"x_max":608.328125,"ha":792,"o":"m 608 1041 l 266 -129 l 183 -129 l 520 1041 l 608 1041 "},"Τ":{"x_min":-0.4375,"x_max":777.453125,"ha":839,"o":"m 777 893 l 458 893 l 458 0 l 319 0 l 319 892 l 0 892 l 0 1013 l 777 1013 l 777 893 "},"y":{"x_min":0,"x_max":684.78125,"ha":771,"o":"m 684 738 l 388 -83 q 311 -216 356 -167 q 173 -279 252 -279 q 97 -266 133 -279 l 97 -149 q 132 -155 109 -151 q 168 -160 155 -160 q 240 -114 213 -160 q 274 -26 248 -98 l 0 738 l 137 737 l 341 139 l 548 737 l 684 738 "},"Π":{"x_min":0,"x_max":803,"ha":917,"o":"m 803 0 l 667 0 l 667 886 l 140 886 l 140 0 l 0 0 l 0 1012 l 803 1012 l 803 0 "},"ΐ":{"x_min":-111,"x_max":339,"ha":361,"o":"m 339 800 l 229 800 l 229 925 l 339 925 l 339 800 m -1 800 l -111 800 l -111 925 l -1 925 l -1 800 m 284 3 q 233 -10 258 -5 q 182 -15 207 -15 q 85 26 119 -15 q 42 200 42 79 l 42 737 l 167 737 l 168 215 q 172 141 168 157 q 226 101 183 101 q 248 103 239 101 q 284 112 257 104 l 284 3 m 302 1040 l 113 819 l 30 819 l 165 1040 l 302 1040 "},"g":{"x_min":0,"x_max":686,"ha":838,"o":"m 686 34 q 586 -213 686 -121 q 331 -306 487 -306 q 131 -252 216 -306 q 31 -84 31 -190 l 155 -84 q 228 -174 166 -138 q 345 -207 284 -207 q 514 -109 454 -207 q 564 89 564 -27 q 461 6 521 36 q 335 -23 401 -23 q 88 100 184 -23 q 0 370 0 215 q 87 634 0 522 q 330 758 183 758 q 457 728 398 758 q 564 644 515 699 l 564 737 l 686 737 l 686 34 m 582 367 q 529 560 582 481 q 358 652 468 652 q 189 561 250 652 q 135 369 135 482 q 189 176 135 255 q 361 85 251 85 q 529 176 468 85 q 582 367 582 255 "},"²":{"x_min":0,"x_max":442,"ha":539,"o":"m 442 383 l 0 383 q 91 566 0 492 q 260 668 176 617 q 354 798 354 727 q 315 875 354 845 q 227 905 277 905 q 136 869 173 905 q 99 761 99 833 l 14 761 q 82 922 14 864 q 232 974 141 974 q 379 926 316 974 q 442 797 442 878 q 351 635 442 704 q 183 539 321 611 q 92 455 92 491 l 442 455 l 442 383 "},"–":{"x_min":0,"x_max":705.5625,"ha":803,"o":"m 705 334 l 0 334 l 0 410 l 705 410 l 705 334 "},"Κ":{"x_min":0,"x_max":819.5625,"ha":893,"o":"m 819 0 l 650 0 l 294 509 l 139 356 l 139 0 l 0 0 l 0 1013 l 139 1013 l 139 526 l 626 1013 l 809 1013 l 395 600 l 819 0 "},"ƒ":{"x_min":-46.265625,"x_max":392,"ha":513,"o":"m 392 651 l 259 651 l 79 -279 l -46 -278 l 134 651 l 14 651 l 14 751 l 135 751 q 151 948 135 900 q 304 1041 185 1041 q 334 1040 319 1041 q 392 1034 348 1039 l 392 922 q 337 931 360 931 q 271 883 287 931 q 260 793 260 853 l 260 751 l 392 751 l 392 651 "},"e":{"x_min":0,"x_max":714,"ha":813,"o":"m 714 326 l 140 326 q 200 157 140 227 q 359 87 260 87 q 488 130 431 87 q 561 245 545 174 l 697 245 q 577 48 670 123 q 358 -26 484 -26 q 97 85 195 -26 q 0 363 0 197 q 94 642 0 529 q 358 765 195 765 q 626 627 529 765 q 714 326 714 503 m 576 429 q 507 583 564 522 q 355 650 445 650 q 206 583 266 650 q 140 429 152 522 l 576 429 "},"ό":{"x_min":0,"x_max":712,"ha":815,"o":"m 356 -25 q 94 91 194 -25 q 0 368 0 202 q 92 642 0 533 q 356 761 192 761 q 617 644 517 761 q 712 368 712 533 q 619 91 712 201 q 356 -25 520 -25 m 356 85 q 527 175 465 85 q 583 369 583 255 q 528 562 583 484 q 356 651 466 651 q 189 560 250 651 q 135 369 135 481 q 187 177 135 257 q 356 85 250 85 m 576 1040 l 387 819 l 303 819 l 438 1040 l 576 1040 "},"J":{"x_min":0,"x_max":588,"ha":699,"o":"m 588 279 q 287 -26 588 -26 q 58 73 126 -26 q 0 327 0 158 l 133 327 q 160 172 133 227 q 288 96 198 96 q 426 171 391 96 q 449 336 449 219 l 449 1013 l 588 1013 l 588 279 "},"»":{"x_min":-1,"x_max":503,"ha":601,"o":"m 503 302 l 280 136 l 281 256 l 429 373 l 281 486 l 280 608 l 503 440 l 503 302 m 221 302 l 0 136 l 0 255 l 145 372 l 0 486 l -1 608 l 221 440 l 221 302 "},"©":{"x_min":-3,"x_max":1008,"ha":1106,"o":"m 502 -7 q 123 151 263 -7 q -3 501 -3 294 q 123 851 -3 706 q 502 1011 263 1011 q 881 851 739 1011 q 1008 501 1008 708 q 883 151 1008 292 q 502 -7 744 -7 m 502 60 q 830 197 709 60 q 940 501 940 322 q 831 805 940 681 q 502 944 709 944 q 174 805 296 944 q 65 501 65 680 q 173 197 65 320 q 502 60 294 60 m 741 394 q 661 246 731 302 q 496 190 591 190 q 294 285 369 190 q 228 497 228 370 q 295 714 228 625 q 499 813 370 813 q 656 762 588 813 q 733 625 724 711 l 634 625 q 589 704 629 673 q 498 735 550 735 q 377 666 421 735 q 334 504 334 597 q 374 340 334 408 q 490 272 415 272 q 589 304 549 272 q 638 394 628 337 l 741 394 "},"ώ":{"x_min":0,"x_max":922,"ha":1030,"o":"m 687 1040 l 498 819 l 415 819 l 549 1040 l 687 1040 m 922 339 q 856 97 922 203 q 650 -26 780 -26 q 538 9 587 -26 q 461 103 489 44 q 387 12 436 46 q 277 -22 339 -22 q 69 97 147 -22 q 0 338 0 202 q 45 551 0 444 q 161 737 84 643 l 302 737 q 175 552 219 647 q 124 336 124 446 q 155 179 124 248 q 275 88 197 88 q 375 163 341 88 q 400 294 400 219 l 400 572 l 524 572 l 524 294 q 561 135 524 192 q 643 88 591 88 q 762 182 719 88 q 797 341 797 257 q 745 555 797 450 q 619 737 705 637 l 760 737 q 874 551 835 640 q 922 339 922 444 "},"^":{"x_min":193.0625,"x_max":598.609375,"ha":792,"o":"m 598 772 l 515 772 l 395 931 l 277 772 l 193 772 l 326 1013 l 462 1013 l 598 772 "},"«":{"x_min":0,"x_max":507.203125,"ha":604,"o":"m 506 136 l 284 302 l 284 440 l 506 608 l 507 485 l 360 371 l 506 255 l 506 136 m 222 136 l 0 302 l 0 440 l 222 608 l 221 486 l 73 373 l 222 256 l 222 136 "},"D":{"x_min":0,"x_max":828,"ha":935,"o":"m 389 1013 q 714 867 593 1013 q 828 521 828 729 q 712 161 828 309 q 382 0 587 0 l 0 0 l 0 1013 l 389 1013 m 376 124 q 607 247 523 124 q 681 510 681 355 q 607 771 681 662 q 376 896 522 896 l 139 896 l 139 124 l 376 124 "},"∙":{"x_min":0,"x_max":142,"ha":239,"o":"m 142 585 l 0 585 l 0 738 l 142 738 l 142 585 "},"ÿ":{"x_min":0,"x_max":47,"ha":125,"o":"m 47 3 q 37 -7 47 -7 q 28 0 30 -7 q 39 -4 32 -4 q 45 3 45 -1 l 37 0 q 28 9 28 0 q 39 19 28 19 l 47 16 l 47 19 l 47 3 m 37 1 q 44 8 44 1 q 37 16 44 16 q 30 8 30 16 q 37 1 30 1 m 26 1 l 23 22 l 14 0 l 3 22 l 3 3 l 0 25 l 13 1 l 22 25 l 26 1 "},"w":{"x_min":0,"x_max":1009.71875,"ha":1100,"o":"m 1009 738 l 783 0 l 658 0 l 501 567 l 345 0 l 222 0 l 0 738 l 130 738 l 284 174 l 432 737 l 576 738 l 721 173 l 881 737 l 1009 738 "},"$":{"x_min":0,"x_max":700,"ha":793,"o":"m 664 717 l 542 717 q 490 825 531 785 q 381 872 450 865 l 381 551 q 620 446 540 522 q 700 241 700 370 q 618 45 700 116 q 381 -25 536 -25 l 381 -152 l 307 -152 l 307 -25 q 81 62 162 -25 q 0 297 0 149 l 124 297 q 169 146 124 204 q 307 81 215 89 l 307 441 q 80 536 148 469 q 13 725 13 603 q 96 910 13 839 q 307 982 180 982 l 307 1077 l 381 1077 l 381 982 q 574 917 494 982 q 664 717 664 845 m 307 565 l 307 872 q 187 831 233 872 q 142 724 142 791 q 180 618 142 656 q 307 565 218 580 m 381 76 q 562 237 562 96 q 517 361 562 313 q 381 423 472 409 l 381 76 "},"ª":{"x_min":-0.015625,"x_max":425.0625,"ha":522,"o":"m 425 -129 l 337 -129 l 0 1041 l 83 1041 l 425 -129 "},"µ":{"x_min":0,"x_max":697.21875,"ha":747,"o":"m 697 -4 q 629 -14 658 -14 q 498 97 513 -14 q 422 9 470 41 q 313 -23 374 -23 q 207 4 258 -23 q 119 81 156 32 l 119 -278 l 0 -278 l 0 738 l 124 738 l 124 343 q 165 173 124 246 q 308 83 216 83 q 452 178 402 83 q 493 359 493 255 l 493 738 l 617 738 l 617 214 q 623 136 617 160 q 673 92 637 92 q 697 96 684 92 l 697 -4 "},"Ι":{"x_min":42,"x_max":181,"ha":297,"o":"m 181 0 l 42 0 l 42 1013 l 181 1013 l 181 0 "},"Ύ":{"x_min":0,"x_max":1144.5,"ha":1214,"o":"m 1144 1012 l 807 416 l 807 0 l 667 0 l 667 416 l 325 1012 l 465 1012 l 736 533 l 1004 1012 l 1144 1012 m 277 1040 l 83 799 l 0 799 l 140 1040 l 277 1040 "},"’":{"x_min":0,"x_max":139,"ha":236,"o":"m 139 851 q 102 737 139 784 q 0 669 65 690 l 0 734 q 59 787 42 741 q 72 873 72 821 l 0 873 l 0 1013 l 139 1013 l 139 851 "},"Ν":{"x_min":0,"x_max":801,"ha":915,"o":"m 801 0 l 651 0 l 131 822 l 131 0 l 0 0 l 0 1013 l 151 1013 l 670 191 l 670 1013 l 801 1013 l 801 0 "},"-":{"x_min":8.71875,"x_max":350.390625,"ha":478,"o":"m 350 317 l 8 317 l 8 428 l 350 428 l 350 317 "},"Q":{"x_min":0,"x_max":968,"ha":1072,"o":"m 954 5 l 887 -79 l 744 35 q 622 -11 687 2 q 483 -26 556 -26 q 127 130 262 -26 q 0 504 0 279 q 127 880 0 728 q 484 1041 262 1041 q 841 884 708 1041 q 968 507 968 735 q 933 293 968 398 q 832 104 899 188 l 954 5 m 723 191 q 802 330 777 248 q 828 499 828 412 q 744 790 828 673 q 483 922 650 922 q 228 791 322 922 q 142 505 142 673 q 227 221 142 337 q 487 91 323 91 q 632 123 566 91 l 520 215 l 587 301 l 723 191 "},"ς":{"x_min":1,"x_max":676.28125,"ha":740,"o":"m 676 460 l 551 460 q 498 595 542 546 q 365 651 448 651 q 199 578 263 651 q 136 401 136 505 q 266 178 136 241 q 508 106 387 142 q 640 -50 640 62 q 625 -158 640 -105 q 583 -278 611 -211 l 465 -278 q 498 -182 490 -211 q 515 -80 515 -126 q 381 12 515 -15 q 134 91 197 51 q 1 388 1 179 q 100 651 1 542 q 354 761 199 761 q 587 680 498 761 q 676 460 676 599 "},"M":{"x_min":0,"x_max":954,"ha":1067,"o":"m 954 0 l 819 0 l 819 869 l 537 0 l 405 0 l 128 866 l 128 0 l 0 0 l 0 1013 l 200 1013 l 472 160 l 757 1013 l 954 1013 l 954 0 "},"Ψ":{"x_min":0,"x_max":1006,"ha":1094,"o":"m 1006 678 q 914 319 1006 429 q 571 200 814 200 l 571 0 l 433 0 l 433 200 q 92 319 194 200 q 0 678 0 429 l 0 1013 l 139 1013 l 139 679 q 191 417 139 492 q 433 326 255 326 l 433 1013 l 571 1013 l 571 326 l 580 326 q 813 423 747 326 q 868 679 868 502 l 868 1013 l 1006 1013 l 1006 678 "},"C":{"x_min":0,"x_max":886,"ha":944,"o":"m 886 379 q 760 87 886 201 q 455 -26 634 -26 q 112 136 236 -26 q 0 509 0 283 q 118 882 0 737 q 469 1041 245 1041 q 748 955 630 1041 q 879 708 879 859 l 745 708 q 649 862 724 805 q 473 920 573 920 q 219 791 312 920 q 136 509 136 675 q 217 229 136 344 q 470 99 311 99 q 672 179 591 99 q 753 379 753 259 l 886 379 "},"!":{"x_min":0,"x_max":138,"ha":236,"o":"m 138 684 q 116 409 138 629 q 105 244 105 299 l 33 244 q 16 465 33 313 q 0 684 0 616 l 0 1013 l 138 1013 l 138 684 m 138 0 l 0 0 l 0 151 l 138 151 l 138 0 "},"{":{"x_min":0,"x_max":480.5625,"ha":578,"o":"m 480 -286 q 237 -213 303 -286 q 187 -45 187 -159 q 194 48 187 -15 q 201 141 201 112 q 164 264 201 225 q 0 314 118 314 l 0 417 q 164 471 119 417 q 201 605 201 514 q 199 665 201 644 q 193 772 193 769 q 241 941 193 887 q 480 1015 308 1015 l 480 915 q 336 866 375 915 q 306 742 306 828 q 310 662 306 717 q 314 577 314 606 q 288 452 314 500 q 176 365 256 391 q 289 275 257 337 q 314 143 314 226 q 313 84 314 107 q 310 -11 310 -5 q 339 -131 310 -94 q 480 -182 377 -182 l 480 -286 "},"X":{"x_min":-0.015625,"x_max":854.15625,"ha":940,"o":"m 854 0 l 683 0 l 423 409 l 166 0 l 0 0 l 347 519 l 18 1013 l 186 1013 l 428 637 l 675 1013 l 836 1013 l 504 520 l 854 0 "},"#":{"x_min":0,"x_max":963.890625,"ha":1061,"o":"m 963 690 l 927 590 l 719 590 l 655 410 l 876 410 l 840 310 l 618 310 l 508 -3 l 393 -2 l 506 309 l 329 310 l 215 -2 l 102 -3 l 212 310 l 0 310 l 36 410 l 248 409 l 312 590 l 86 590 l 120 690 l 347 690 l 459 1006 l 573 1006 l 462 690 l 640 690 l 751 1006 l 865 1006 l 754 690 l 963 690 m 606 590 l 425 590 l 362 410 l 543 410 l 606 590 "},"ι":{"x_min":42,"x_max":284,"ha":361,"o":"m 284 3 q 233 -10 258 -5 q 182 -15 207 -15 q 85 26 119 -15 q 42 200 42 79 l 42 738 l 167 738 l 168 215 q 172 141 168 157 q 226 101 183 101 q 248 103 239 101 q 284 112 257 104 l 284 3 "},"Ά":{"x_min":0,"x_max":906.953125,"ha":982,"o":"m 283 1040 l 88 799 l 5 799 l 145 1040 l 283 1040 m 906 0 l 756 0 l 650 303 l 251 303 l 143 0 l 0 0 l 376 1012 l 529 1012 l 906 0 m 609 421 l 452 866 l 293 421 l 609 421 "},")":{"x_min":0,"x_max":318,"ha":415,"o":"m 318 365 q 257 25 318 191 q 87 -290 197 -141 l 0 -290 q 140 21 93 -128 q 193 360 193 189 q 141 704 193 537 q 0 1024 97 850 l 87 1024 q 257 706 197 871 q 318 365 318 542 "},"ε":{"x_min":0,"x_max":634.71875,"ha":714,"o":"m 634 234 q 527 38 634 110 q 300 -25 433 -25 q 98 29 183 -25 q 0 204 0 93 q 37 314 0 265 q 128 390 67 353 q 56 460 82 419 q 26 555 26 505 q 114 712 26 654 q 295 763 191 763 q 499 700 416 763 q 589 515 589 631 l 478 515 q 419 618 464 580 q 307 657 374 657 q 207 630 253 657 q 151 547 151 598 q 238 445 151 469 q 389 434 280 434 l 389 331 l 349 331 q 206 315 255 331 q 125 210 125 287 q 183 107 125 145 q 302 76 233 76 q 436 117 379 76 q 509 234 493 159 l 634 234 "},"Δ":{"x_min":0,"x_max":952.78125,"ha":1028,"o":"m 952 0 l 0 0 l 400 1013 l 551 1013 l 952 0 m 762 124 l 476 867 l 187 124 l 762 124 "},"}":{"x_min":0,"x_max":481,"ha":578,"o":"m 481 314 q 318 262 364 314 q 282 136 282 222 q 284 65 282 97 q 293 -58 293 -48 q 241 -217 293 -166 q 0 -286 174 -286 l 0 -182 q 143 -130 105 -182 q 171 -2 171 -93 q 168 81 171 22 q 165 144 165 140 q 188 275 165 229 q 306 365 220 339 q 191 455 224 391 q 165 588 165 505 q 168 681 165 624 q 171 742 171 737 q 141 865 171 827 q 0 915 102 915 l 0 1015 q 243 942 176 1015 q 293 773 293 888 q 287 675 293 741 q 282 590 282 608 q 318 466 282 505 q 481 417 364 417 l 481 314 "},"‰":{"x_min":-3,"x_max":1672,"ha":1821,"o":"m 846 0 q 664 76 732 0 q 603 244 603 145 q 662 412 603 344 q 846 489 729 489 q 1027 412 959 489 q 1089 244 1089 343 q 1029 76 1089 144 q 846 0 962 0 m 845 103 q 945 143 910 103 q 981 243 981 184 q 947 340 981 301 q 845 385 910 385 q 745 342 782 385 q 709 243 709 300 q 742 147 709 186 q 845 103 781 103 m 888 986 l 284 -25 l 199 -25 l 803 986 l 888 986 m 241 468 q 58 545 126 468 q -3 715 -3 615 q 56 881 -3 813 q 238 958 124 958 q 421 881 353 958 q 483 712 483 813 q 423 544 483 612 q 241 468 356 468 m 241 855 q 137 811 175 855 q 100 710 100 768 q 136 612 100 653 q 240 572 172 572 q 344 614 306 572 q 382 713 382 656 q 347 810 382 771 q 241 855 308 855 m 1428 0 q 1246 76 1314 0 q 1185 244 1185 145 q 1244 412 1185 344 q 1428 489 1311 489 q 1610 412 1542 489 q 1672 244 1672 343 q 1612 76 1672 144 q 1428 0 1545 0 m 1427 103 q 1528 143 1492 103 q 1564 243 1564 184 q 1530 340 1564 301 q 1427 385 1492 385 q 1327 342 1364 385 q 1291 243 1291 300 q 1324 147 1291 186 q 1427 103 1363 103 "},"a":{"x_min":0,"x_max":698.609375,"ha":794,"o":"m 698 0 q 661 -12 679 -7 q 615 -17 643 -17 q 536 12 564 -17 q 500 96 508 41 q 384 6 456 37 q 236 -25 312 -25 q 65 31 130 -25 q 0 194 0 88 q 118 390 0 334 q 328 435 180 420 q 488 483 476 451 q 495 523 495 504 q 442 619 495 584 q 325 654 389 654 q 209 617 257 654 q 152 513 161 580 l 33 513 q 123 705 33 633 q 332 772 207 772 q 528 712 448 772 q 617 531 617 645 l 617 163 q 624 108 617 126 q 664 90 632 90 l 698 94 l 698 0 m 491 262 l 491 372 q 272 329 350 347 q 128 201 128 294 q 166 113 128 144 q 264 83 205 83 q 414 130 346 83 q 491 262 491 183 "},"—":{"x_min":0,"x_max":941.671875,"ha":1039,"o":"m 941 334 l 0 334 l 0 410 l 941 410 l 941 334 "},"=":{"x_min":8.71875,"x_max":780.953125,"ha":792,"o":"m 780 510 l 8 510 l 8 606 l 780 606 l 780 510 m 780 235 l 8 235 l 8 332 l 780 332 l 780 235 "},"N":{"x_min":0,"x_max":801,"ha":914,"o":"m 801 0 l 651 0 l 131 823 l 131 0 l 0 0 l 0 1013 l 151 1013 l 670 193 l 670 1013 l 801 1013 l 801 0 "},"ρ":{"x_min":0,"x_max":712,"ha":797,"o":"m 712 369 q 620 94 712 207 q 362 -26 521 -26 q 230 2 292 -26 q 119 83 167 30 l 119 -278 l 0 -278 l 0 362 q 91 643 0 531 q 355 764 190 764 q 617 647 517 764 q 712 369 712 536 m 583 366 q 530 559 583 480 q 359 651 469 651 q 190 562 252 651 q 135 370 135 483 q 189 176 135 257 q 359 85 250 85 q 528 175 466 85 q 583 366 583 254 "},"2":{"x_min":59,"x_max":731,"ha":792,"o":"m 731 0 l 59 0 q 197 314 59 188 q 457 487 199 315 q 598 691 598 580 q 543 819 598 772 q 411 867 488 867 q 272 811 328 867 q 209 630 209 747 l 81 630 q 182 901 81 805 q 408 986 271 986 q 629 909 536 986 q 731 694 731 826 q 613 449 731 541 q 378 316 495 383 q 201 122 235 234 l 731 122 l 731 0 "},"¯":{"x_min":0,"x_max":941.671875,"ha":938,"o":"m 941 1033 l 0 1033 l 0 1109 l 941 1109 l 941 1033 "},"Z":{"x_min":0,"x_max":779,"ha":849,"o":"m 779 0 l 0 0 l 0 113 l 621 896 l 40 896 l 40 1013 l 779 1013 l 778 887 l 171 124 l 779 124 l 779 0 "},"u":{"x_min":0,"x_max":617,"ha":729,"o":"m 617 0 l 499 0 l 499 110 q 391 10 460 45 q 246 -25 322 -25 q 61 58 127 -25 q 0 258 0 136 l 0 738 l 125 738 l 125 284 q 156 148 125 202 q 273 82 197 82 q 433 165 369 82 q 493 340 493 243 l 493 738 l 617 738 l 617 0 "},"k":{"x_min":0,"x_max":612.484375,"ha":697,"o":"m 612 738 l 338 465 l 608 0 l 469 0 l 251 382 l 121 251 l 121 0 l 0 0 l 0 1013 l 121 1013 l 121 402 l 456 738 l 612 738 "},"Η":{"x_min":0,"x_max":803,"ha":917,"o":"m 803 0 l 667 0 l 667 475 l 140 475 l 140 0 l 0 0 l 0 1013 l 140 1013 l 140 599 l 667 599 l 667 1013 l 803 1013 l 803 0 "},"Α":{"x_min":0,"x_max":906.953125,"ha":985,"o":"m 906 0 l 756 0 l 650 303 l 251 303 l 143 0 l 0 0 l 376 1013 l 529 1013 l 906 0 m 609 421 l 452 866 l 293 421 l 609 421 "},"s":{"x_min":0,"x_max":604,"ha":697,"o":"m 604 217 q 501 36 604 104 q 292 -23 411 -23 q 86 43 166 -23 q 0 238 0 114 l 121 237 q 175 122 121 164 q 300 85 223 85 q 415 112 363 85 q 479 207 479 147 q 361 309 479 276 q 140 372 141 370 q 21 544 21 426 q 111 708 21 647 q 298 761 190 761 q 492 705 413 761 q 583 531 583 643 l 462 531 q 412 625 462 594 q 298 657 363 657 q 199 636 242 657 q 143 558 143 608 q 262 454 143 486 q 484 394 479 397 q 604 217 604 341 "},"B":{"x_min":0,"x_max":778,"ha":876,"o":"m 580 546 q 724 469 670 535 q 778 311 778 403 q 673 83 778 171 q 432 0 575 0 l 0 0 l 0 1013 l 411 1013 q 629 957 541 1013 q 732 768 732 892 q 691 633 732 693 q 580 546 650 572 m 393 899 l 139 899 l 139 588 l 379 588 q 521 624 462 588 q 592 744 592 667 q 531 859 592 819 q 393 899 471 899 m 419 124 q 566 169 504 124 q 635 303 635 219 q 559 436 635 389 q 402 477 494 477 l 139 477 l 139 124 l 419 124 "},"…":{"x_min":0,"x_max":614,"ha":708,"o":"m 142 0 l 0 0 l 0 151 l 142 151 l 142 0 m 378 0 l 236 0 l 236 151 l 378 151 l 378 0 m 614 0 l 472 0 l 472 151 l 614 151 l 614 0 "},"?":{"x_min":0,"x_max":607,"ha":704,"o":"m 607 777 q 543 599 607 674 q 422 474 482 537 q 357 272 357 391 l 236 272 q 297 487 236 395 q 411 619 298 490 q 474 762 474 691 q 422 885 474 838 q 301 933 371 933 q 179 880 228 933 q 124 706 124 819 l 0 706 q 94 963 0 872 q 302 1044 177 1044 q 511 973 423 1044 q 607 777 607 895 m 370 0 l 230 0 l 230 151 l 370 151 l 370 0 "},"H":{"x_min":0,"x_max":803,"ha":915,"o":"m 803 0 l 667 0 l 667 475 l 140 475 l 140 0 l 0 0 l 0 1013 l 140 1013 l 140 599 l 667 599 l 667 1013 l 803 1013 l 803 0 "},"ν":{"x_min":0,"x_max":675,"ha":761,"o":"m 675 738 l 404 0 l 272 0 l 0 738 l 133 738 l 340 147 l 541 738 l 675 738 "},"c":{"x_min":1,"x_max":701.390625,"ha":775,"o":"m 701 264 q 584 53 681 133 q 353 -26 487 -26 q 91 91 188 -26 q 1 370 1 201 q 92 645 1 537 q 353 761 190 761 q 572 688 479 761 q 690 493 666 615 l 556 493 q 487 606 545 562 q 356 650 428 650 q 186 563 246 650 q 134 372 134 487 q 188 179 134 258 q 359 88 250 88 q 492 136 437 88 q 566 264 548 185 l 701 264 "},"¶":{"x_min":0,"x_max":566.671875,"ha":678,"o":"m 21 892 l 52 892 l 98 761 l 145 892 l 176 892 l 178 741 l 157 741 l 157 867 l 108 741 l 88 741 l 40 871 l 40 741 l 21 741 l 21 892 m 308 854 l 308 731 q 252 691 308 691 q 227 691 240 691 q 207 696 213 695 l 207 712 l 253 706 q 288 733 288 706 l 288 763 q 244 741 279 741 q 193 797 193 741 q 261 860 193 860 q 287 860 273 860 q 308 854 302 855 m 288 842 l 263 843 q 213 796 213 843 q 248 756 213 756 q 288 796 288 756 l 288 842 m 566 988 l 502 988 l 502 -1 l 439 -1 l 439 988 l 317 988 l 317 -1 l 252 -1 l 252 602 q 81 653 155 602 q 0 805 0 711 q 101 989 0 918 q 309 1053 194 1053 l 566 1053 l 566 988 "},"β":{"x_min":0,"x_max":660,"ha":745,"o":"m 471 550 q 610 450 561 522 q 660 280 660 378 q 578 64 660 151 q 367 -22 497 -22 q 239 5 299 -22 q 126 82 178 32 l 126 -278 l 0 -278 l 0 593 q 54 903 0 801 q 318 1042 127 1042 q 519 964 436 1042 q 603 771 603 887 q 567 644 603 701 q 471 550 532 586 m 337 79 q 476 138 418 79 q 535 279 535 198 q 427 437 535 386 q 226 477 344 477 l 226 583 q 398 620 329 583 q 486 762 486 668 q 435 884 486 833 q 312 935 384 935 q 169 861 219 935 q 126 698 126 797 l 126 362 q 170 169 126 242 q 337 79 224 79 "},"Μ":{"x_min":0,"x_max":954,"ha":1068,"o":"m 954 0 l 819 0 l 819 868 l 537 0 l 405 0 l 128 865 l 128 0 l 0 0 l 0 1013 l 199 1013 l 472 158 l 758 1013 l 954 1013 l 954 0 "},"Ό":{"x_min":0.109375,"x_max":1120,"ha":1217,"o":"m 1120 505 q 994 132 1120 282 q 642 -29 861 -29 q 290 130 422 -29 q 167 505 167 280 q 294 883 167 730 q 650 1046 430 1046 q 999 882 868 1046 q 1120 505 1120 730 m 977 504 q 896 784 977 669 q 644 915 804 915 q 391 785 484 915 q 307 504 307 669 q 391 224 307 339 q 644 95 486 95 q 894 224 803 95 q 977 504 977 339 m 277 1040 l 83 799 l 0 799 l 140 1040 l 277 1040 "},"Ή":{"x_min":0,"x_max":1158,"ha":1275,"o":"m 1158 0 l 1022 0 l 1022 475 l 496 475 l 496 0 l 356 0 l 356 1012 l 496 1012 l 496 599 l 1022 599 l 1022 1012 l 1158 1012 l 1158 0 m 277 1040 l 83 799 l 0 799 l 140 1040 l 277 1040 "},"•":{"x_min":0,"x_max":663.890625,"ha":775,"o":"m 663 529 q 566 293 663 391 q 331 196 469 196 q 97 294 194 196 q 0 529 0 393 q 96 763 0 665 q 331 861 193 861 q 566 763 469 861 q 663 529 663 665 "},"¥":{"x_min":0.1875,"x_max":819.546875,"ha":886,"o":"m 563 561 l 697 561 l 696 487 l 520 487 l 482 416 l 482 380 l 697 380 l 695 308 l 482 308 l 482 0 l 342 0 l 342 308 l 125 308 l 125 380 l 342 380 l 342 417 l 303 487 l 125 487 l 125 561 l 258 561 l 0 1013 l 140 1013 l 411 533 l 679 1013 l 819 1013 l 563 561 "},"(":{"x_min":0,"x_max":318.0625,"ha":415,"o":"m 318 -290 l 230 -290 q 61 23 122 -142 q 0 365 0 190 q 62 712 0 540 q 230 1024 119 869 l 318 1024 q 175 705 219 853 q 125 360 125 542 q 176 22 125 187 q 318 -290 223 -127 "},"U":{"x_min":0,"x_max":796,"ha":904,"o":"m 796 393 q 681 93 796 212 q 386 -25 566 -25 q 101 95 208 -25 q 0 393 0 211 l 0 1013 l 138 1013 l 138 391 q 204 191 138 270 q 394 107 276 107 q 586 191 512 107 q 656 391 656 270 l 656 1013 l 796 1013 l 796 393 "},"γ":{"x_min":0.5,"x_max":744.953125,"ha":822,"o":"m 744 737 l 463 54 l 463 -278 l 338 -278 l 338 54 l 154 495 q 104 597 124 569 q 13 651 67 651 l 0 651 l 0 751 l 39 753 q 168 711 121 753 q 242 594 207 676 l 403 208 l 617 737 l 744 737 "},"α":{"x_min":0,"x_max":765.5625,"ha":809,"o":"m 765 -4 q 698 -14 726 -14 q 564 97 586 -14 q 466 7 525 40 q 337 -26 407 -26 q 88 98 186 -26 q 0 369 0 212 q 88 637 0 525 q 337 760 184 760 q 465 728 407 760 q 563 637 524 696 l 563 739 l 685 739 l 685 222 q 693 141 685 168 q 748 94 708 94 q 765 96 760 94 l 765 -4 m 584 371 q 531 562 584 485 q 360 653 470 653 q 192 566 254 653 q 135 379 135 489 q 186 181 135 261 q 358 84 247 84 q 528 176 465 84 q 584 371 584 260 "},"F":{"x_min":0,"x_max":683.328125,"ha":717,"o":"m 683 888 l 140 888 l 140 583 l 613 583 l 613 458 l 140 458 l 140 0 l 0 0 l 0 1013 l 683 1013 l 683 888 "},"­":{"x_min":0,"x_max":705.5625,"ha":803,"o":"m 705 334 l 0 334 l 0 410 l 705 410 l 705 334 "},":":{"x_min":0,"x_max":142,"ha":239,"o":"m 142 585 l 0 585 l 0 738 l 142 738 l 142 585 m 142 0 l 0 0 l 0 151 l 142 151 l 142 0 "},"Χ":{"x_min":0,"x_max":854.171875,"ha":935,"o":"m 854 0 l 683 0 l 423 409 l 166 0 l 0 0 l 347 519 l 18 1013 l 186 1013 l 427 637 l 675 1013 l 836 1013 l 504 521 l 854 0 "},"*":{"x_min":116,"x_max":674,"ha":792,"o":"m 674 768 l 475 713 l 610 544 l 517 477 l 394 652 l 272 478 l 178 544 l 314 713 l 116 766 l 153 876 l 341 812 l 342 1013 l 446 1013 l 446 811 l 635 874 l 674 768 "},"†":{"x_min":0,"x_max":777,"ha":835,"o":"m 458 804 l 777 804 l 777 683 l 458 683 l 458 0 l 319 0 l 319 681 l 0 683 l 0 804 l 319 804 l 319 1015 l 458 1013 l 458 804 "},"°":{"x_min":0,"x_max":347,"ha":444,"o":"m 173 802 q 43 856 91 802 q 0 977 0 905 q 45 1101 0 1049 q 173 1153 90 1153 q 303 1098 255 1153 q 347 977 347 1049 q 303 856 347 905 q 173 802 256 802 m 173 884 q 238 910 214 884 q 262 973 262 937 q 239 1038 262 1012 q 173 1064 217 1064 q 108 1037 132 1064 q 85 973 85 1010 q 108 910 85 937 q 173 884 132 884 "},"V":{"x_min":0,"x_max":862.71875,"ha":940,"o":"m 862 1013 l 505 0 l 361 0 l 0 1013 l 143 1013 l 434 165 l 718 1012 l 862 1013 "},"Ξ":{"x_min":0,"x_max":734.71875,"ha":763,"o":"m 723 889 l 9 889 l 9 1013 l 723 1013 l 723 889 m 673 463 l 61 463 l 61 589 l 673 589 l 673 463 m 734 0 l 0 0 l 0 124 l 734 124 l 734 0 "}," ":{"x_min":0,"x_max":0,"ha":853},"Ϋ":{"x_min":0.328125,"x_max":819.515625,"ha":889,"o":"m 588 1046 l 460 1046 l 460 1189 l 588 1189 l 588 1046 m 360 1046 l 232 1046 l 232 1189 l 360 1189 l 360 1046 m 819 1012 l 482 416 l 482 0 l 342 0 l 342 416 l 0 1012 l 140 1012 l 411 533 l 679 1012 l 819 1012 "},"0":{"x_min":73,"x_max":715,"ha":792,"o":"m 394 -29 q 153 129 242 -29 q 73 479 73 272 q 152 829 73 687 q 394 989 241 989 q 634 829 545 989 q 715 479 715 684 q 635 129 715 270 q 394 -29 546 -29 m 394 89 q 546 211 489 89 q 598 479 598 322 q 548 748 598 640 q 394 871 491 871 q 241 748 298 871 q 190 479 190 637 q 239 211 190 319 q 394 89 296 89 "},"”":{"x_min":0,"x_max":347,"ha":454,"o":"m 139 851 q 102 737 139 784 q 0 669 65 690 l 0 734 q 59 787 42 741 q 72 873 72 821 l 0 873 l 0 1013 l 139 1013 l 139 851 m 347 851 q 310 737 347 784 q 208 669 273 690 l 208 734 q 267 787 250 741 q 280 873 280 821 l 208 873 l 208 1013 l 347 1013 l 347 851 "},"@":{"x_min":0,"x_max":1260,"ha":1357,"o":"m 1098 -45 q 877 -160 1001 -117 q 633 -203 752 -203 q 155 -29 327 -203 q 0 360 0 127 q 176 802 0 616 q 687 1008 372 1008 q 1123 854 969 1008 q 1260 517 1260 718 q 1155 216 1260 341 q 868 82 1044 82 q 772 106 801 82 q 737 202 737 135 q 647 113 700 144 q 527 82 594 82 q 367 147 420 82 q 314 312 314 212 q 401 565 314 452 q 639 690 498 690 q 810 588 760 690 l 849 668 l 938 668 q 877 441 900 532 q 833 226 833 268 q 853 182 833 198 q 902 167 873 167 q 1088 272 1012 167 q 1159 512 1159 372 q 1051 793 1159 681 q 687 925 925 925 q 248 747 415 925 q 97 361 97 586 q 226 26 97 159 q 627 -122 370 -122 q 856 -87 737 -122 q 1061 8 976 -53 l 1098 -45 m 786 488 q 738 580 777 545 q 643 615 700 615 q 483 517 548 615 q 425 322 425 430 q 457 203 425 250 q 552 156 490 156 q 722 273 665 156 q 786 488 738 309 "},"Ί":{"x_min":0,"x_max":499,"ha":613,"o":"m 277 1040 l 83 799 l 0 799 l 140 1040 l 277 1040 m 499 0 l 360 0 l 360 1012 l 499 1012 l 499 0 "},"i":{"x_min":14,"x_max":136,"ha":275,"o":"m 136 873 l 14 873 l 14 1013 l 136 1013 l 136 873 m 136 0 l 14 0 l 14 737 l 136 737 l 136 0 "},"Β":{"x_min":0,"x_max":778,"ha":877,"o":"m 580 545 q 724 468 671 534 q 778 310 778 402 q 673 83 778 170 q 432 0 575 0 l 0 0 l 0 1013 l 411 1013 q 629 957 541 1013 q 732 768 732 891 q 691 632 732 692 q 580 545 650 571 m 393 899 l 139 899 l 139 587 l 379 587 q 521 623 462 587 q 592 744 592 666 q 531 859 592 819 q 393 899 471 899 m 419 124 q 566 169 504 124 q 635 302 635 219 q 559 435 635 388 q 402 476 494 476 l 139 476 l 139 124 l 419 124 "},"υ":{"x_min":0,"x_max":617,"ha":725,"o":"m 617 352 q 540 94 617 199 q 308 -24 455 -24 q 76 94 161 -24 q 0 352 0 199 l 0 739 l 126 739 l 126 355 q 169 185 126 257 q 312 98 220 98 q 451 185 402 98 q 492 355 492 257 l 492 739 l 617 739 l 617 352 "},"]":{"x_min":0,"x_max":275,"ha":372,"o":"m 275 -281 l 0 -281 l 0 -187 l 151 -187 l 151 920 l 0 920 l 0 1013 l 275 1013 l 275 -281 "},"m":{"x_min":0,"x_max":1019,"ha":1128,"o":"m 1019 0 l 897 0 l 897 454 q 860 591 897 536 q 739 660 816 660 q 613 586 659 660 q 573 436 573 522 l 573 0 l 447 0 l 447 455 q 412 591 447 535 q 294 657 372 657 q 165 586 213 657 q 122 437 122 521 l 122 0 l 0 0 l 0 738 l 117 738 l 117 640 q 202 730 150 697 q 316 763 254 763 q 437 730 381 763 q 525 642 494 697 q 621 731 559 700 q 753 763 682 763 q 943 694 867 763 q 1019 512 1019 625 l 1019 0 "},"χ":{"x_min":8.328125,"x_max":780.5625,"ha":815,"o":"m 780 -278 q 715 -294 747 -294 q 616 -257 663 -294 q 548 -175 576 -227 l 379 133 l 143 -277 l 9 -277 l 313 254 l 163 522 q 127 586 131 580 q 36 640 91 640 q 8 637 27 640 l 8 752 l 52 757 q 162 719 113 757 q 236 627 200 690 l 383 372 l 594 737 l 726 737 l 448 250 l 625 -69 q 670 -153 647 -110 q 743 -188 695 -188 q 780 -184 759 -188 l 780 -278 "},"8":{"x_min":55,"x_max":736,"ha":792,"o":"m 571 527 q 694 424 652 491 q 736 280 736 358 q 648 71 736 158 q 395 -26 551 -26 q 142 69 238 -26 q 55 279 55 157 q 96 425 55 359 q 220 527 138 491 q 120 615 153 562 q 88 726 88 668 q 171 904 88 827 q 395 986 261 986 q 618 905 529 986 q 702 727 702 830 q 670 616 702 667 q 571 527 638 565 m 394 565 q 519 610 475 565 q 563 717 563 655 q 521 823 563 781 q 392 872 474 872 q 265 824 312 872 q 224 720 224 783 q 265 613 224 656 q 394 565 312 565 m 395 91 q 545 150 488 91 q 597 280 597 204 q 546 408 597 355 q 395 465 492 465 q 244 408 299 465 q 194 280 194 356 q 244 150 194 203 q 395 91 299 91 "},"ί":{"x_min":42,"x_max":326.71875,"ha":361,"o":"m 284 3 q 233 -10 258 -5 q 182 -15 207 -15 q 85 26 119 -15 q 42 200 42 79 l 42 737 l 167 737 l 168 215 q 172 141 168 157 q 226 101 183 101 q 248 102 239 101 q 284 112 257 104 l 284 3 m 326 1040 l 137 819 l 54 819 l 189 1040 l 326 1040 "},"Ζ":{"x_min":0,"x_max":779.171875,"ha":850,"o":"m 779 0 l 0 0 l 0 113 l 620 896 l 40 896 l 40 1013 l 779 1013 l 779 887 l 170 124 l 779 124 l 779 0 "},"R":{"x_min":0,"x_max":781.953125,"ha":907,"o":"m 781 0 l 623 0 q 587 242 590 52 q 407 433 585 433 l 138 433 l 138 0 l 0 0 l 0 1013 l 396 1013 q 636 946 539 1013 q 749 731 749 868 q 711 597 749 659 q 608 502 674 534 q 718 370 696 474 q 729 207 722 352 q 781 26 736 62 l 781 0 m 373 551 q 533 594 465 551 q 614 731 614 645 q 532 859 614 815 q 373 896 465 896 l 138 896 l 138 551 l 373 551 "},"o":{"x_min":0,"x_max":713,"ha":821,"o":"m 357 -25 q 94 91 194 -25 q 0 368 0 202 q 93 642 0 533 q 357 761 193 761 q 618 644 518 761 q 713 368 713 533 q 619 91 713 201 q 357 -25 521 -25 m 357 85 q 528 175 465 85 q 584 369 584 255 q 529 562 584 484 q 357 651 467 651 q 189 560 250 651 q 135 369 135 481 q 187 177 135 257 q 357 85 250 85 "},"5":{"x_min":54.171875,"x_max":738,"ha":792,"o":"m 738 314 q 626 60 738 153 q 382 -23 526 -23 q 155 47 248 -23 q 54 256 54 125 l 183 256 q 259 132 204 174 q 382 91 314 91 q 533 149 471 91 q 602 314 602 213 q 538 469 602 411 q 386 528 475 528 q 284 506 332 528 q 197 439 237 484 l 81 439 l 159 958 l 684 958 l 684 840 l 254 840 l 214 579 q 306 627 258 612 q 407 643 354 643 q 636 552 540 643 q 738 314 738 457 "},"7":{"x_min":58.71875,"x_max":730.953125,"ha":792,"o":"m 730 839 q 469 448 560 641 q 335 0 378 255 l 192 0 q 328 441 235 252 q 593 830 421 630 l 58 830 l 58 958 l 730 958 l 730 839 "},"K":{"x_min":0,"x_max":819.46875,"ha":906,"o":"m 819 0 l 649 0 l 294 509 l 139 355 l 139 0 l 0 0 l 0 1013 l 139 1013 l 139 526 l 626 1013 l 809 1013 l 395 600 l 819 0 "},",":{"x_min":0,"x_max":142,"ha":239,"o":"m 142 -12 q 105 -132 142 -82 q 0 -205 68 -182 l 0 -138 q 57 -82 40 -124 q 70 0 70 -51 l 0 0 l 0 151 l 142 151 l 142 -12 "},"d":{"x_min":0,"x_max":683,"ha":796,"o":"m 683 0 l 564 0 l 564 93 q 456 6 516 38 q 327 -25 395 -25 q 87 100 181 -25 q 0 365 0 215 q 90 639 0 525 q 343 763 187 763 q 564 647 486 763 l 564 1013 l 683 1013 l 683 0 m 582 373 q 529 562 582 484 q 361 653 468 653 q 190 561 253 653 q 135 365 135 479 q 189 175 135 254 q 358 85 251 85 q 529 178 468 85 q 582 373 582 258 "},"¨":{"x_min":-109,"x_max":247,"ha":232,"o":"m 247 1046 l 119 1046 l 119 1189 l 247 1189 l 247 1046 m 19 1046 l -109 1046 l -109 1189 l 19 1189 l 19 1046 "},"E":{"x_min":0,"x_max":736.109375,"ha":789,"o":"m 736 0 l 0 0 l 0 1013 l 725 1013 l 725 889 l 139 889 l 139 585 l 677 585 l 677 467 l 139 467 l 139 125 l 736 125 l 736 0 "},"Y":{"x_min":0,"x_max":820,"ha":886,"o":"m 820 1013 l 482 416 l 482 0 l 342 0 l 342 416 l 0 1013 l 140 1013 l 411 534 l 679 1012 l 820 1013 "},"‹":{"x_min":17.984375,"x_max":773.609375,"ha":792,"o":"m 773 40 l 18 376 l 17 465 l 773 799 l 773 692 l 159 420 l 773 149 l 773 40 "},"„":{"x_min":0,"x_max":364,"ha":467,"o":"m 141 -12 q 104 -132 141 -82 q 0 -205 67 -182 l 0 -138 q 56 -82 40 -124 q 69 0 69 -51 l 0 0 l 0 151 l 141 151 l 141 -12 m 364 -12 q 327 -132 364 -82 q 222 -205 290 -182 l 222 -138 q 279 -82 262 -124 q 292 0 292 -51 l 222 0 l 222 151 l 364 151 l 364 -12 "},"δ":{"x_min":1,"x_max":710,"ha":810,"o":"m 710 360 q 616 87 710 196 q 356 -28 518 -28 q 99 82 197 -28 q 1 356 1 192 q 100 606 1 509 q 355 703 199 703 q 180 829 288 754 q 70 903 124 866 l 70 1012 l 643 1012 l 643 901 l 258 901 q 462 763 422 794 q 636 592 577 677 q 710 360 710 485 m 584 365 q 552 501 584 447 q 451 602 521 555 q 372 611 411 611 q 197 541 258 611 q 136 355 136 472 q 190 171 136 245 q 358 85 252 85 q 528 173 465 85 q 584 365 584 252 "},"έ":{"x_min":0,"x_max":634.71875,"ha":714,"o":"m 634 234 q 527 38 634 110 q 300 -25 433 -25 q 98 29 183 -25 q 0 204 0 93 q 37 313 0 265 q 128 390 67 352 q 56 459 82 419 q 26 555 26 505 q 114 712 26 654 q 295 763 191 763 q 499 700 416 763 q 589 515 589 631 l 478 515 q 419 618 464 580 q 307 657 374 657 q 207 630 253 657 q 151 547 151 598 q 238 445 151 469 q 389 434 280 434 l 389 331 l 349 331 q 206 315 255 331 q 125 210 125 287 q 183 107 125 145 q 302 76 233 76 q 436 117 379 76 q 509 234 493 159 l 634 234 m 520 1040 l 331 819 l 248 819 l 383 1040 l 520 1040 "},"ω":{"x_min":0,"x_max":922,"ha":1031,"o":"m 922 339 q 856 97 922 203 q 650 -26 780 -26 q 538 9 587 -26 q 461 103 489 44 q 387 12 436 46 q 277 -22 339 -22 q 69 97 147 -22 q 0 339 0 203 q 45 551 0 444 q 161 738 84 643 l 302 738 q 175 553 219 647 q 124 336 124 446 q 155 179 124 249 q 275 88 197 88 q 375 163 341 88 q 400 294 400 219 l 400 572 l 524 572 l 524 294 q 561 135 524 192 q 643 88 591 88 q 762 182 719 88 q 797 342 797 257 q 745 556 797 450 q 619 738 705 638 l 760 738 q 874 551 835 640 q 922 339 922 444 "},"´":{"x_min":0,"x_max":96,"ha":251,"o":"m 96 606 l 0 606 l 0 988 l 96 988 l 96 606 "},"±":{"x_min":11,"x_max":781,"ha":792,"o":"m 781 490 l 446 490 l 446 255 l 349 255 l 349 490 l 11 490 l 11 586 l 349 586 l 349 819 l 446 819 l 446 586 l 781 586 l 781 490 m 781 21 l 11 21 l 11 115 l 781 115 l 781 21 "},"|":{"x_min":343,"x_max":449,"ha":792,"o":"m 449 462 l 343 462 l 343 986 l 449 986 l 449 462 m 449 -242 l 343 -242 l 343 280 l 449 280 l 449 -242 "},"ϋ":{"x_min":0,"x_max":617,"ha":725,"o":"m 482 800 l 372 800 l 372 925 l 482 925 l 482 800 m 239 800 l 129 800 l 129 925 l 239 925 l 239 800 m 617 352 q 540 93 617 199 q 308 -24 455 -24 q 76 93 161 -24 q 0 352 0 199 l 0 738 l 126 738 l 126 354 q 169 185 126 257 q 312 98 220 98 q 451 185 402 98 q 492 354 492 257 l 492 738 l 617 738 l 617 352 "},"§":{"x_min":0,"x_max":593,"ha":690,"o":"m 593 425 q 554 312 593 369 q 467 233 516 254 q 537 83 537 172 q 459 -74 537 -12 q 288 -133 387 -133 q 115 -69 184 -133 q 47 96 47 -6 l 166 96 q 199 7 166 40 q 288 -26 232 -26 q 371 -5 332 -26 q 420 60 420 21 q 311 201 420 139 q 108 309 210 255 q 0 490 0 383 q 33 602 0 551 q 124 687 66 654 q 75 743 93 712 q 58 812 58 773 q 133 984 58 920 q 300 1043 201 1043 q 458 987 394 1043 q 529 814 529 925 l 411 814 q 370 908 404 877 q 289 939 336 939 q 213 911 246 939 q 180 841 180 883 q 286 720 180 779 q 484 612 480 615 q 593 425 593 534 m 467 409 q 355 544 467 473 q 196 630 228 612 q 146 587 162 609 q 124 525 124 558 q 239 387 124 462 q 398 298 369 315 q 448 345 429 316 q 467 409 467 375 "},"b":{"x_min":0,"x_max":685,"ha":783,"o":"m 685 372 q 597 99 685 213 q 347 -25 501 -25 q 219 5 277 -25 q 121 93 161 36 l 121 0 l 0 0 l 0 1013 l 121 1013 l 121 634 q 214 723 157 692 q 341 754 272 754 q 591 637 493 754 q 685 372 685 526 m 554 356 q 499 550 554 470 q 328 644 437 644 q 162 556 223 644 q 108 369 108 478 q 160 176 108 256 q 330 83 221 83 q 498 169 435 83 q 554 356 554 245 "},"q":{"x_min":0,"x_max":683,"ha":876,"o":"m 683 -278 l 564 -278 l 564 97 q 474 8 533 39 q 345 -23 415 -23 q 91 93 188 -23 q 0 364 0 203 q 87 635 0 522 q 337 760 184 760 q 466 727 408 760 q 564 637 523 695 l 564 737 l 683 737 l 683 -278 m 582 375 q 527 564 582 488 q 358 652 466 652 q 190 565 253 652 q 135 377 135 488 q 189 179 135 261 q 361 84 251 84 q 530 179 469 84 q 582 375 582 260 "},"Ω":{"x_min":-0.171875,"x_max":969.5625,"ha":1068,"o":"m 969 0 l 555 0 l 555 123 q 744 308 675 194 q 814 558 814 423 q 726 812 814 709 q 484 922 633 922 q 244 820 334 922 q 154 567 154 719 q 223 316 154 433 q 412 123 292 199 l 412 0 l 0 0 l 0 124 l 217 124 q 68 327 122 210 q 15 572 15 444 q 144 911 15 781 q 484 1041 274 1041 q 822 909 691 1041 q 953 569 953 777 q 899 326 953 443 q 750 124 846 210 l 969 124 l 969 0 "},"ύ":{"x_min":0,"x_max":617,"ha":725,"o":"m 617 352 q 540 93 617 199 q 308 -24 455 -24 q 76 93 161 -24 q 0 352 0 199 l 0 738 l 126 738 l 126 354 q 169 185 126 257 q 312 98 220 98 q 451 185 402 98 q 492 354 492 257 l 492 738 l 617 738 l 617 352 m 535 1040 l 346 819 l 262 819 l 397 1040 l 535 1040 "},"z":{"x_min":-0.015625,"x_max":613.890625,"ha":697,"o":"m 613 0 l 0 0 l 0 100 l 433 630 l 20 630 l 20 738 l 594 738 l 593 636 l 163 110 l 613 110 l 613 0 "},"™":{"x_min":0,"x_max":894,"ha":1000,"o":"m 389 951 l 229 951 l 229 503 l 160 503 l 160 951 l 0 951 l 0 1011 l 389 1011 l 389 951 m 894 503 l 827 503 l 827 939 l 685 503 l 620 503 l 481 937 l 481 503 l 417 503 l 417 1011 l 517 1011 l 653 580 l 796 1010 l 894 1011 l 894 503 "},"ή":{"x_min":0.78125,"x_max":697,"ha":810,"o":"m 697 -278 l 572 -278 l 572 454 q 540 587 572 536 q 425 650 501 650 q 271 579 337 650 q 206 420 206 509 l 206 0 l 81 0 l 81 489 q 73 588 81 562 q 0 644 56 644 l 0 741 q 68 755 38 755 q 158 721 124 755 q 200 630 193 687 q 297 726 234 692 q 434 761 359 761 q 620 692 544 761 q 697 516 697 624 l 697 -278 m 479 1040 l 290 819 l 207 819 l 341 1040 l 479 1040 "},"Θ":{"x_min":0,"x_max":960,"ha":1056,"o":"m 960 507 q 833 129 960 280 q 476 -32 698 -32 q 123 129 255 -32 q 0 507 0 280 q 123 883 0 732 q 476 1045 255 1045 q 832 883 696 1045 q 960 507 960 732 m 817 500 q 733 789 817 669 q 476 924 639 924 q 223 792 317 924 q 142 507 142 675 q 222 222 142 339 q 476 89 315 89 q 730 218 636 89 q 817 500 817 334 m 716 449 l 243 449 l 243 571 l 716 571 l 716 449 "},"®":{"x_min":-3,"x_max":1008,"ha":1106,"o":"m 503 532 q 614 562 566 532 q 672 658 672 598 q 614 747 672 716 q 503 772 569 772 l 338 772 l 338 532 l 503 532 m 502 -7 q 123 151 263 -7 q -3 501 -3 294 q 123 851 -3 706 q 502 1011 263 1011 q 881 851 739 1011 q 1008 501 1008 708 q 883 151 1008 292 q 502 -7 744 -7 m 502 60 q 830 197 709 60 q 940 501 940 322 q 831 805 940 681 q 502 944 709 944 q 174 805 296 944 q 65 501 65 680 q 173 197 65 320 q 502 60 294 60 m 788 146 l 678 146 q 653 316 655 183 q 527 449 652 449 l 338 449 l 338 146 l 241 146 l 241 854 l 518 854 q 688 808 621 854 q 766 658 766 755 q 739 563 766 607 q 668 497 713 519 q 751 331 747 472 q 788 164 756 190 l 788 146 "},"~":{"x_min":0,"x_max":833,"ha":931,"o":"m 833 958 q 778 753 833 831 q 594 665 716 665 q 402 761 502 665 q 240 857 302 857 q 131 795 166 857 q 104 665 104 745 l 0 665 q 54 867 0 789 q 237 958 116 958 q 429 861 331 958 q 594 765 527 765 q 704 827 670 765 q 729 958 729 874 l 833 958 "},"Ε":{"x_min":0,"x_max":736.21875,"ha":778,"o":"m 736 0 l 0 0 l 0 1013 l 725 1013 l 725 889 l 139 889 l 139 585 l 677 585 l 677 467 l 139 467 l 139 125 l 736 125 l 736 0 "},"³":{"x_min":0,"x_max":450,"ha":547,"o":"m 450 552 q 379 413 450 464 q 220 366 313 366 q 69 414 130 366 q 0 567 0 470 l 85 567 q 126 470 85 504 q 225 437 168 437 q 320 467 280 437 q 360 552 360 498 q 318 632 360 608 q 213 657 276 657 q 195 657 203 657 q 176 657 181 657 l 176 722 q 279 733 249 722 q 334 815 334 752 q 300 881 334 856 q 220 907 267 907 q 133 875 169 907 q 97 781 97 844 l 15 781 q 78 926 15 875 q 220 972 135 972 q 364 930 303 972 q 426 817 426 888 q 344 697 426 733 q 421 642 392 681 q 450 552 450 603 "},"[":{"x_min":0,"x_max":273.609375,"ha":371,"o":"m 273 -281 l 0 -281 l 0 1013 l 273 1013 l 273 920 l 124 920 l 124 -187 l 273 -187 l 273 -281 "},"L":{"x_min":0,"x_max":645.828125,"ha":696,"o":"m 645 0 l 0 0 l 0 1013 l 140 1013 l 140 126 l 645 126 l 645 0 "},"σ":{"x_min":0,"x_max":803.390625,"ha":894,"o":"m 803 628 l 633 628 q 713 368 713 512 q 618 93 713 204 q 357 -25 518 -25 q 94 91 194 -25 q 0 368 0 201 q 94 644 0 533 q 356 761 194 761 q 481 750 398 761 q 608 739 564 739 l 803 739 l 803 628 m 360 85 q 529 180 467 85 q 584 374 584 262 q 527 566 584 490 q 352 651 463 651 q 187 559 247 651 q 135 368 135 478 q 189 175 135 254 q 360 85 251 85 "},"ζ":{"x_min":0,"x_max":573,"ha":642,"o":"m 573 -40 q 553 -162 573 -97 q 510 -278 543 -193 l 400 -278 q 441 -187 428 -219 q 462 -90 462 -132 q 378 -14 462 -14 q 108 45 197 -14 q 0 290 0 117 q 108 631 0 462 q 353 901 194 767 l 55 901 l 55 1012 l 561 1012 l 561 924 q 261 669 382 831 q 128 301 128 489 q 243 117 128 149 q 458 98 350 108 q 573 -40 573 80 "},"θ":{"x_min":0,"x_max":674,"ha":778,"o":"m 674 496 q 601 160 674 304 q 336 -26 508 -26 q 73 153 165 -26 q 0 485 0 296 q 72 840 0 683 q 343 1045 166 1045 q 605 844 516 1045 q 674 496 674 692 m 546 579 q 498 798 546 691 q 336 935 437 935 q 178 798 237 935 q 126 579 137 701 l 546 579 m 546 475 l 126 475 q 170 233 126 348 q 338 80 230 80 q 504 233 447 80 q 546 475 546 346 "},"Ο":{"x_min":0,"x_max":958,"ha":1054,"o":"m 485 1042 q 834 883 703 1042 q 958 511 958 735 q 834 136 958 287 q 481 -26 701 -26 q 126 130 261 -26 q 0 504 0 279 q 127 880 0 729 q 485 1042 263 1042 m 480 98 q 731 225 638 98 q 815 504 815 340 q 733 783 815 670 q 480 913 640 913 q 226 785 321 913 q 142 504 142 671 q 226 224 142 339 q 480 98 319 98 "},"Γ":{"x_min":0,"x_max":705.28125,"ha":749,"o":"m 705 886 l 140 886 l 140 0 l 0 0 l 0 1012 l 705 1012 l 705 886 "}," ":{"x_min":0,"x_max":0,"ha":375},"%":{"x_min":-3,"x_max":1089,"ha":1186,"o":"m 845 0 q 663 76 731 0 q 602 244 602 145 q 661 412 602 344 q 845 489 728 489 q 1027 412 959 489 q 1089 244 1089 343 q 1029 76 1089 144 q 845 0 962 0 m 844 103 q 945 143 909 103 q 981 243 981 184 q 947 340 981 301 q 844 385 909 385 q 744 342 781 385 q 708 243 708 300 q 741 147 708 186 q 844 103 780 103 m 888 986 l 284 -25 l 199 -25 l 803 986 l 888 986 m 241 468 q 58 545 126 468 q -3 715 -3 615 q 56 881 -3 813 q 238 958 124 958 q 421 881 353 958 q 483 712 483 813 q 423 544 483 612 q 241 468 356 468 m 241 855 q 137 811 175 855 q 100 710 100 768 q 136 612 100 653 q 240 572 172 572 q 344 614 306 572 q 382 713 382 656 q 347 810 382 771 q 241 855 308 855 "},"P":{"x_min":0,"x_max":726,"ha":806,"o":"m 424 1013 q 640 931 555 1013 q 726 719 726 850 q 637 506 726 587 q 413 426 548 426 l 140 426 l 140 0 l 0 0 l 0 1013 l 424 1013 m 379 889 l 140 889 l 140 548 l 372 548 q 522 589 459 548 q 593 720 593 637 q 528 845 593 801 q 379 889 463 889 "},"Έ":{"x_min":0,"x_max":1078.21875,"ha":1118,"o":"m 1078 0 l 342 0 l 342 1013 l 1067 1013 l 1067 889 l 481 889 l 481 585 l 1019 585 l 1019 467 l 481 467 l 481 125 l 1078 125 l 1078 0 m 277 1040 l 83 799 l 0 799 l 140 1040 l 277 1040 "},"Ώ":{"x_min":0.125,"x_max":1136.546875,"ha":1235,"o":"m 1136 0 l 722 0 l 722 123 q 911 309 842 194 q 981 558 981 423 q 893 813 981 710 q 651 923 800 923 q 411 821 501 923 q 321 568 321 720 q 390 316 321 433 q 579 123 459 200 l 579 0 l 166 0 l 166 124 l 384 124 q 235 327 289 210 q 182 572 182 444 q 311 912 182 782 q 651 1042 441 1042 q 989 910 858 1042 q 1120 569 1120 778 q 1066 326 1120 443 q 917 124 1013 210 l 1136 124 l 1136 0 m 277 1040 l 83 800 l 0 800 l 140 1041 l 277 1040 "},"_":{"x_min":0,"x_max":705.5625,"ha":803,"o":"m 705 -334 l 0 -334 l 0 -234 l 705 -234 l 705 -334 "},"Ϊ":{"x_min":-110,"x_max":246,"ha":275,"o":"m 246 1046 l 118 1046 l 118 1189 l 246 1189 l 246 1046 m 18 1046 l -110 1046 l -110 1189 l 18 1189 l 18 1046 m 136 0 l 0 0 l 0 1012 l 136 1012 l 136 0 "},"+":{"x_min":23,"x_max":768,"ha":792,"o":"m 768 372 l 444 372 l 444 0 l 347 0 l 347 372 l 23 372 l 23 468 l 347 468 l 347 840 l 444 840 l 444 468 l 768 468 l 768 372 "},"½":{"x_min":0,"x_max":1050,"ha":1149,"o":"m 1050 0 l 625 0 q 712 178 625 108 q 878 277 722 187 q 967 385 967 328 q 932 456 967 429 q 850 484 897 484 q 759 450 798 484 q 721 352 721 416 l 640 352 q 706 502 640 448 q 851 551 766 551 q 987 509 931 551 q 1050 385 1050 462 q 976 251 1050 301 q 829 179 902 215 q 717 68 740 133 l 1050 68 l 1050 0 m 834 985 l 215 -28 l 130 -28 l 750 984 l 834 985 m 224 422 l 142 422 l 142 811 l 0 811 l 0 867 q 104 889 62 867 q 164 973 157 916 l 224 973 l 224 422 "},"Ρ":{"x_min":0,"x_max":720,"ha":783,"o":"m 424 1013 q 637 933 554 1013 q 720 723 720 853 q 633 508 720 591 q 413 426 546 426 l 140 426 l 140 0 l 0 0 l 0 1013 l 424 1013 m 378 889 l 140 889 l 140 548 l 371 548 q 521 589 458 548 q 592 720 592 637 q 527 845 592 801 q 378 889 463 889 "},"ª":{"x_min":0,"x_max":139,"ha":236,"o":"m 139 851 q 102 737 139 784 q 0 669 65 690 l 0 734 q 59 787 42 741 q 72 873 72 821 l 0 873 l 0 1013 l 139 1013 l 139 851 "},"ª":{"x_min":0,"x_max":350,"ha":397,"o":"m 350 625 q 307 616 328 616 q 266 631 281 616 q 247 673 251 645 q 190 628 225 644 q 116 613 156 613 q 32 641 64 613 q 0 722 0 669 q 72 826 0 800 q 247 866 159 846 l 247 887 q 220 934 247 916 q 162 953 194 953 q 104 934 129 953 q 76 882 80 915 l 16 882 q 60 976 16 941 q 166 1011 104 1011 q 266 979 224 1011 q 308 891 308 948 l 308 706 q 311 679 308 688 q 331 670 315 670 l 350 672 l 350 625 m 247 757 l 247 811 q 136 790 175 798 q 64 726 64 773 q 83 682 64 697 q 132 667 103 667 q 207 690 174 667 q 247 757 247 718 "},"΅":{"x_min":0,"x_max":450,"ha":553,"o":"m 450 800 l 340 800 l 340 925 l 450 925 l 450 800 m 406 1040 l 212 800 l 129 800 l 269 1040 l 406 1040 m 110 800 l 0 800 l 0 925 l 110 925 l 110 800 "},"T":{"x_min":0,"x_max":777,"ha":835,"o":"m 777 894 l 458 894 l 458 0 l 319 0 l 319 894 l 0 894 l 0 1013 l 777 1013 l 777 894 "},"Φ":{"x_min":0,"x_max":915,"ha":997,"o":"m 527 0 l 389 0 l 389 122 q 110 231 220 122 q 0 509 0 340 q 110 785 0 677 q 389 893 220 893 l 389 1013 l 527 1013 l 527 893 q 804 786 693 893 q 915 509 915 679 q 805 231 915 341 q 527 122 696 122 l 527 0 m 527 226 q 712 310 641 226 q 779 507 779 389 q 712 705 779 627 q 527 787 641 787 l 527 226 m 389 226 l 389 787 q 205 698 275 775 q 136 505 136 620 q 206 308 136 391 q 389 226 276 226 "},"⁋":{"x_min":0,"x_max":0,"ha":694},"j":{"x_min":-77.78125,"x_max":167,"ha":349,"o":"m 167 871 l 42 871 l 42 1013 l 167 1013 l 167 871 m 167 -80 q 121 -231 167 -184 q -26 -278 76 -278 l -77 -278 l -77 -164 l -41 -164 q 26 -143 11 -164 q 42 -65 42 -122 l 42 737 l 167 737 l 167 -80 "},"Σ":{"x_min":0,"x_max":756.953125,"ha":819,"o":"m 756 0 l 0 0 l 0 107 l 395 523 l 22 904 l 22 1013 l 745 1013 l 745 889 l 209 889 l 566 523 l 187 125 l 756 125 l 756 0 "},"1":{"x_min":215.671875,"x_max":574,"ha":792,"o":"m 574 0 l 442 0 l 442 697 l 215 697 l 215 796 q 386 833 330 796 q 475 986 447 875 l 574 986 l 574 0 "},"›":{"x_min":18.0625,"x_max":774,"ha":792,"o":"m 774 376 l 18 40 l 18 149 l 631 421 l 18 692 l 18 799 l 774 465 l 774 376 "},"<":{"x_min":17.984375,"x_max":773.609375,"ha":792,"o":"m 773 40 l 18 376 l 17 465 l 773 799 l 773 692 l 159 420 l 773 149 l 773 40 "},"£":{"x_min":0,"x_max":704.484375,"ha":801,"o":"m 704 41 q 623 -10 664 5 q 543 -26 583 -26 q 359 15 501 -26 q 243 36 288 36 q 158 23 197 36 q 73 -21 119 10 l 6 76 q 125 195 90 150 q 175 331 175 262 q 147 443 175 383 l 0 443 l 0 512 l 108 512 q 43 734 43 623 q 120 929 43 854 q 358 1010 204 1010 q 579 936 487 1010 q 678 729 678 857 l 678 684 l 552 684 q 504 838 552 780 q 362 896 457 896 q 216 852 263 896 q 176 747 176 815 q 199 627 176 697 q 248 512 217 574 l 468 512 l 468 443 l 279 443 q 297 356 297 398 q 230 194 297 279 q 153 107 211 170 q 227 133 190 125 q 293 142 264 142 q 410 119 339 142 q 516 96 482 96 q 579 105 550 96 q 648 142 608 115 l 704 41 "},"t":{"x_min":0,"x_max":367,"ha":458,"o":"m 367 0 q 312 -5 339 -2 q 262 -8 284 -8 q 145 28 183 -8 q 108 143 108 64 l 108 638 l 0 638 l 0 738 l 108 738 l 108 944 l 232 944 l 232 738 l 367 738 l 367 638 l 232 638 l 232 185 q 248 121 232 140 q 307 102 264 102 q 345 104 330 102 q 367 107 360 107 l 367 0 "},"¬":{"x_min":0,"x_max":706,"ha":803,"o":"m 706 411 l 706 158 l 630 158 l 630 335 l 0 335 l 0 411 l 706 411 "},"λ":{"x_min":0,"x_max":750,"ha":803,"o":"m 750 -7 q 679 -15 716 -15 q 538 59 591 -15 q 466 214 512 97 l 336 551 l 126 0 l 0 0 l 270 705 q 223 837 247 770 q 116 899 190 899 q 90 898 100 899 l 90 1004 q 152 1011 125 1011 q 298 938 244 1011 q 373 783 326 901 l 605 192 q 649 115 629 136 q 716 95 669 95 l 736 95 q 750 97 745 97 l 750 -7 "},"W":{"x_min":0,"x_max":1263.890625,"ha":1351,"o":"m 1263 1013 l 995 0 l 859 0 l 627 837 l 405 0 l 265 0 l 0 1013 l 136 1013 l 342 202 l 556 1013 l 701 1013 l 921 207 l 1133 1012 l 1263 1013 "},">":{"x_min":18.0625,"x_max":774,"ha":792,"o":"m 774 376 l 18 40 l 18 149 l 631 421 l 18 692 l 18 799 l 774 465 l 774 376 "},"v":{"x_min":0,"x_max":675.15625,"ha":761,"o":"m 675 738 l 404 0 l 272 0 l 0 738 l 133 737 l 340 147 l 541 737 l 675 738 "},"τ":{"x_min":0.28125,"x_max":644.5,"ha":703,"o":"m 644 628 l 382 628 l 382 179 q 388 120 382 137 q 436 91 401 91 q 474 94 447 91 q 504 97 501 97 l 504 0 q 454 -9 482 -5 q 401 -14 426 -14 q 278 67 308 -14 q 260 233 260 118 l 260 628 l 0 628 l 0 739 l 644 739 l 644 628 "},"ξ":{"x_min":0,"x_max":624.9375,"ha":699,"o":"m 624 -37 q 608 -153 624 -96 q 563 -278 593 -211 l 454 -278 q 491 -183 486 -200 q 511 -83 511 -126 q 484 -23 511 -44 q 370 1 452 1 q 323 0 354 1 q 283 -1 293 -1 q 84 76 169 -1 q 0 266 0 154 q 56 431 0 358 q 197 538 108 498 q 94 613 134 562 q 54 730 54 665 q 77 823 54 780 q 143 901 101 867 l 27 901 l 27 1012 l 576 1012 l 576 901 l 380 901 q 244 863 303 901 q 178 745 178 820 q 312 600 178 636 q 532 582 380 582 l 532 479 q 276 455 361 479 q 118 281 118 410 q 165 173 118 217 q 274 120 208 133 q 494 101 384 110 q 624 -37 624 76 "},"&":{"x_min":-3,"x_max":894.25,"ha":992,"o":"m 894 0 l 725 0 l 624 123 q 471 0 553 40 q 306 -41 390 -41 q 168 -7 231 -41 q 62 92 105 26 q 14 187 31 139 q -3 276 -3 235 q 55 433 -3 358 q 248 581 114 508 q 170 689 196 640 q 137 817 137 751 q 214 985 137 922 q 384 1041 284 1041 q 548 988 483 1041 q 622 824 622 928 q 563 666 622 739 q 431 556 516 608 l 621 326 q 649 407 639 361 q 663 493 653 426 l 781 493 q 703 229 781 352 l 894 0 m 504 818 q 468 908 504 877 q 384 940 433 940 q 293 907 331 940 q 255 818 255 875 q 289 714 255 767 q 363 628 313 678 q 477 729 446 682 q 504 818 504 771 m 556 209 l 314 499 q 179 395 223 449 q 135 283 135 341 q 146 222 135 253 q 183 158 158 192 q 333 80 241 80 q 556 209 448 80 "},"Λ":{"x_min":0,"x_max":862.5,"ha":942,"o":"m 862 0 l 719 0 l 426 847 l 143 0 l 0 0 l 356 1013 l 501 1013 l 862 0 "},"I":{"x_min":41,"x_max":180,"ha":293,"o":"m 180 0 l 41 0 l 41 1013 l 180 1013 l 180 0 "},"G":{"x_min":0,"x_max":921,"ha":1011,"o":"m 921 0 l 832 0 l 801 136 q 655 15 741 58 q 470 -28 568 -28 q 126 133 259 -28 q 0 499 0 284 q 125 881 0 731 q 486 1043 259 1043 q 763 957 647 1043 q 905 709 890 864 l 772 709 q 668 866 747 807 q 486 926 589 926 q 228 795 322 926 q 142 507 142 677 q 228 224 142 342 q 483 94 323 94 q 712 195 625 94 q 796 435 796 291 l 477 435 l 477 549 l 921 549 l 921 0 "},"ΰ":{"x_min":0,"x_max":617,"ha":725,"o":"m 524 800 l 414 800 l 414 925 l 524 925 l 524 800 m 183 800 l 73 800 l 73 925 l 183 925 l 183 800 m 617 352 q 540 93 617 199 q 308 -24 455 -24 q 76 93 161 -24 q 0 352 0 199 l 0 738 l 126 738 l 126 354 q 169 185 126 257 q 312 98 220 98 q 451 185 402 98 q 492 354 492 257 l 492 738 l 617 738 l 617 352 m 489 1040 l 300 819 l 216 819 l 351 1040 l 489 1040 "},"`":{"x_min":0,"x_max":138.890625,"ha":236,"o":"m 138 699 l 0 699 l 0 861 q 36 974 0 929 q 138 1041 72 1020 l 138 977 q 82 931 95 969 q 69 839 69 893 l 138 839 l 138 699 "},"·":{"x_min":0,"x_max":142,"ha":239,"o":"m 142 585 l 0 585 l 0 738 l 142 738 l 142 585 "},"Υ":{"x_min":0.328125,"x_max":819.515625,"ha":889,"o":"m 819 1013 l 482 416 l 482 0 l 342 0 l 342 416 l 0 1013 l 140 1013 l 411 533 l 679 1013 l 819 1013 "},"r":{"x_min":0,"x_max":355.5625,"ha":432,"o":"m 355 621 l 343 621 q 179 569 236 621 q 122 411 122 518 l 122 0 l 0 0 l 0 737 l 117 737 l 117 604 q 204 719 146 686 q 355 753 262 753 l 355 621 "},"x":{"x_min":0,"x_max":675,"ha":764,"o":"m 675 0 l 525 0 l 331 286 l 144 0 l 0 0 l 256 379 l 12 738 l 157 737 l 336 473 l 516 738 l 661 738 l 412 380 l 675 0 "},"μ":{"x_min":0,"x_max":696.609375,"ha":747,"o":"m 696 -4 q 628 -14 657 -14 q 498 97 513 -14 q 422 8 470 41 q 313 -24 374 -24 q 207 3 258 -24 q 120 80 157 31 l 120 -278 l 0 -278 l 0 738 l 124 738 l 124 343 q 165 172 124 246 q 308 82 216 82 q 451 177 402 82 q 492 358 492 254 l 492 738 l 616 738 l 616 214 q 623 136 616 160 q 673 92 636 92 q 696 95 684 92 l 696 -4 "},"h":{"x_min":0,"x_max":615,"ha":724,"o":"m 615 472 l 615 0 l 490 0 l 490 454 q 456 590 490 535 q 338 654 416 654 q 186 588 251 654 q 122 436 122 522 l 122 0 l 0 0 l 0 1013 l 122 1013 l 122 633 q 218 727 149 694 q 362 760 287 760 q 552 676 484 760 q 615 472 615 600 "},".":{"x_min":0,"x_max":142,"ha":239,"o":"m 142 0 l 0 0 l 0 151 l 142 151 l 142 0 "},"φ":{"x_min":-2,"x_max":878,"ha":974,"o":"m 496 -279 l 378 -279 l 378 -17 q 101 88 204 -17 q -2 367 -2 194 q 68 626 -2 510 q 283 758 151 758 l 283 646 q 167 537 209 626 q 133 373 133 462 q 192 177 133 254 q 378 93 259 93 l 378 758 q 445 764 426 763 q 476 765 464 765 q 765 659 653 765 q 878 377 878 553 q 771 96 878 209 q 496 -17 665 -17 l 496 -279 m 496 93 l 514 93 q 687 183 623 93 q 746 380 746 265 q 691 569 746 491 q 522 658 629 658 l 496 656 l 496 93 "},";":{"x_min":0,"x_max":142,"ha":239,"o":"m 142 585 l 0 585 l 0 738 l 142 738 l 142 585 m 142 -12 q 105 -132 142 -82 q 0 -206 68 -182 l 0 -138 q 58 -82 43 -123 q 68 0 68 -56 l 0 0 l 0 151 l 142 151 l 142 -12 "},"f":{"x_min":0,"x_max":378,"ha":472,"o":"m 378 638 l 246 638 l 246 0 l 121 0 l 121 638 l 0 638 l 0 738 l 121 738 q 137 935 121 887 q 290 1028 171 1028 q 320 1027 305 1028 q 378 1021 334 1026 l 378 908 q 323 918 346 918 q 257 870 273 918 q 246 780 246 840 l 246 738 l 378 738 l 378 638 "},"“":{"x_min":1,"x_max":348.21875,"ha":454,"o":"m 140 670 l 1 670 l 1 830 q 37 943 1 897 q 140 1011 74 990 l 140 947 q 82 900 97 940 q 68 810 68 861 l 140 810 l 140 670 m 348 670 l 209 670 l 209 830 q 245 943 209 897 q 348 1011 282 990 l 348 947 q 290 900 305 940 q 276 810 276 861 l 348 810 l 348 670 "},"A":{"x_min":0.03125,"x_max":906.953125,"ha":1008,"o":"m 906 0 l 756 0 l 648 303 l 251 303 l 142 0 l 0 0 l 376 1013 l 529 1013 l 906 0 m 610 421 l 452 867 l 293 421 l 610 421 "},"6":{"x_min":53,"x_max":739,"ha":792,"o":"m 739 312 q 633 62 739 162 q 400 -31 534 -31 q 162 78 257 -31 q 53 439 53 206 q 178 859 53 712 q 441 986 284 986 q 643 912 559 986 q 732 713 732 833 l 601 713 q 544 830 594 786 q 426 875 494 875 q 268 793 331 875 q 193 517 193 697 q 301 597 240 570 q 427 624 362 624 q 643 540 552 624 q 739 312 739 451 m 603 298 q 540 461 603 400 q 404 516 484 516 q 268 461 323 516 q 207 300 207 401 q 269 137 207 198 q 405 83 325 83 q 541 137 486 83 q 603 298 603 197 "},"‘":{"x_min":1,"x_max":139.890625,"ha":236,"o":"m 139 670 l 1 670 l 1 830 q 37 943 1 897 q 139 1011 74 990 l 139 947 q 82 900 97 940 q 68 810 68 861 l 139 810 l 139 670 "},"ϊ":{"x_min":-70,"x_max":283,"ha":361,"o":"m 283 800 l 173 800 l 173 925 l 283 925 l 283 800 m 40 800 l -70 800 l -70 925 l 40 925 l 40 800 m 283 3 q 232 -10 257 -5 q 181 -15 206 -15 q 84 26 118 -15 q 41 200 41 79 l 41 737 l 166 737 l 167 215 q 171 141 167 157 q 225 101 182 101 q 247 103 238 101 q 283 112 256 104 l 283 3 "},"π":{"x_min":-0.21875,"x_max":773.21875,"ha":857,"o":"m 773 -7 l 707 -11 q 575 40 607 -11 q 552 174 552 77 l 552 226 l 552 626 l 222 626 l 222 0 l 97 0 l 97 626 l 0 626 l 0 737 l 773 737 l 773 626 l 676 626 l 676 171 q 695 103 676 117 q 773 90 714 90 l 773 -7 "},"ά":{"x_min":0,"x_max":765.5625,"ha":809,"o":"m 765 -4 q 698 -14 726 -14 q 564 97 586 -14 q 466 7 525 40 q 337 -26 407 -26 q 88 98 186 -26 q 0 369 0 212 q 88 637 0 525 q 337 760 184 760 q 465 727 407 760 q 563 637 524 695 l 563 738 l 685 738 l 685 222 q 693 141 685 168 q 748 94 708 94 q 765 95 760 94 l 765 -4 m 584 371 q 531 562 584 485 q 360 653 470 653 q 192 566 254 653 q 135 379 135 489 q 186 181 135 261 q 358 84 247 84 q 528 176 465 84 q 584 371 584 260 m 604 1040 l 415 819 l 332 819 l 466 1040 l 604 1040 "},"O":{"x_min":0,"x_max":958,"ha":1057,"o":"m 485 1041 q 834 882 702 1041 q 958 512 958 734 q 834 136 958 287 q 481 -26 702 -26 q 126 130 261 -26 q 0 504 0 279 q 127 880 0 728 q 485 1041 263 1041 m 480 98 q 731 225 638 98 q 815 504 815 340 q 733 783 815 669 q 480 912 640 912 q 226 784 321 912 q 142 504 142 670 q 226 224 142 339 q 480 98 319 98 "},"n":{"x_min":0,"x_max":615,"ha":724,"o":"m 615 463 l 615 0 l 490 0 l 490 454 q 453 592 490 537 q 331 656 410 656 q 178 585 240 656 q 117 421 117 514 l 117 0 l 0 0 l 0 738 l 117 738 l 117 630 q 218 728 150 693 q 359 764 286 764 q 552 675 484 764 q 615 463 615 593 "},"3":{"x_min":54,"x_max":737,"ha":792,"o":"m 737 284 q 635 55 737 141 q 399 -25 541 -25 q 156 52 248 -25 q 54 308 54 140 l 185 308 q 245 147 185 202 q 395 96 302 96 q 539 140 484 96 q 602 280 602 190 q 510 429 602 390 q 324 454 451 454 l 324 565 q 487 584 441 565 q 565 719 565 617 q 515 835 565 791 q 395 879 466 879 q 255 824 307 879 q 203 661 203 769 l 78 661 q 166 909 78 822 q 387 992 250 992 q 603 921 513 992 q 701 723 701 844 q 669 607 701 656 q 578 524 637 558 q 696 434 655 499 q 737 284 737 369 "},"9":{"x_min":53,"x_max":739,"ha":792,"o":"m 739 524 q 619 94 739 241 q 362 -32 516 -32 q 150 47 242 -32 q 59 244 59 126 l 191 244 q 246 129 191 176 q 373 82 301 82 q 526 161 466 82 q 597 440 597 255 q 363 334 501 334 q 130 432 216 334 q 53 650 53 521 q 134 880 53 786 q 383 986 226 986 q 659 841 566 986 q 739 524 739 719 m 388 449 q 535 514 480 449 q 585 658 585 573 q 535 805 585 744 q 388 873 480 873 q 242 809 294 873 q 191 658 191 745 q 239 514 191 572 q 388 449 292 449 "},"l":{"x_min":41,"x_max":166,"ha":279,"o":"m 166 0 l 41 0 l 41 1013 l 166 1013 l 166 0 "},"¤":{"x_min":40.09375,"x_max":728.796875,"ha":825,"o":"m 728 304 l 649 224 l 512 363 q 383 331 458 331 q 256 363 310 331 l 119 224 l 40 304 l 177 441 q 150 553 150 493 q 184 673 150 621 l 40 818 l 119 898 l 267 749 q 321 766 291 759 q 384 773 351 773 q 447 766 417 773 q 501 749 477 759 l 649 898 l 728 818 l 585 675 q 612 618 604 648 q 621 553 621 587 q 591 441 621 491 l 728 304 m 384 682 q 280 643 318 682 q 243 551 243 604 q 279 461 243 499 q 383 423 316 423 q 487 461 449 423 q 525 553 525 500 q 490 641 525 605 q 384 682 451 682 "},"κ":{"x_min":0,"x_max":632.328125,"ha":679,"o":"m 632 0 l 482 0 l 225 384 l 124 288 l 124 0 l 0 0 l 0 738 l 124 738 l 124 446 l 433 738 l 596 738 l 312 466 l 632 0 "},"4":{"x_min":48,"x_max":742.453125,"ha":792,"o":"m 742 243 l 602 243 l 602 0 l 476 0 l 476 243 l 48 243 l 48 368 l 476 958 l 602 958 l 602 354 l 742 354 l 742 243 m 476 354 l 476 792 l 162 354 l 476 354 "},"p":{"x_min":0,"x_max":685,"ha":786,"o":"m 685 364 q 598 96 685 205 q 350 -23 504 -23 q 121 89 205 -23 l 121 -278 l 0 -278 l 0 738 l 121 738 l 121 633 q 220 726 159 691 q 351 761 280 761 q 598 636 504 761 q 685 364 685 522 m 557 371 q 501 560 557 481 q 330 651 437 651 q 162 559 223 651 q 108 366 108 479 q 162 177 108 254 q 333 87 224 87 q 502 178 441 87 q 557 371 557 258 "},"‡":{"x_min":0,"x_max":777,"ha":835,"o":"m 458 238 l 458 0 l 319 0 l 319 238 l 0 238 l 0 360 l 319 360 l 319 681 l 0 683 l 0 804 l 319 804 l 319 1015 l 458 1013 l 458 804 l 777 804 l 777 683 l 458 683 l 458 360 l 777 360 l 777 238 l 458 238 "},"ψ":{"x_min":0,"x_max":808,"ha":907,"o":"m 465 -278 l 341 -278 l 341 -15 q 87 102 180 -15 q 0 378 0 210 l 0 739 l 133 739 l 133 379 q 182 195 133 275 q 341 98 242 98 l 341 922 l 465 922 l 465 98 q 623 195 563 98 q 675 382 675 278 l 675 742 l 808 742 l 808 381 q 720 104 808 213 q 466 -13 627 -13 l 465 -278 "},"η":{"x_min":0.78125,"x_max":697,"ha":810,"o":"m 697 -278 l 572 -278 l 572 454 q 540 587 572 536 q 425 650 501 650 q 271 579 337 650 q 206 420 206 509 l 206 0 l 81 0 l 81 489 q 73 588 81 562 q 0 644 56 644 l 0 741 q 68 755 38 755 q 158 720 124 755 q 200 630 193 686 q 297 726 234 692 q 434 761 359 761 q 620 692 544 761 q 697 516 697 624 l 697 -278 "}},"cssFontWeight":"normal","ascender":1189,"underlinePosition":-100,"cssFontStyle":"normal","boundingBox":{"yMin":-334,"xMin":-111,"yMax":1189,"xMax":1672},"resolution":1000,"original_font_information":{"postscript_name":"Helvetiker-Regular","version_string":"Version 1.00 2004 initial release","vendor_url":"http://www.magenta.gr/","full_font_name":"Helvetiker","font_family_name":"Helvetiker","copyright":"Copyright (c) Μagenta ltd, 2004","description":"","trademark":"","designer":"","designer_url":"","unique_font_identifier":"Μagenta ltd:Helvetiker:22-10-104","license_url":"http://www.ellak.gr/fonts/MgOpen/license.html","license_description":"Copyright (c) 2004 by MAGENTA Ltd. All Rights Reserved.Permission is hereby granted, free of charge, to any person obtaining a copy of the fonts accompanying this license (), to reproduce and distribute the Font Software, including without limitation the rights to use, copy, merge, publish, distribute, and/or sell copies of the Font Software, and to permit persons to whom the Font Software is furnished to do so, subject to the following conditions: ","manufacturer_name":"Μagenta ltd","font_sub_family_name":"Regular"},"descender":-334,"familyName":"Helvetiker","lineHeight":1522,"underlineThickness":50}';

window.fontjson = JSON.parse(fontstring);
window.three_font = new THREE.Font(fontjson);

export function get_text_geometry(s, size) {
  var g = new THREE.TextGeometry(s, {
    font: three_font,
    size: size,
    height: 0.0,
    curveSegments: 12,
    bevelEnabled: false,
    bevelThickness: 0.1,
    bevelSize: 1,
    bevelSegments: 1
  });

  return g;
}

export function add_text_to_scene(str, node, x, y, z, color_r = default_text_color_r, color_g = default_text_color_g, color_b = default_text_color_b) {
  var text_size = /*0.05*/ viewSize/50;
  var text_geometry = get_text_geometry(str, text_size);

  var text_color = new THREE.Color(color_r, color_g, color_b);
  var text_material = new THREE.MeshBasicMaterial({ color: text_color });
  var text_mesh = new THREE.Mesh(text_geometry, text_material);
  var subnode = new THREE.Object3D();
  subnode.add(text_mesh);
  subnode.quaternion.copy( uid_camera.quaternion );
  subnode.position.set(x, y, z);
  node.add(subnode);
}

// We want to display:
// - Coordinate axes
// - Vertex coordinates
// - Tangency mates between edges
// - Smoothness property of vertices

export function update_dom_text_values_and_coordinates() {
  App.state.selected_edges_count = uid_selected_edges.length;
  App.state.selected_faces_count = uid_selected_faces.length;
  App.state.selected_vertices_count = uid_selected_vertices.length;
  App.state.current_operation_text = calculate_current_operation();
  App.state.rotation_lock_text = get_horizontal_vertical_rotate_direction_text();
  //console.log(App.state.rotation_lock_text);
  for (const x of dom_text_elements.values()) {
    x.update_text_value();
    x.reposition_text();
  }
  force_refresh();
  animate();
}

export function recreate_tangency_texts() {
  var count = 1;
  dom_edge_tangency_strings.clear();

  // Recreate the list of edges with tangencies.
  var edges_with_tangencies = new HashTable();
  uid_edges.keys().forEach(function(e1id) {
    var vids = uil_get_vertices_of_edge(e1id);
    vids.forEach(function(vid) {
      var eids = uil_get_edges_of_vertex(vid);
      eids.forEach(function(e2id){
        if (uil_is_joint_smooth(e1id, e2id) == 1) {
          edges_with_tangencies.setItem(e1id, e1id);
          edges_with_tangencies.setItem(e2id, e2id);
          if (e1id <= e2id)
            var s = e1id + "-" + e2id;
          else
            var s = e2id + "-" + e1id;
          if (!dom_edge_tangency_strings.hasItem(s))
            dom_edge_tangency_strings.setItem(s, count++);
        }
      });
    });
  });

  // Also display all tangencies between pairs of edges.
  if (uid_state.tangency_coordinates_visible) {
    // First remove all tangencies from DOM.
    dom_text_elements.values().forEach(function(e){
      if (e.text_type == "tangent_edge") {
        e.remove_text_from_dom();
      }
    });

    // The add the new ones.
    edges_with_tangencies.keys().forEach(function(eid) {
      var id_str = "T_" + eid;
      var v_dom_text = new Dom_text("tangent_edge", eid, id_str);
      v_dom_text.add_text_to_dom();
    });
  }
}

export function get_number(domelement) {
    const n = parseFloat(domelement.value);
    if (isNaN(n) || typeof n !== 'number')
        return 0;
    else
        return n;
}

// ---------------------------------------------------
// Parsing binary files
// ---------------------------------------------------

// read big-endian (network byte order) unsigned 32-bit int from data, at offset
window.readChar = function(data, offset) {
  return data.charCodeAt(offset) & 0xff;
};

window.readUInt32 = function(data, offset) {
  //console_log("A:", data.charCodeAt(offset));
  //console_log("B:", data.charCodeAt(offset+1));
  //console_log("C:", data.charCodeAt(offset+2));
  //console_log("D:", data.charCodeAt(offset+3));
  return (
    ((data.charCodeAt(offset) & 0xff) << 24) +
    ((data.charCodeAt(offset + 1) & 0xff) << 16) +
    ((data.charCodeAt(offset + 2) & 0xff) << 8) +
    (data.charCodeAt(offset + 3) & 0xff)
  );
};
// read big-endian (network byte order) unsigned 16-bit int from data, at offset
window.readUInt16 = function(data, offset) {
  return (
    ((data.charCodeAt(offset) & 0xff) << 8) +
    (data.charCodeAt(offset + 1) & 0xff)
  );
};

export function toHexString(byteArray) {
  return Array.from(byteArray, function(byte) {
    return ("0" + (byte & 0xff).toString(16)).slice(-2);
  }).join("");
}

// ---------------------------------------------------
// Array
// ---------------------------------------------------

export function count_occurences_of_each_id_in_array(arr) {
  var element_hash = new HashTable();
  arr.forEach(function(el) {
    element_hash.setItem(el, element_hash.getItem(el) ? element_hash.getItem(el) + 1 : 1);
  });
  return element_hash;
}

// Note: Integers and strings compared
export function isInArray(value, array) {
  // var v = (typeof value  === 'string') ? value : value.toString();
  return array.indexOf(value) > -1;
}

export function push_nodups(val, arr) {
  if (arr.indexOf(val) == -1)
    arr.push(val);
}

// ---------------------------------------------------
// User interface operations
// ---------------------------------------------------

export function object_is_selected(id) {
  if (isFace(id))
    return uid_selected_faces.hasItem(id);
  if (isEdge(id))
    return uid_selected_edges.hasItem(id);
  if (isVertex(id))
    return uid_selected_vertices.hasItem(id);
}

export function reference_object_is_selected(id) {
  if (uid_reference_faces.hasItem(id)) {
    var ref = uid_reference_faces.getItem(id);
    var is_selected = false;
    uid_selected_reference_faces.forEach(function(srf){
      if (srf.id == ref.uid_object.o3d_id)
        is_selected = true;
    });
    return is_selected;
  }
  if (uid_reference_edges.hasItem(id))
    return false; // XXX
  if (uid_reference_vertices.hasItem(id))
    return false; // XXX
}

export function calculate_halo_difference(mousenormalized, normal, uid_halo_initial_horizontal_2D_position, uid_halo_initial_vertical_2D_position) {
  var halo_difference_x = mousenormalized.x - uid_halo_initial_horizontal_2D_position;
  var halo_difference_y = mousenormalized.y - uid_halo_initial_vertical_2D_position;

  var O = new THREE.Vector3(0,0,0);
  var p = from_3d_coordinates_to_2d_screen_coordinates(normal);
  var Op = from_3d_coordinates_to_2d_screen_coordinates(O);
  var Np = new THREE.Vector2(0,0,0);
  Np.subVectors(p,Op);
  var dotProduct = halo_difference_x*Np.x + halo_difference_y*Np.y;

  /*
  console_log("Halo_difference_x:", halo_difference_x);
  console_log("Halo_difference_y:", halo_difference_y);
  console_log("Normal:", normal);
  console_log("P:", p);
  console_log("Op:", Op);
  */

  if (dotProduct > 0)
    return Math.sqrt(halo_difference_x*halo_difference_x + halo_difference_y*halo_difference_y);
  else
    return - Math.sqrt(halo_difference_x*halo_difference_x + halo_difference_y*halo_difference_y);
}


export function add_small_ball(x, y, z, rgbx, rgby, rgbz, scale = 1) {
  var vertex_geometry = new THREE.SphereGeometry(uid_vertex_diameter / 6.0 * scale);
  vertex_geometry.dynamic = true;
  var vertex_material = new THREE.MeshBasicMaterial({
    color: 0x333333,
    opacity: 0.2
  });
  vertex_material.color.setRGB( rgbx/256, rgby/256, rgbz/256 );

  vertex_material.transparent = true;
  vertex_material.opacity = 0.4;
  vertex_material.depthTest = false;
  vertex_material.depthWrite = false;

  var vertex_mesh = new THREE.Mesh(vertex_geometry, vertex_material);
  // Position the vertex in 3D space
  vertex_mesh.position.set(x, y, z);
  uid_draw_edge_raynode.add(vertex_mesh);
}

export function show_rotation_center(x, y, z, is_for_setting_target_point = false) {

  let r = 254/300;
  let g = 196/300;
  let b = 38/300;
  if (is_for_setting_target_point) {
    r = 50/300;
    g = 196/300;
    b = 100/300;
  }
  let scale = 0.5;

  let box1_geometry = new THREE.BoxGeometry(uid_vertex_diameter / 6.0 * scale, uid_vertex_diameter / 6.0 * scale, uid_vertex_diameter * 1.2 * scale);
  let box2_geometry = new THREE.BoxGeometry(uid_vertex_diameter / 6.0 * scale, uid_vertex_diameter * 1.2 * scale, uid_vertex_diameter / 6.0 * scale);
  let box3_geometry = new THREE.BoxGeometry(uid_vertex_diameter * 1.2 * scale, uid_vertex_diameter / 6.0 * scale, uid_vertex_diameter / 6.0 * scale);
  box1_geometry.dynamic = true;
  box2_geometry.dynamic = true;
  box3_geometry.dynamic = true;

  let box_material = new THREE.MeshBasicMaterial({
    color: 0x333333,
    opacity: 0.8
  });
  box_material.color.setRGB( r, g, b );

  box_material.transparent = true;
  box_material.opacity = 0.8;
  box_material.depthTest = false;
  box_material.depthWrite = false;

  let box1_mesh = new THREE.Mesh(box1_geometry, box_material);
  let box2_mesh = new THREE.Mesh(box2_geometry, box_material);
  let box3_mesh = new THREE.Mesh(box3_geometry, box_material);

  // Position the vertex in 3D space
  box1_mesh.position.set(x, y, z);
  box2_mesh.position.set(x, y, z);
  box3_mesh.position.set(x, y, z);

  uid_rotation_center_raynode.add(box1_mesh);
  uid_rotation_center_raynode.add(box2_mesh);
  uid_rotation_center_raynode.add(box3_mesh);
}

export function hide_rotation_center() {
  for (var i = uid_rotation_center_raynode.children.length - 1; i >= 0; i--) {
    uid_rotation_center_raynode.remove(uid_rotation_center_raynode.children[i]);
  }
  animate();
}

export function set_linear_draw_direction() {
  //console_log('uid_linear_direction:', uid_linear_draw_direction);
  if (uid_linear_draw_direction == null) {
      const v1 = new THREE.Vector3();
      v1.copy(uid_camera_click_origin);
      const v2 = new THREE.Vector3();
      v2.copy(uid_raycaster.ray.origin);
      v2.sub(v1);
      //console_log('Length:', v2.length());
      if (v2.length() > viewSize/100) {
          uid_linear_draw_direction = new THREE.Vector3();
          const vside = new THREE.Vector3();
          vside.crossVectors(uid_camera_click_direction, uid_camera_click_up);
          const d1 = v2.dot(uid_camera_click_up);
          const d2 = v2.dot(vside);
          if (Math.abs(d1) > Math.abs(d2))
              uid_linear_draw_direction.copy(uid_camera_click_up);
          else
              uid_linear_draw_direction.copy(vside);
      }
  }
}

export function reset_linear_draw_direction() {
  uid_linear_draw_direction = null;
}

var uid_horizontal_vertical_rotate_direction = null;

export function set_horizontal_vertical_rotate_direction(p, p_prev) {

  if (!App.state.rotation_locked)
      uid_horizontal_vertical_rotate_direction = 'f'; // Free, i.e. unlocked
  else if (uid_horizontal_vertical_rotate_direction == null && p_prev != null) {
    let diffx = (p_prev.x - p.x) * App.state.canvas.width; // Revert from normalized to pixel coordinates, in case a screen is very thin.
    let diffy = (p_prev.y - p.y) * App.state.canvas.height; // Revert from normalized to pixel coordinates, in case a screen is very thin.

    if (Math.abs(diffx) > Math.abs(diffy))
      uid_horizontal_vertical_rotate_direction = 'h';
    else
      uid_horizontal_vertical_rotate_direction = 'v';
  }
}

export function reset_horizontal_vertical_rotate_direction() {
  let must_clear_text = false;
  if (uid_horizontal_vertical_rotate_direction != null)
    must_clear_text = true;

  uid_horizontal_vertical_rotate_direction = null;
  if (must_clear_text)
    // Clear text
    update_dom_text_values_and_coordinates();
}

export function get_horizontal_vertical_rotate_direction() {
  return uid_horizontal_vertical_rotate_direction;
}

export function get_horizontal_vertical_rotate_direction_text() {

  if (!App.state.rotation_locked)
    return '';

  if (uid_horizontal_vertical_rotate_direction == 'h')
    return "Locked horizontal rotation";
  else if (uid_horizontal_vertical_rotate_direction == 'v')
    return "Locked vertical rotation";
  else
    return '';
}

export function in_horizontal_vertical_rotation_band(p) {
  let in_horizontal_middle_band = false;
  let in_vertical_middle_band = false;
  let pointer_limited = new THREE.Vector2().copy(p);

  if (pointer_limited.y < 0.25 && pointer_limited.y > -0.15)
      in_horizontal_middle_band = true;

  if (pointer_limited.x < 0.25 && pointer_limited.x > -0.15)
      in_vertical_middle_band = true;

  return [in_horizontal_middle_band, in_vertical_middle_band];
}


export function add_snap_plane(x, y, z, rgbx, rgby, rgbz, normal, scale = 1) {
  var snap_geometry;

  if (normal == "x") {
    snap_geometry = new THREE.BoxGeometry(uid_vertex_diameter / 30.0 * scale, uid_vertex_diameter / 2.0 * scale, uid_vertex_diameter / 2.0 * scale, 1);
  }
  else if (normal == "y") {
    snap_geometry = new THREE.BoxGeometry(uid_vertex_diameter / 2.0 * scale, uid_vertex_diameter / 30.0 * scale, uid_vertex_diameter / 2.0 * scale, 1);
  }
  else if (normal == "z") {
    snap_geometry = new THREE.BoxGeometry(uid_vertex_diameter / 2.0 * scale, uid_vertex_diameter / 2.0 * scale, uid_vertex_diameter / 30.0 * scale, 1);
  }

  snap_geometry.dynamic = true;
  var snap_material = new THREE.MeshBasicMaterial({
    color: 0x333333,
    opacity: 0.1
  });
  snap_material.color.setRGB( rgbx/256, rgby/256, rgbz/256 );

  snap_material.transparent = true;
  snap_material.opacity = 0.4;
  snap_material.depthTest = false;
  snap_material.depthWrite = false;

  var snap_mesh = new THREE.Mesh(snap_geometry, snap_material);
  // Position the vertex in 3D space
  snap_mesh.position.set(x, y, z);
  uid_draw_edge_raynode.add(snap_mesh);
}

export function clear_small_balls() {
  for (var i = uid_draw_edge_raynode.children.length - 1; i >= 0; i--) {
    uid_draw_edge_raynode.remove(uid_draw_edge_raynode.children[i]);
  }
  animate();
}

//--------------- Mouse coordinates

export async function recalculate_view_dimensions(only_offset) {
  const c = document.getElementById('container-parent');
  const canvas = c.querySelector('canvas');
  if (!canvas) return;
  canvas.height = 0;
  canvas.width = 0;
  await next_frame();

  const rect = c.getBoundingClientRect();
  console_log('Recalculating view dimensions. Rect:', rect);
  xOffset = Math.floor(rect.left);
  yOffset = Math.floor(rect.top) + window.scrollY;

  if (!only_offset) {
    App.state.canvas = {
        width: Math.floor(rect.width),
        height: Math.floor(rect.height),
    };
    aspectRatio = App.state.canvas.width / App.state.canvas.height;

    if (uid_camera != null) {
      uid_camera.left = (-aspectRatio * viewSize) / 2;
      uid_camera.right = (aspectRatio * viewSize) / 2;
      uid_camera.top = viewSize / 2;
      uid_camera.bottom = -viewSize / 2;
      adjust_camera_up_and_update_projection_matrix(uid_camera);
      //uid_camera.updateProjectionMatrix();
    }

    resizeDiv();
  }
  animate();
}

export function from_normalized_to_actual_screen_coordinates(mn) {
  var ac = new THREE.Vector2();
  ac.x = (mn.x + 1)/2 * App.state.canvas.width + xOffset;
  ac.y = (mn.y * (-1) + 1)/2 * App.state.canvas.height + yOffset;
  return ac;
}

export function mouse_near_edges(mousenormalized, very = false) {
  var h_tol = 0.98;
  var v_tol = 0.95;
  if (very) {
    h_tol = 0.99;
    v_tol = 0.98;
  }
  return !(
    mousenormalized.x > -h_tol &&
    mousenormalized.x < h_tol &&
    mousenormalized.y > -v_tol &&
    mousenormalized.y < v_tol
  );
}

export function identify_faces_missing_tangencies() {
  var bad_faces = [];
  var angle_threshold = 10.0 * Math.PI/180.0; // Anythinng under 1 degree
  uid_faces.keys().forEach(function(fid){
    var bad_vertices = [];
    if (uil_is_object_visible(fid)) {
      vertices_of_face(fid).forEach(function(vid){
        //var missing_vertex_tangencies = vertex_missing_exact_tangencies_on_face(vid, fid, true);
        var missing_vertex_tangencies = vertex_missing_tangencies_approximately(vid, fid, angle_threshold);

        if (missing_vertex_tangencies.length > 0)
          bad_vertices.push(vid);
      });
    }
    if (number_of_proper_vertices_on_face(fid) - bad_vertices.length < 3)
      bad_faces.push(fid);
  });
  return bad_faces;
}

export function number_of_proper_vertices_on_face(fid) {
  var edge_pairs = find_all_pairs_in_unlistified_edge_list(uil_get_edges_of_face(fid));
  var proper = 0;
  edge_pairs.forEach(function(ep) {
    if (!uil_is_joint_smooth(ep[0], ep[1]) == 1)
      proper++;
  });
  return proper;
}

export function display_missing_tangencies(fix_tangencies) {
  var issues_found = 0;
  uid_vertices_with_undeclared_tangencies.clear();

  suppress_animate();

  var angle_threshold = App.state.smoothness_threshold_for_consistency_checks * Math.PI/180.0;

  if (fix_tangencies) {
    suppress_undo_tracking();
    uid_vertices.keys().forEach(function(vid) {
    if (uil_is_object_visible(vid)) {
      var missing_tangencies = vertex_missing_tangencies_approximately(vid, null, angle_threshold);
      issues_found += missing_tangencies.length;
        missing_tangencies.forEach(function (edge_pair) {
          var e1id = edge_pair[0];
          var e2id = edge_pair[1];

          // Here we want to add the missing tangency. The steps are:
          // - Check if the vertex is smooth, and if so unsmoothen it.
          var vertex_is_smooth = uil_is_vertex_smooth(vid);
          if (vertex_is_smooth)
            uil_remove_vertex_smoothness([vid]);

          // - Check if the vertex is unfixed, and if so fix it
          var vertex_is_fixed = uil_is_object_fixed(vid);
          if (!vertex_is_fixed)
            uil_fix_objects([vid]);

          // Add 2 vertices on either side, at parameter value 0.05 or 0.95, whichever is closest.
          let p1a = uil_get_point_at_edge_param(e1id, 0.05);
          let p1b = uil_get_point_at_edge_param(e1id, 0.95);
          let p = uid_vertices.getItem(vid).object3d.position;
          let par1 = v3.point_point_distance(p1a, p) < v3.point_point_distance(p1b, p) ? 0.05 : 0.95;

          let p2a = uil_get_point_at_edge_param(e2id, 0.05);
          let p2b = uil_get_point_at_edge_param(e2id, 0.95);
          let par2 = v3.point_point_distance(p2a, p) < v3.point_point_distance(p2b, p) ? 0.05 : 0.95;

          let v1id = uil_add_vertex_at_param(e1id, par1);
          let v2id = uil_add_vertex_at_param(e2id, par2);

          // - Identify the new edges
          let es = uil_get_edges_of_vertex(vid).filter(ee => (uil_get_edges_of_vertex(v1id).includes(ee) || uil_get_edges_of_vertex(v2id).includes(ee)) );

          // Check if any of the two is linear, in which case fix it
          let edges_to_fix_unfix = [];
          if (!uil_is_object_fixed(es[0]) && uil_is_edge_linear_geometry(es[0]) && !uil_is_edge_linear_geometry(es[1]))
            edges_to_fix_unfix.push(es[0])
          else if (!uil_is_object_fixed(es[1]) && uil_is_edge_linear_geometry(es[1]) && !uil_is_edge_linear_geometry(es[0]))
            edges_to_fix_unfix.push(es[1])
          uil_fix_objects(edges_to_fix_unfix);

          uil_add_joint_smoothness([es], FAINT_STRENGTH, true, false);

          // Unfix the edges we temporarily fixed
          uil_unfix_objects(edges_to_fix_unfix);
          uil_unfix_objects([v1id, v2id]);

          // Delete the vertices we added.
          uil_delete_vertices([v1id, v2id], true);

          // - If necessary unfix again the vertex.
          if (!vertex_is_fixed)
            uil_unfix_objects([vid]);
          else
            uil_fix_objects([vid]);

          // - If necessary smoothen again the vertex.
          if (vertex_is_smooth)
            uil_smoothen_vertices([vid]);

        });
      }
    });
    unsuppress_undo_tracking_if_ready(false);
    request_setting_button_states();
  }
  else {
    uid_vertices.keys().forEach(function(vid) {
      if (uil_is_object_visible(vid)) {
        var missing_tangencies = vertex_missing_tangencies_approximately(vid, null, angle_threshold);
        issues_found += missing_tangencies.length;
        if (missing_tangencies.length > 0) {
          // Add vertex to hash
          uid_vertices_with_undeclared_tangencies.setItem(vid, uid_vertices.getItem(vid));
          set_default_vertex_color(vid);
        }
      }
    });
  }
  unsuppress_animate_if_ready();
  return issues_found;
}

export function vertex_has_undeclared_tangency(vid) {
  return(uid_vertices_with_undeclared_tangencies.hasItem(vid));
}

export function vertex_missing_exact_tangencies_on_face(vid, face_id) {
  var es_unfiltered = uil_get_edges_of_vertex(vid);
  var es_vis = es_unfiltered.filter(e => uil_is_object_visible(e));
  var es;
  if (face_id)
    es = es_vis.filter(e => uil_get_edges_of_face(face_id).includes(e));
  else
    es = es_unfiltered;

  var i, j;
  var missing_tangencies = [];

  for (i = 0; i <= es.length-2; i++){
    for (j = i+1; j <= es.length-1; j++) {
      if ( uil_is_joint_geom_smooth(es[i], es[j]) && !uil_is_joint_smooth(es[i], es[j]) ) {
        missing_tangencies.push([es[i], es[j]]);
      }
    }
  }
  return missing_tangencies;
}

export function vertex_missing_tangencies_approximately(vid, fid, angle_threshold) {
  var es_unfiltered = uil_get_edges_of_vertex(vid);
  var es = es_unfiltered.filter(e => uil_is_object_visible(e));

  if (fid) {
    es = es.filter(e => uil_get_edges_of_face(fid).includes(e));
  }

  var tans = new Array(es.length);
  var i, j;
  var missing_tangencies = [];

  for (i = 0; i <= es.length-1; i++){
    var t = uil_get_edge_tangent_at_vertex(es[i], vid).normalize();
    if (t)
      tans[i] = t;
    else {
      tans[i] = null;
      console_log_xml("Unable to find tangency of edge " + es[i] + " on vertex " + vid);
      console.log("Unable to find tangency of edge " + es[i] + " on vertex " + vid);
    }
  }

  for (i = 0; i <= es.length-2; i++){
    for (j = i+1; j <= es.length-1; j++) {
      var t1 = tans[i];
      var t2 = tans[j];
      if (t1 && t2 && uil_is_joint_smooth(es[i], es[j]) == 0) {
        var angle = Math.abs(t1.angleTo(t2));
        angle = Math.min(angle, Math.abs(Math.PI-angle));
        if (angle < angle_threshold) {
          missing_tangencies.push([es[i], es[j]]);
        }
      }
    }
  }

  return missing_tangencies;
}

export function remove_tangencies_from_visible_invisible_edge_pairs() {
  suppress_animate();
  suppress_undo_tracking();
  suppress_core_face_tessellation();

  let selected_objs = [];
  selected_objs.push(...uid_selected_edges.keys());
  selected_objs.push(...uid_selected_vertices.keys());
  selected_objs.push(...uid_selected_faces.keys());
  uid_deselect_all_objects();
  uid_edges.keys().forEach(function(e1id){
    if (!uil_is_object_visible(e1id)) {
      let es = get_neighbouring_edges_of_edge(e1id);
      es.forEach(function(e2id){
        if (uil_is_object_visible(e2id))
          var res = uil_remove_joint_smoothness([[e1id, e2id]], true, false);
      });
    }
  });
  selected_objs.forEach(function(id){
    uid_select_object(id);
  });

  unsuppress_core_face_tessellation_if_ready();
  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
}

export function add_undeclared_tangencies() {
  // Explicitly add any tangencies that are not declared.
  // XXX XXX XXX THIS FUNCTION SEEMS TO CAUSE A MEMORY OUT OF BOUNDS ERROR
  var eps = 1e-3;
  var tangencies_added = 0;
  uid_vertices.keys().forEach(function(vid) {
    var es = uil_get_edges_of_vertex(vid);
    var i, j;
    for (i = 0; i < es.length-1; i++){
      for (j = i+1; j < es.length; j++) {
        var t1 = uil_get_edge_tangent_at_vertex(es[i], vid);
        if (t1)
          t1.normalize();
        var t2 = uil_get_edge_tangent_at_vertex(es[j], vid);
        if (t2)
          t2.normalize();
        if (t1 && t2 && uil_is_joint_smooth(es[i], es[j]) == 0 && maths.proximate(Math.abs(t1.dot(t2)), 1.0, eps)) {
          var l = [];
          if (uil_is_edge_linear(es[i]))
            l.push(es[i]);
          if (uil_is_edge_linear(es[j]))
            l.push(es[j]);
          if (l.length > 0)
            uil_set_linearity_of_edges(l, false);
          if (uil_add_joint_smoothness([[es[i], es[j]]]), global_smoothen_strength, true, false) {
            tangencies_added++;
          }
        }
      }
    }
  });
  if (tangencies_added > 0) {
    var t = "Added " + tangencies_added + " tangencies.";
    display_message_to_user(LOG.INFO, t);
  }
  else {
    display_message_to_user(LOG.INFO, "No missing tangencies were detected.");
  }
}

export function check_model_integrity(in_dragging = false, is_start_drag = false, is_end_drag = false, quick_and_dirty = true, on_vertices, on_rest) {
  /* Checks for:
     - Intersecting edges/faces
     - Inverted edge tangencies
     - Extreme curvatures
     - Very short edges
     - Very long mouse movements
     - Vertex continuity
  */

  //If we're not animating skip this.
  if (animation_is_suppressed())
    return;

  if (uid_state.shaders_on)
    return;

  var issues_found = 0;

  remove_all_glows();

  if (on_rest) {
    var edges_to_check = [];
    if (in_dragging && !is_end_drag) {
      if (App.state.dragging_type == "vertex") {
        // Check whether this is an actual vertex drag, or a manipulator vertex drag
        if (uid_raynode == uid_objects_raynode)
          edges_to_check = [... uil_get_edges_of_vertex(uid_drag_object.id)];
        else {
          uid_rigid_move_manipulator.rigid_move_vertex_object_ids.forEach(function(vid){
            edges_to_check.push(... uil_get_edges_of_vertex(vid));
          });
        }
      }
      else if (App.state.dragging_type == "edge") {
        edges_to_check.push(uid_drag_object.id);
      }
      else if (App.state.dragging_type == "edge_list") {
        if (uid_rigid_move_manipulator && uid_rigid_move_manipulator.rigid_move_edge_object_ids) {
          uid_rigid_move_manipulator.rigid_move_edge_object_ids.forEach(function (eid) {
            edges_to_check.push(... get_neighbouring_edges_of_edge(eid));
          });
          for (var i = edges_to_check.length-1; i >= 0; i--) {
            if (uid_rigid_move_manipulator.rigid_move_edge_object_ids.indexOf(edges_to_check[i]) != -1)
              edges_to_check.splice(i, 1);
          }
        }
      }
      else if (App.state.dragging_type == "scale_edge_list") {
        if (scale_edge_objects) {
          scale_edge_objects.forEach(function(eid) {
            edges_to_check.push(... get_neighbouring_edges_of_edge(eid));
          });
          for (var i = edges_to_check.length-1; i >= 0; i--) {
            if (scale_edge_objects.indexOf(edges_to_check[i]) != -1)
              edges_to_check.splice(i, 1);
          }
        }
      }
    }
    else {
      var all_edges = [... uid_edges.keys()];
      edges_to_check = all_edges.filter(e => uil_is_object_visible(e));
    }

    // Check edges
    var all_ok = true;
    consistency_problem_edge_list = [];
    consistency_problem_face_list = [];
    edges_to_check.forEach(function (eid) {
      var this_edge_ok = true;
      var vs = uil_get_vertices_of_edge(eid);
      var vertex_dist = v3.point_point_distance(uid_vertices.getItem(vs[0]).object3d.position, uid_vertices.getItem(vs[1]).object3d.position);
      var edge_length = uil_get_edge_length(eid);
      var stretch_factor = consistency_problem_stretch_factor;
      var bbsize = uid_calculate_global_bounding_box(true, false, false).dimension;
      var min_length_factor = consistency_problem_min_length_factor;
      var min_curv_radius_factor = consistency_problem_min_curv_radius_factor;

      // See if there are any tangencies on the edge ends.
      var esA = uil_get_edges_of_vertex(vs[0]);
      var esB = uil_get_edges_of_vertex(vs[1]);
      var tangents = 0;
      esA.forEach(function(eAid) {
        if (eAid != eid && uil_is_joint_smooth(eAid, eid) == 1)
          tangents++;
      });
      esB.forEach(function(eBid) {
        if (eBid != eid && uil_is_joint_smooth(eBid, eid) == 1)
          tangents++;
      });

      // Remove a stretch factor unit for each tangent.
      stretch_factor -= tangents;
      min_length_factor /= ((tangents+1)*2);

      // Check that length is not too short
      if (vertex_dist < bbsize/min_length_factor) {
        this_edge_ok = false;
      }
      else if (edge_length > stretch_factor * vertex_dist) {
          this_edge_ok = false;
      }
      else {
        // Check for extreme curvatures
        var returned_tuple = uil_get_edge_curvatures(eid, 100);
        if (returned_tuple) {
          var sample_coords = returned_tuple[0];
          var sample_vectors = returned_tuple[1];
          var max_curv = 0;
          sample_vectors.forEach(function(v){
            if (v.length() > max_curv)
              max_curv = v.length();
          });
          if (1.0/max_curv < bbsize/min_curv_radius_factor)
            this_edge_ok = false;
        }
      }

      if (!this_edge_ok) {
        all_ok = false;
        // Add edge to list of problematic edges
        if (consistency_problem_edge_list.indexOf(eid) == -1)
          consistency_problem_edge_list.push(eid);
      }

    });

    // Change coloring of faces and edges depending on all_ok
    if (!all_ok) {
      model_consistency_problems = true;
      if (!is_start_drag) {
        // Create list of faces to highlight
        consistency_problem_edge_list.forEach(function(eid) {
          var fs =uil_get_faces_of_edge(eid);
          if (consistency_problem_face_list.indexOf(fs[0]) == -1)
            consistency_problem_face_list.push(fs[0]);
          if (consistency_problem_face_list.indexOf(fs[1]) == -1)
            consistency_problem_face_list.push(fs[1]);
        });

        consistency_problem_face_list.forEach(function(fid){
          set_default_face_color(fid);
        });
        consistency_problem_edge_list.forEach(function(eid){
          set_default_edge_color(eid);
        });
      }
      issues_found = consistency_problem_edge_list.length + consistency_problem_face_list.length;
      consistency_problem_edge_list = [];
      consistency_problem_face_list = [];
      animate();
    }
    else if (model_consistency_problems) {
      model_consistency_problems = false;
      uid_faces.keys().forEach(function(fid){
        set_default_face_color(fid);
      });
      consistency_problem_edge_list.forEach(function(eid){
        set_default_edge_color(eid);
      });
      issues_found = consistency_problem_edge_list.length + consistency_problem_face_list.length
      consistency_problem_edge_list = [];
      consistency_problem_face_list = [];
      animate ();
    }
  }

  if (on_vertices) {
    if (!in_dragging || (in_dragging && uid_in_dragging_mode))
      issues_found += display_missing_tangencies(false);
  }

  // Add timeout for removing all glows
  var timeout_period = quick_and_dirty ? 750 : 1000;
  if (remove_all_glows_timeout_handle)
    clearTimeout(remove_all_glows_timeout_handle);
  remove_all_glows_timeout_handle = setTimeout(function() { remove_all_glows();}, timeout_period);
  App.state.consistency_issues_found = issues_found;
  return issues_found;
}

export function remove_all_glows() {
  if (remove_all_glows_timeout_handle)
    clearTimeout(remove_all_glows_timeout_handle);
  for (var i = uid_glow_raynode.children.length - 1; i >= 0; i--) {
    uid_glow_raynode.remove(uid_glow_raynode.children[i]);
  }

  consistency_problem_edge_list = [];
  consistency_problem_face_list = [];
  uid_vertices_with_undeclared_tangencies.clear();

  uid_edges.keys().forEach(function(eid){
    set_default_edge_color(eid);
  });
  uid_faces.keys().forEach(function(fid){
    set_default_face_color(fid);
  });
  uid_vertices.keys().forEach(function(vid){
    set_default_vertex_color(vid);
  });
  animate();
}

//------------------------ Snapping

export function identify_snapping_targets(movement_type, moving_objects_construct, must_display_targets, must_snap_to_targets) {

  // If we're in grid mode simply exit. The two don't mix (for now)
  if (App.state.grid_visible)
    return;

  // Check for snapping
  var eps = uid_grid_step;
  var snap_status = null;
  var x_translation = 0.0;
  var y_translation = 0.0;
  var z_translation = 0.0;
  var only_moving_vertices = true;

  clear_small_balls();

  var moving_objects;
  if (movement_type == "dragging_vertices") {
    // The moving_objects_construct is an array of objects.
    moving_objects = moving_objects_construct;
  }
  else if (movement_type == "rigid_move") {
    // The moving_objects_construct is the rigid_move_manipulator.
    moving_objects = [... moving_objects_construct.rigid_move_vertex_object_ids];
    moving_objects_construct.rigid_move_edge_object_ids.forEach(function(eid){
      var vs = uil_get_vertices_of_edge(eid);
      if (moving_objects.indexOf(vs[0]) == -1)
        moving_objects.push(vs[0]);
      if (moving_objects.indexOf(vs[1]) == -1)
        moving_objects.push(vs[1]);
    });
    if (moving_objects_construct.rigid_move_face_object_id) {
      var vs = vertices_of_face(moving_objects_construct.rigid_move_face_object_id);
      vs.forEach(function(vid){
        if (moving_objects.indexOf(vid) == -1)
        moving_objects.push(vid);
      });
    }
  }

  var candidate_target_vertices = new HashTable();
  uid_vertices.values().forEach(function(other_vertex) {
    if (moving_objects.indexOf(other_vertex.id) == -1)
      candidate_target_vertices.setItem(other_vertex.id, other_vertex);
  });

  moving_objects.forEach(function(moid){
    var o = uid_vertices.getItem(moid);
    if (o) {
      candidate_target_vertices.values().forEach(function(other_vertex) {
        if (maths.proximate(other_vertex.object3d.position.x, o.object3d.position.x, eps)) {
          snap_status = "Snap X";
          x_translation = other_vertex.object3d.position.x - o.object3d.position.x;
          if (must_display_targets)
            add_snap_plane(other_vertex.object3d.position.x, other_vertex.object3d.position.y, other_vertex.object3d.position.z, 255, 170, 170, "x", 4)
        }
        if (maths.proximate(other_vertex.object3d.position.y, o.object3d.position.y, eps)) {
          snap_status = "Snap Y";
          y_translation = other_vertex.object3d.position.y - o.object3d.position.y;
          if (must_display_targets)
            add_snap_plane(other_vertex.object3d.position.x, other_vertex.object3d.position.y, other_vertex.object3d.position.z, 170, 255, 170, "y", 4);
        }
        if (maths.proximate(other_vertex.object3d.position.z, o.object3d.position.z, eps)) {
          snap_status = "Snap Z";
          z_translation = other_vertex.object3d.position.z - o.object3d.position.z;
          if (must_display_targets)
            add_snap_plane(other_vertex.object3d.position.x, other_vertex.object3d.position.y, other_vertex.object3d.position.z, 170, 170, 255, "z", 4);
        }
      });
      if (snap_status) {
        console_log(snap_status);
      }
    }
    else {
      // Simply mark that we're not just moving a list of vertices
      only_moving_vertices = false;
    }
  });

  if (must_snap_to_targets) {
    suppress_animate();

    // Translate all objects in the moving set.
    // If we only have vertices, move each one of the vertices using
    // uil_move_vertex_to_coords( vid, newx, newy, newz, uid_get_ball_size(), uid_get_ball_hardness() );
    // Otherwise form an edge list and move that using
    // uil_directly_move_edge_list(edge_ids, translation_vector, rotation_angle, rotation_axis_base, rotation_axis_direction);
    if (movement_type == "dragging_vertices") {
      moving_objects.forEach(function(moid) {
        var v = uid_vertices.getItem(moid);
        uil_move_vertex_to_coords( moid, v.object3d.position.x + x_translation, v.object3d.position.y + y_translation, v.object3d.position.z + z_translation );
      });
    }
    else if (movement_type == "rigid_move") {
      // Either we are dragging one or more vertices using the rigid move manipulator, or we are dragging a set of edges/faces/vertices.
      // We handle these two cases differently.
      if (!uid_rigid_move_manipulator.rigid_move_face_object_id && uid_rigid_move_manipulator.rigid_move_edge_object_ids.length == 0) {
        // Must exit drag mode for this action.
        uil_end_drag_manipulator();
        uid_hide_rigid_move_manipulator(true, 0, false);
        moving_objects.forEach(function(moid) {
          var v = uid_vertices.getItem(moid);
          uil_move_vertex_to_coords( moid, v.object3d.position.x + x_translation, v.object3d.position.y + y_translation, v.object3d.position.z + z_translation );
        });
        uid_show_rigid_move_manipulator(true, 0, false, false, false, false, false, false);
      }
      else {
        // Form list of edge_ids to move from moving objects
        var edge_ids = [];
        moving_objects.forEach(function(moid) {
          if (isEdge(moid))
          edge_ids.push(moid);
          else if (isFace(moid)) {
            edge_ids.push([... uil_get_edges_of_face(moid)]);
          }
        });
        var translation_vector = new THREE.Vector3(x_translation, y_translation, z_translation);
        var v_bogus = new THREE.Vector3(1,0,0);
        uil_directly_move_edge_list(edge_ids, translation_vector, 0, v_bogus, v_bogus);

        // Re-position manipulator
        uid_hide_rigid_move_manipulator(true, 0, false);
        uid_show_rigid_move_manipulator(true, 0, false, false, false, false, false, false);
      }
    }
    unsuppress_animate_if_ready();
  }
}

export function display_consistency_checks_modal() {
  show_modal("consistencyChecksModal");
}

export function run_consistency_checks(fix_tangencies, force_checks = false) {
  //console.log("In run consistency checks");

  var issues_found = 0;
  if (fix_tangencies)
    issues_found = display_missing_tangencies(true);
  else
    issues_found = check_model_integrity(false, false, false, false,
                                         (App.state.always_run_consistency_checks_on_vertices || force_checks),
                                         App.state.always_run_consistency_checks_on_rest);

  App.state.consistency_issues_found = issues_found;
  update_dom_text_values_and_coordinates();

  animate();
  if (fix_tangencies)
    display_message_to_user(LOG.INFO, "Checked model. Attempted to correct " + issues_found + " issues.");
  else
    display_message_to_user(LOG.INFO, "Checked model. Found " + issues_found + " issues.");

  if (fix_tangencies)
    App.state.consistency_issues_found = display_missing_tangencies(false);
}

export function display_set_smoothen_strength_modal() {
  show_modal("setSmoothenStrengthModal");
}

export function set_smoothen_strength(strength) {
  global_smoothen_strength = strength;
}

// ---------- Semaphores

// Suppressing undo tracking

/** Semaphore counter for suppressing undo tracking. */
let uid_suppress_undo_tracking_count = 0;

/** Semaphore for suppressing undo */
export function suppress_undo_tracking() {
  if (uid_suppress_undo_tracking_count == 0)
    uil_stop_undo_tracking();
  uid_suppress_undo_tracking_count++;
  //console.log("Suppress, count: " + uid_suppress_undo_tracking_count);
}

export function unsuppress_undo_tracking_if_ready(dont_save_state = false) {
  if (uid_suppress_undo_tracking_count > 0) {
    uid_suppress_undo_tracking_count--;
    if (uid_suppress_undo_tracking_count == 0) {
      uil_start_undo_tracking();
      if (!dont_save_state)
        uil_save_undo_state();
    }
  }
  //console.log("Unsuppress count: " + uid_suppress_undo_tracking_count);
}

export function undo_tracking_is_suppressed() {
  return uid_suppress_undo_tracking_count > 0;
}

export function reset_undo_tracking(also_save_state) {
  uid_suppress_undo_tracking_count = 0;
  uil_start_undo_tracking();
  if (also_save_state)
    uil_save_undo_state();
}

// Core face tesselation

/** Semaphore counter for suppressing core side face tessellation. */
let uid_suppress_core_face_tessellation_count = 0

/** Semaphore for suppressing core side face tessellation */
export function suppress_core_face_tessellation() {
  if (uid_suppress_core_face_tessellation_count == 0)
    uil_set_face_tessel(false);
  uid_suppress_core_face_tessellation_count++;
}

export function unsuppress_core_face_tessellation_if_ready() {
  // Remember selected objects
  let fs = [... uid_selected_faces.keys()];
  let es = [... uid_selected_edges.keys()];
  let vs = [... uid_selected_vertices.keys()];

  if (uid_suppress_core_face_tessellation_count > 0) {
    uid_suppress_core_face_tessellation_count--;
    if (uid_suppress_core_face_tessellation_count == 0)
      uil_set_face_tessel(true);
  }
  fs.forEach(function(f){uid_select_object(f)});
  es.forEach(function(e){uid_select_object(e)});
  vs.forEach(function(v){uid_select_object(v)});
}

export function reset_core_face_tessellation() {
  uid_suppress_core_face_tessellation_count = 0;
  uil_set_face_tessel(true);
}

export function core_face_tessellation_is_suppressed() {
  return uid_suppress_core_face_tessellation_count > 0;
}

// Properly splitting invisible components

/** Semaphore counter for suppressing core side face tessellation. */
let uid_properly_split_invisible_component_count = 0

export function suppress_properly_splitting_invisible_components() {
  uid_properly_split_invisible_component_count++;
}

export function unsuppress_properly_splitting_invisible_components_if_ready() {
  if (uid_properly_split_invisible_component_count > 0)
  uid_properly_split_invisible_component_count--;
}

export function properly_splitting_invisible_components_is_suppressed() {
  return (uid_properly_split_invisible_component_count > 0);
}

export function reset_properly_splitting_invisible_components() {
  uid_properly_split_invisible_component_count = 0;
}

//------------------------ Animation

/** Semaphore counter for suppressing animation and counting whether we need to set the button states. */
let uid_suppress_animate_count = 0;

export function suppress_animate() {
  uid_suppress_animate_count++;
}

export function unsuppress_animate_if_ready(set_button_states = true) {
  if (uid_suppress_animate_count > 0)
    uid_suppress_animate_count--;

  if (uid_suppress_animate_count == 0) {
      if (set_button_states)
        request_setting_button_states();
      animate();
  }
}

export function animation_is_suppressed() {
    return (uid_suppress_animate_count > 0);
}

export function reset_animate_count() {
  uid_suppress_animate_count = 0;
}

export const request_setting_button_states = on_next_frame(set_button_states);

// Zero thickness checking

/** Semaphore counter for suppressing zero thickness checking. */
let uid_suppress_zero_thickness_checking_count = 0;

export function suppress_zero_thickness_checking() {
  uid_suppress_zero_thickness_checking_count++;
}

export function unsuppress_zero_thickness_checking_if_ready() {
  if (uid_suppress_zero_thickness_checking_count > 0)
    uid_suppress_zero_thickness_checking_count--;

  if (uid_suppress_zero_thickness_checking_count == 0)
    check_for_zero_thickness_faces();
}

export function zero_thickness_checking_is_suppressed() {
    return (uid_suppress_zero_thickness_checking_count > 0);
}

export function reset_zero_thickness_checking_count() {
  uid_suppress_zero_thickness_checking_count = 0;
}

//------------------------ Modals and scrolling

export function hideTutorialsModal() {
  if (!App.state.is_demo)
    hide_modal("tutorialsModal");
  else
    hide_modal("tutorialsModalDemo");
}

//----------------------- Cameras etc.

export function adjust_camera_up_and_update_projection_matrix(cam) {
    // Make camera up perpendicular to look direction
    let a = get_camera_look_vector(cam).angleTo(cam.up);
    if (a != Math.PI/2.0) {
      let newv = new THREE.Vector3().crossVectors(get_camera_look_vector(cam), cam.up).normalize();
      let newv1 = new THREE.Vector3().crossVectors(newv, get_camera_look_vector(cam)).normalize();
      cam.up.copy(newv1);
    }
    cam.updateProjectionMatrix();
}

export function set_camera_target(uid_camera, t) {
  //uid_camera.lookAt(t);
  if (t)
    uid_camera.lookAt(t);
  else
    uid_camera.lookAt(new THREE.Vector3(0,0,0));
  adjust_camera_up_and_update_projection_matrix(uid_camera);
  //uid_camera.updateProjectionMatrix();
}

export function get_camera_look_vector(uid_camera) {
  if (!uid_camera) {
    console.log("Camera not defined yet in get_camera_look_vector.")
    var look_vector = new THREE.Vector3(0, 0, -1);
    return look_vector;
  }


  //The camera does not have a "look vector", so you cannot set it.
  //You can, however, construct a point to look at by adding your look vector to the camera's position, and then call
  //camera.lookAt( point );
  //Here is how to determine the direction in which the camera is looking -- assuming the camera either has no parent (other than the scene).
  //The camera is looking down it's internal negative z-axis, so create a vector pointing down the negative z-axis:
  var look_vector = new THREE.Vector3(0, 0, -1);
  //Now, apply the same rotation to the vector that is applied to the camera:
  look_vector.applyQuaternion(uid_camera.quaternion);
  look_vector.normalize();
  return look_vector;

  // The resulting vector will be pointing in the direction that the camera is looking.
  // See: http://stackoverflow.com/questions/15696963/three-js-set-and-read-camera-look-vector
  // http://stackoverflow.com/questions/15696963/three-js-set-and-read-camera-look-vector/15697227#15697227
}

export function adjust_camera_position_along_axis_and_depth(cam) {
  if (!uid_camera)
    return;
  if (uid_vertices.length == 0)
    return;

  let bb = uid_calculate_global_bounding_box(true, false, false);
  let bb_center = new THREE.Vector3(bb.centerx, bb.centery, bb.centerz);
  let ideal_camera_dist = bb.dimension * 40;

  // Adjust camera far
  if (cam.far < ideal_camera_dist * 20)
    cam.far = ideal_camera_dist * 20;

  // Adjust camera position

  let current_camera_dist = v3.point_point_distance(bb_center, cam.position);

  if (current_camera_dist / ideal_camera_dist > 3 || current_camera_dist / ideal_camera_dist < 0.33) {
    // Slide camera along its look vector
    const look_vector = get_camera_look_vector(cam).normalize();

    // Project bbox center onto look vector that's the closest point the camera can get to the center along its
    // look vector.
    const proj = v3.project_point_on_line(bb_center, cam.position, look_vector);
    const ptoproj = new THREE.Vector3().subVectors(proj, cam.position);
    let displ = new THREE.Vector3().copy(look_vector).normalize();
    if (ptoproj.dot(look_vector) > 0)
      displ.multiplyScalar(-1);

    let new_position = proj.addScaledVector(displ, ideal_camera_dist * 0.5);
    cam.position.copy(new_position)  ;
  }

  cam.updateProjectionMatrix();
  animate();
}

export function rotate_camera_by_axis_and_angle(cam, rotation_target, axis, angle) {
  // Rotates the camera based on an axis and angle, keeping the rotation target at the same location within the viewport.
  if (!rotation_target)
    rotation_target = new THREE.Vector3();

  // The current camera look, up and side vectors
  let look_v = get_camera_look_vector(cam);
  let up_v = new THREE.Vector3().copy(cam.up).normalize();
  let side_v = new THREE.Vector3().crossVectors(look_v, up_v);

  // Define a plane that is perpendicular to the camera look vector, and passes through the target point, and intersect it with the
  // camera look vector to find the point on it that is straight ahead of the camera. And the distance between this point and
  // the target point. Break them down in movement along the camera.up direction, and movement along the sideways direction.
  const camera_target_plane_center = plane_ray_intersect(rotation_target, look_v, cam.position, look_v, 1.0);
  const center_target_vector = new THREE.Vector3().subVectors(rotation_target, camera_target_plane_center);
  let up_move = center_target_vector.dot(up_v);
  let side_move = center_target_vector.dot(side_v);
  let look_move = v3.point_point_distance(cam.position, camera_target_plane_center);

  const quaternion = new THREE.Quaternion().setFromAxisAngle(axis, angle);

  // Shift camera so it comes directly in front of target point
  cam.position.add(look_v.multiplyScalar(look_move));
  cam.position.add(up_v.multiplyScalar(up_move));
  cam.position.add(side_v.multiplyScalar(side_move));

  // Rotate camera
  cam.up.applyQuaternion(quaternion);
  cam.applyQuaternion(quaternion);

  // Shift camera back
  // Re-calculate current look, up and side vectors
  look_v = get_camera_look_vector(cam);
  up_v = new THREE.Vector3().copy(cam.up).normalize();
  side_v = new THREE.Vector3().crossVectors(look_v, up_v);
  look_move *= -1;
  up_move *= -1;
  side_move *= -1;
  cam.position.add(look_v.multiplyScalar(look_move));
  cam.position.add(up_v.multiplyScalar(up_move));
  cam.position.add(side_v.multiplyScalar(side_move));
  animate();
}

export async function rotate_camera_by_axis_and_angle_in_steps(cam, camera_target_point, axis, angle, n_steps) {
  const n_edges = uid_edges.length + uid_reference_edges.length;
  const must_make_faster = n_edges > 1000;
  if (must_make_faster)
    n_steps = 4;
  let time_step = 10;
  for (var i = 0; i < n_steps; i++) {
      rotate_camera_by_axis_and_angle(cam, camera_target_point, axis, angle/n_steps);
      update_dom_text_values_and_coordinates();
      animate();
      await sleep(time_step);
  }
}

export function move_camera_by_vector(cam, v) {
    cam.position.add(v);
    animate();
}

export function move_camera_to(final_position, final_look_at, final_up) {
  if (!uid_camera) {
    console.log("Camera not defined yet in move_camera_to.")
    return;
  }

  if (final_position == null || final_look_at == null || final_up == null) {
    console.log("Null input vector!");
    console.log("Final position: ", final_position);
    console.log("Final lookat: ", final_look_at);
    console.log("Final up: ", final_up);
    return;
  }

  var initial_position = new THREE.Vector3();
  initial_position.copy(uid_camera.position);

  var initial_look_vector = get_camera_look_vector(uid_camera);
  var arbitrary_initial_lookat = new THREE.Vector3(initial_position.x, initial_position.y, initial_position.z);
  var pri = plane_ray_intersect(final_look_at, initial_look_vector, initial_position, initial_look_vector, 1);
  if (pri == null) {
    pri = new THREE.Vector3(0,0,0);
    console.log("PRI null;")
  }
  var dist = v3.point_point_distance(initial_position, pri);
  arbitrary_initial_lookat.addScaledVector(initial_look_vector, dist);

  var initial_up = new THREE.Vector3();
  initial_up.copy(uid_camera.up);
  const n_edges = uid_edges.length + uid_reference_edges.length;
  const must_make_faster = n_edges > 1000;
  const steps = must_make_faster ? 5 : 15;
  var time_step = 10;

  if (uid_state.camera_still_moving)
    return Promise.resolve(undefined);
  return camera_move_step(
    initial_position,
    arbitrary_initial_lookat,
    initial_up,
    final_position,
    final_look_at,
    final_up,
    steps,
    time_step,
  );
}

export function camera_move_step(
    initial_position,
    arbitrary_initial_lookat,
    initial_up,
    final_position,
    final_look_at,
    final_up,
    steps,
    time_step,
) {
    if (uid_state.camera_still_moving)
        return Promise.resolve(undefined);
    return new Promise(async (done) => {
        uid_state.camera_still_moving = true;
        for (let step = 0; step <= steps; step++) {
            // Calculate new target position for this step
            const target_position = new THREE.Vector3()
                .copy(initial_position);
            const v = new THREE.Vector3()
                .subVectors(final_position, initial_position);
            target_position.addScaledVector(v, step/steps);
            uid_camera.position.set(target_position.x, target_position.y, target_position.z);

            // Calculate new camera up direction for this step
            const target_camera_up = new THREE.Vector3(initial_up.x, initial_up.y, initial_up.z);
            const u = new THREE.Vector3()
                .crossVectors(initial_up, final_up)
                .normalize();
            const a = v3.cc_angle(initial_up, final_up, u);

            target_camera_up.applyAxisAngle( u, a*step/steps );
            uid_camera.up.copy(target_camera_up);

            // Calculate new lookAt direction for this step
            const target_lookat = new THREE.Vector3()
                .copy(arbitrary_initial_lookat);
            const w = new THREE.Vector3()
                .subVectors(final_look_at, arbitrary_initial_lookat);
            target_lookat.addScaledVector(w, step/steps);
            uid_camera.lookAt(target_lookat);

            adjust_camera_up_and_update_projection_matrix(uid_camera);
            //uid_camera.updateProjectionMatrix();
            update_dom_text_values_and_coordinates();
            animate();
            await sleep(time_step);
        }
        uid_state.camera_still_moving = false;
        done(undefined);
    });
}

export function calc_side_views(O, P, d, n, P_r, d_r, n_r, P_u, d_u, n_u) {
  var v = new THREE.Vector3();
  v.subVectors(O, P);
  var delta = v.length() * -1;
  d_r.crossVectors(n, d);
  n_r.copy(n);
  P_r.copy(O);
  P_r.addScaledVector(d_r, delta);
  d_u.copy(n);
  d_u.multiplyScalar(-1);
  n_u.copy(d);
  P_u.copy(O);
  P_u.addScaledVector(d_u, delta);
}

export function rotate_viewpoint_actions() {
  if (viewing_down_orthogonal_plane) {
    // We're just leaving the orthogonal plane. Change grid color, if grid is visible.
    if (App.state.grid_visible) {
      toggle_grid(null, true);
      toggle_grid(null, true);
    }
  }
  viewing_down_orthogonal_plane = false;
  //update_dom_text_values_and_coordinates();
  hide_halo();
}

export function must_view_down_negative_direction(axis_to_switch_to) {
  var axis;
  if (axis_to_switch_to == "x")
    var axis = new THREE.Vector3(1, 0, 0);
  else if (axis_to_switch_to == "y")
    var axis = new THREE.Vector3(0, 1, 0);
  else
    var axis = new THREE.Vector3(0, 0, 1);

  var look_vector = get_camera_look_vector(uid_camera).normalize();
  var eps = 0.0001;
  var angle = v3.angle( look_vector, axis );
  if (maths.proximate(angle, 0, eps))
    return true; // We're looking down the axis, go -ve
  else if (maths.proximate(angle, Math.PI, eps))
    return false; // We're looking down the -axis, go +ve
  else if (angle < Math.PI/2)
    return false; // We're closer to down the axis than to the -axis. Stay there.
  else
    return true; // We're closer to down the -axis than to the axis. Stay there.
}

export function camera_looking_down_orthogonal_axis(uid_camera) {
    var xaxis = new THREE.Vector3(1, 0, 0);
    var yaxis = new THREE.Vector3(0, 1, 0);
    var zaxis = new THREE.Vector3(0, 0, 1);
    var look_vector = get_camera_look_vector(uid_camera).normalize();
    var dx = Math.abs(look_vector.dot(xaxis));
    var dy = Math.abs(look_vector.dot(yaxis));
    var dz = Math.abs(look_vector.dot(zaxis));
    var eps = 0.00001;
    if (maths.proximate(dx, 1.0, eps) || maths.proximate(dy, 1.0, eps) || maths.proximate(dz, 1.0, eps))
      return true;
    else
      return false;
}

export function find_camera_target_point_for_rotation(cam) {
  var camera_target_point = null;
  var n = get_camera_look_vector(cam).normalize();
  uid_raycaster.setFromCamera(pointer_normalised, cam);
  var intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
  if (intersects.length > 0) {
    // Find intersection object
    var intersected_obj = find_intersection_object(intersects, true);
    if (intersected_obj) {
      camera_target_point = new THREE.Vector3();
      camera_target_point.copy(intersected_obj.point);
    }
  }
  if (!camera_target_point) {
    // Set camera target on center of BBox containing only objects currently within the viewport.
    var bb = uid_calculate_viewport_object_bounding_box();
    camera_target_point = new THREE.Vector3(bb.centerx, bb.centery, bb.centerz);
  }
  return camera_target_point;
}

export function find_camera_target_point_from_mouse_location(cam) {
  return find_camera_target_point_from_normalized_2d_screen_coordinates(cam, pointer_normalised);
}

export function find_camera_target_point_from_normalized_2d_screen_coordinates(cam, coords) {
  var camera_target_point = null;
  var n = get_camera_look_vector(cam).normalize();
  uid_raycaster.setFromCamera(coords, cam);
  var intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
  if (intersects.length > 0) {
    // Find intersection object
    var intersected_obj = find_intersection_object(intersects, true);
    if (intersected_obj) {
      camera_target_point = new THREE.Vector3();
      camera_target_point.copy(intersected_obj.point);
    }
  }
  if (!camera_target_point) {
    // Set camera target on intersection of camera look vector and plane perpendicular to it passing through
    // BBox center

    var mouse3d = from_2d_canvas_coordinates_to_3d_world_coordinates(coords);
    var bb = uid_calculate_global_bounding_box(true, false, false);
    var c = new THREE.Vector3(bb.centerx, bb.centery, bb.centerz);
    camera_target_point = plane_ray_intersect(c, n, mouse3d, n, 1.0);
  }
  return camera_target_point;
}

export function find_camera_target_point_for_switch_view(cam) {

  let camera_target_point = new THREE.Vector3();

  // Check how "zoomed in" we are w.r.t. the object bounding box. We will use this to decide whether we'll be
  // rotating around the "center of mass" or around the "hit location" of the camera ray.
  let rotate_around_center_of_mass = true;

  if (we_are_currently_very_zoomed_in(cam))
  rotate_around_center_of_mass = false;

  if (rotate_around_center_of_mass) {
    const bb = uid_calculate_global_bounding_box(true, false, false);
    camera_target_point.set(bb.centerx, bb.centery, bb.centerz);
  }
  else {
      //console.log("Rotating around ray hit point");
      const camera_trace = new THREE.Vector2(0, 0);
      uid_raycaster.setFromCamera(camera_trace, cam);
      const intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
      const intersected_obj = find_intersection_object(intersects);
      if (intersected_obj)
          camera_target_point.copy(intersected_obj.point);
      else {
          /* Option 1:
          // Set target at point of intersection with plane parallel to screen through object center of mass.
          const c = new THREE.Vector3(bb.centerx, bb.centery, bb.centerz);
          camera_target_point = plane_ray_intersect(
              c,
              get_camera_look_vector(cam).normalize(),
              cam.position,
              get_camera_look_vector(cam).normalize(),
              1.0,
          );
          */
          /* Option 2: */
          // Set target at center of mass of visible part of the objects.
          const bb = uid_calculate_viewport_object_bounding_box();
          camera_target_point = new THREE.Vector3(bb.centerx, bb.centery, bb.centerz);
      }
  }

  return camera_target_point;
}

export function we_are_currently_very_zoomed_in(cam) {
  const bb = uid_calculate_global_bounding_box(true, false, false);
  let zoomed_in = false;
  if (!point_is_in_viewport(new THREE.Vector3(bb.minx, bb.centery, bb.centerz)) ||
      !point_is_in_viewport(new THREE.Vector3(bb.maxx, bb.centery, bb.centerz)) ||
      !point_is_in_viewport(new THREE.Vector3(bb.centerx, bb.miny, bb.centerz)) ||
      !point_is_in_viewport(new THREE.Vector3(bb.centerx, bb.maxy, bb.centerz)) ||
      !point_is_in_viewport(new THREE.Vector3(bb.centerx, bb.centery, bb.minz)) ||
      !point_is_in_viewport(new THREE.Vector3(bb.centerx, bb.centery, bb.maxz)))
    zoomed_in = true;
  return zoomed_in;
}

export function set_camera_rotation_around_target_point(camera_target_point) {
  var n = get_camera_look_vector(uid_camera).normalize();
  var up = new THREE.Vector3();
  up.copy(uid_camera.up);
  var p = plane_ray_intersect(uid_camera.position, n, camera_target_point, n, 1.0);
  move_camera_to(p, camera_target_point, up);

  show_rotation_center(camera_target_point.x, camera_target_point.y, camera_target_point.z, true);
  setTimeout(function() {hide_rotation_center()},500);
  animate();
}


//-----------------------------------------------
// Initialization and tutorials
//-----------------------------------------------

export function set_default_appearance() {
  hide_axes();
  hide_shadows();
  view_all();
}

export async function closing_welcome_window(go_to_tutorial) {
  hideTutorialsModal();

  if (App.state.is_demo) {
    App.state.tut_stage = 1;
    App.state.aside = 'tutorials';
    return;
  }

  if (go_to_tutorial)
    App.state.aside = 'intro';

  await sleep(300);
  App.state.modal.selectModelModal = true;
}

export function clearSelectedFile(file) {
  const input = document.querySelector(`#${file}`)
  const label = document.querySelector(`label[for='${file}'`)
  const filePlaceholder = label.querySelector('.selected-file');

  if (input) {
      input.value = '';
      label.classList.toggle('has-file')
      filePlaceholder.innerHTML = ''
  }
}

// ------------------------------------
// Memory allocation and calling the core
// ------------------------------------

export function allocate_heapBytes_array(input_array, type) {

  var b = allocate_heapBytes_array_bundle(input_array, type);
  return b[0];
}

export function allocate_heapBytes_array_bundle(input_array, type) {
  var data;
  if (type == "int") {
    // Copy the input array elements into an Int32Array
    data = new Int32Array(input_array.length);
  }
  else if (type == "float") {
    // Copy the input array elements into an Float32Array
    data = new Float32Array(input_array.length);
  }
  for (var i = 0; i < data.length; i++)
    data[i] = input_array[i];

  // get the length of the data in bytes
  var numBytes = data.length * data.BYTES_PER_ELEMENT;

  // malloc enough space for the data
  var ptr = Module._malloc(numBytes);

  // get a bytes-wise view on the newly allocated buffer
  var heapBytes = new Uint8Array(Module.HEAPU8.buffer, ptr, numBytes);

  // copy data into heapBytes
  heapBytes.set(new Uint8Array(data.buffer));
  return [heapBytes, ptr, numBytes];
}

export function get_heapbytes(bundle) {
  return bundle[0];
}

export function reallocate_view(bundle) {
  var heapBytes = new Uint8Array(Module.HEAPU8.buffer, bundle[1], bundle[2]);
  return heapBytes;
}

export function reallocate_view_and_get_array(bundle, arraytype, length) {
  var heapBytes = new Uint8Array(Module.HEAPU8.buffer, bundle[1], bundle[2]);
  var heapArray;
  if (arraytype == "Int32Array")
    heapArray = new Int32Array(heapBytes.buffer, heapBytes.byteOffset, length);
  else if (arraytype == "Float32Array")
    heapArray = new Float32Array(heapBytes.buffer, heapBytes.byteOffset, length);
  else
    console_log_xml("ERROR ARRAY TYPE");
  return heapArray;
}

export function free_heapBytes_array(heapBytes) {
  Module._free(heapBytes.byteOffset);
}

export function free_array(arr) {
  if (Array.isArray(arr))
    arr.length = 0;
  arr = null;
}

// ---------------------------------------------------
//  Reference objects
// ---------------------------------------------------

export function delete_selected_reference_partstudio_body() {
  delete_selected_reference_body();
}

export function delete_selected_reference_stl_body() {
  delete_selected_reference_body();
}

export function delete_selected_reference_body() {
    if (uid_selected_reference_faces.length == 0)
      return;

    const rf_geometry = uid_selected_reference_faces[0];
    const uido = uid_object3dIDToUid_object.getItem(rf_geometry.id);
    const ref_face = uid_reference_faces.getItem(uido.id);
    const body_id = ref_face.onshape_body_id;

    const [faces_to_delete, edges_to_delete, vertices_to_delete] = collect_reference_objects_of_selected_body();

    deselect_all_reference_faces();

    // Delete each face
    for (const id of faces_to_delete) {
        const ref_face_obj = uid_reference_faces.getItem(id);
        const uido = ref_face_obj.uid_object;
        if (ref_face_obj.source == 'partstudio')
            uid_ref_partstudio_faces_raynode.remove(uido.object3d);
        else if (ref_face_obj.source == 'parasolid')
            uid_ref_parasolid_faces_raynode.remove(uido.object3d);
        else if (ref_face_obj.source == 'stl')
            uid_ref_stl_faces_raynode.remove(uido.object3d);
        uid_reference_faces.removeItem(id);

        /* If an STL reference face is deleted,
         * we need to also remove it from imported_stl_models.
         * Note that we don't do the same for imported reference partstudio models,
         * since one such model may consist of multiple bodies.
         * We'll check their status after the bodies are deleted.
         */
        if (ref_face_obj.source == "stl")
            App.state.imported_stl_models = App.state.imported_stl_models.filter(x => x.elementId !== body_id)
        if (uido.object3d && uido.object3d.id)
            uid_object3dIDToUid_object.removeItem(uido.object3d.id);
        uido.object3d.geometry.dispose();
        uido.object3d.material.dispose();
    }

    // Delete each edge
    for (const id of edges_to_delete) {
        const ref_edge_obj = uid_reference_edges.getItem(id);
        const uido = ref_edge_obj.uid_object;
        if (ref_edge_obj.source == "partstudio")
            uid_ref_partstudio_edges_raynode.remove(uido.object3d);
        else if (ref_edge_obj.source == "parasolid")
            uid_ref_parasolid_edges_raynode.remove(uido.object3d);
        else if (ref_edge_obj.source == "stl")
            uid_ref_stl_edges_raynode.remove(uido.object3d);
        uido.remove_visible_edge_mesh(true);
        uid_reference_edges.removeItem(id);
        if (uido.object3d && uido.object3d.id)
            uid_object3dIDToUid_object.removeItem(uido.object3d.id);
        uido.object3d.geometry.dispose();
        uido.object3d.material.dispose();

        // For the visible edge:
        const visible_edge_mesh = uido.get_visible_edge_mesh();
        visible_edge_mesh.geometry.dispose();
        visible_edge_mesh.material.dispose();
    };

    // Delete each vertex
    for (const id of vertices_to_delete) {
        const ref_vertex_obj = uid_reference_vertices.getItem(id);
        const uido = ref_vertex_obj.uid_object;
        if (ref_vertex_obj.source == "partstudio")
            uid_ref_partstudio_vertex_raynode.remove(uido.object3d);
        else if (ref_vertex_obj.source == "parasolid")
            uid_ref_parasolid_vertex_raynode.remove(uido.object3d);
        else if (ref_vertex_obj.source == "stl")
            uid_ref_stl_vertex_raynode.remove(uido.object3d);
        uid_reference_vertices.removeItem(id);
        if (uido.object3d && uido.object3d.id)
        uid_object3dIDToUid_object.removeItem(uido.object3d.id);
        uido.object3d.geometry.dispose();
        uido.object3d.material.dispose();
    };

    // In case this is an onshape imported part studio reference body:
    uid_reference_bodies.removeItem(body_id);

    // clear selected reference faces
    uid_selected_reference_faces = [];

    // if we have no partstudio bodies, remove all loaded partstudio models
    if (
        App.state.imported_partstudio_reference_models.length > 0
        && uid_reference_faces.values().every(face => face.source !== 'partstudio')
    )
        App.state.imported_partstudio_reference_models = [];

    // exit edit ref mode when no refs exist
    if (
        App.state.imported_partstudio_reference_models.length === 0
        && App.state.editing_reference_partstudio
    ) {
        disable_edit_reference_body_mode();
        hide_reference_partstudio_model();
    }

    // exit edit ref stl mode when no stl faces exist
    if (
        uid_ref_stl_faces_raynode.children.length === 0
        && uid_state.editing_reference_stl_body
    ) {
        disable_edit_reference_body_mode();
        hide_reference_stl_model();
    }

    animate();
    request_setting_button_states();
}

export function translate_selected_reference_body(x, y, z) {
  if (uid_selected_reference_faces.length == 0)
    return;

  const [faces_to_translate, edges_to_translate, vertices_to_translate] = collect_reference_objects_of_selected_body();

  var bb = uid_calculate_global_bounding_box(true, false, false);
  //var translate_step = bb.dimension/100;
  var translate_step = viewSize/100;

  var dx = translate_step * x;
  var dy = translate_step * y;
  var dz = translate_step * z;

  // Translate each face
  faces_to_translate.forEach(function(id){
    if (uid_reference_faces.hasItem(id)) {
      var f = uid_reference_faces.getItem(id).uid_object;

      for (var i = 0; i < f.object3d.geometry.attributes.position.array.length / 3.0; i++) {
        f.object3d.geometry.attributes.position.array[ i * 3 ]  += dx;
        f.object3d.geometry.attributes.position.array[ i * 3 + 1 ]  += dy;
        f.object3d.geometry.attributes.position.array[ i * 3 + 2 ]  += dz;
      }

      f.object3d.geometry.attributes.position.needsUpdate = true;
      f.object3d.geometry.attributes.position.needsUpdate = true;
      f.object3d.geometry.verticesNeedUpdate = true;
      f.object3d.geometry.normalsNeedUpdate = true;
      f.object3d.geometry.computeFaceNormals();
      f.object3d.geometry.computeVertexNormals();
      f.object3d.geometry.computeBoundingSphere();
    }
    else {
      display_message_to_user(LOG.WARN, "Error: Callback trying to modify face with id " + id + " which doesn't exist!" );
      console.trace();
    }
  });

  // Translate each edge
  edges_to_translate.forEach(function(id){
    var e = uid_reference_edges.getItem(id).uid_object;
    if (!e)
      return;

    for (var i = 0; i < e.object3d.geometry.vertices.length; i++) {
      e.object3d.geometry.vertices[i].set(e.object3d.geometry.vertices[i].x+dx, e.object3d.geometry.vertices[i].y+dy, e.object3d.geometry.vertices[i].z+dz);
    }
    e.object3d.geometry.verticesNeedUpdate = true;
    e.object3d.geometry.normalsNeedUpdate = true;
    e.object3d.geometry.computeBoundingSphere();

    // For the visible edge:
    var visible_edge_mesh = e.get_visible_edge_mesh();

    var positions = [];
    e.object3d.geometry.vertices.forEach(function(v){
      positions.push(v.x);
      positions.push(v.y);
      positions.push(v.z);
    });
    visible_edge_mesh.geometry.setPositions( positions );
    visible_edge_mesh.computeLineDistances();

    visible_edge_mesh.geometry.attributes.position.needsUpdate = true;
    visible_edge_mesh.geometry.normalsNeedUpdate = true;
    visible_edge_mesh.geometry.computeBoundingSphere();
  });

  // Translate each vertex
  vertices_to_translate.forEach(function(id){
    var v = uid_reference_vertices.getItem(id);
    if (!v.uid_object)
      return;

    // Here we need to calculate a new vertex geometry.
    var newx = v.uid_object.object3d.position.x+dx;
    var newy = v.uid_object.object3d.position.y+dy;
    var newz = v.uid_object.object3d.position.z+dz;

    v.uid_object.object3d.position.x += dx;
    v.uid_object.object3d.position.y += dy;
    v.uid_object.object3d.position.z += dz;

    //uid_add_vertex_geometry(v.uid_object, newx, newy, newz, true, false);
    v.vertex.set(newx, newy, newz);
  });

  update_shadow_light_dimensions();

  animate();
}

export function scale_selected_reference_body(factorx, factory, factorz) {
  if (uid_selected_reference_faces.length == 0)
    return;

  const [faces_to_translate, edges_to_translate, vertices_to_translate] = collect_reference_objects_of_selected_body();

  // Find a rough "center of mass" of the body
  var minx = 10000000;
  var miny = 10000000;
  var minz = 10000000;
  var maxx = -10000000;
  var maxy = -10000000;
  var maxz = -10000000;

  vertices_to_translate.forEach(function(vid) {
    var v = uid_reference_vertices.getItem(vid);
    if (v.uid_object.object3d.position.x < minx) {
      minx = v.uid_object.object3d.position.x;
    }
    if (v.uid_object.object3d.position.x > maxx) {
      maxx = v.uid_object.object3d.position.x;
    }
    if (v.uid_object.object3d.position.y < miny) {
      miny = v.uid_object.object3d.position.y;
    }
    if (v.uid_object.object3d.position.y > maxy) {
      maxy = v.uid_object.object3d.position.y;
    }
    if (v.uid_object.object3d.position.z < minz) {
      minz = v.uid_object.object3d.position.z;
    }
    if (v.uid_object.object3d.position.z > maxz) {
      maxz = v.uid_object.object3d.position.z;
    }
  });
  var centerx = (minx + maxx) / 2.0;
  var centery = (miny + maxy) / 2.0;
  var centerz = (minz + maxz) / 2.0;

  // Scale each vertex
  vertices_to_translate.forEach(function(id){
    var v = uid_reference_vertices.getItem(id);
    if (!v.uid_object)
      return;

    // Here we need to calculate a new vertex geometry.
    var newx = scale_wrt_center(centerx, v.uid_object.object3d.position.x, factorx);
    var newy = scale_wrt_center(centery, v.uid_object.object3d.position.y, factory);
    var newz = scale_wrt_center(centerz, v.uid_object.object3d.position.z, factorz);

    // Identify source of this reference object
    uid_add_vertex_geometry(v.uid_object, newx, newy, newz, true, false, v.source);
    v.vertex.set(newx, newy, newz);
  });


  // Scale each face
  faces_to_translate.forEach(function(id){
    if (uid_reference_faces.hasItem(id)) {
      var f = uid_reference_faces.getItem(id).uid_object;

      for (var i = 0; i < f.object3d.geometry.attributes.position.array.length / 3.0; i++) {
        f.object3d.geometry.attributes.position.array[ i * 3 ]  =
          scale_wrt_center(centerx, f.object3d.geometry.attributes.position.array[ i * 3 ], factorx);
        f.object3d.geometry.attributes.position.array[ i * 3 + 1 ]  =
          scale_wrt_center(centery, f.object3d.geometry.attributes.position.array[ i * 3 + 1 ], factory);
        f.object3d.geometry.attributes.position.array[ i * 3 + 2 ]  =
          scale_wrt_center(centerz, f.object3d.geometry.attributes.position.array[ i * 3 + 2 ], factorz);
      }

      f.object3d.geometry.attributes.position.needsUpdate = true;
      f.object3d.geometry.attributes.position.needsUpdate = true;
      f.object3d.geometry.verticesNeedUpdate = true;
      f.object3d.geometry.normalsNeedUpdate = true;
      f.object3d.geometry.computeFaceNormals();
      f.object3d.geometry.computeVertexNormals();
      f.object3d.geometry.computeBoundingSphere();
    }
    else {
      display_message_to_user(LOG.WARN, "Error: Callback trying to modify face with id " + id + " which doesn't exist!" );
      console.trace();
    }
  });

  // Scale each edge
  edges_to_translate.forEach(function(id){
    var e = uid_reference_edges.getItem(id).uid_object;
    if (!e)
      return;

    for (var i = 0; i < e.object3d.geometry.vertices.length; i++) {
      e.object3d.geometry.vertices[i].set(
        scale_wrt_center(centerx, e.object3d.geometry.vertices[i].x, factorx),
        scale_wrt_center(centery, e.object3d.geometry.vertices[i].y, factory),
        scale_wrt_center(centerz, e.object3d.geometry.vertices[i].z, factorz));
    }
    e.object3d.geometry.verticesNeedUpdate = true;
    e.object3d.geometry.normalsNeedUpdate = true;
    e.object3d.geometry.computeBoundingSphere();

    // For the visible edge:
    var visible_edge_mesh = e.get_visible_edge_mesh();

    var positions = [];
    e.object3d.geometry.vertices.forEach(function(v){
      positions.push(v.x);
      positions.push(v.y);
      positions.push(v.z);
    });
    visible_edge_mesh.geometry.setPositions( positions );
    visible_edge_mesh.computeLineDistances();

    visible_edge_mesh.geometry.attributes.position.needsUpdate = true;
    visible_edge_mesh.geometry.normalsNeedUpdate = true;
    visible_edge_mesh.geometry.computeBoundingSphere();
  });
  animate();
}

export function scale_wrt_center(center, v, factor) {
  return (v-center)*factor + center;
}

/** returns all the faces, edges, vertices
 * of the currently selected reference object
 *
 * @returns {[number[], number[], number[]]} `[faces[], edges[], vertices[]]`
 */
export function collect_reference_objects_of_selected_body() {
  if (uid_selected_reference_faces.length == 0)
    return [];

    /** selected faces */
    const faces = uid_selected_reference_faces
        .map(rf => uid_object3dIDToUid_object.getItem(rf.id).id);

    /** edges of selected faces (selected edges) */
    const edges = faces.flatMap(fid =>
        uid_reference_faces.getItem(fid)
            .edges
            .map(eid => uid_reference_edges.getItem(eid).uid_object.id)
    );

    /** vertices of selected edges (selected vertices) */
    const vertices = faces.flatMap(fid =>
        uid_reference_faces.getItem(fid)
            .edges
            .flatMap(eid =>
                uid_reference_edges.getItem(eid)
                .vertices
                .map(v => uid_reference_vertices.getItem(v).uid_object.id))
    );

    /** get `id` property of object `x` */
    const id = x => x.id;

    /** get item `x` from hashtable `xs` */
    const getFrom = xs => x => xs.getItem(x);

    /** onshape id of the body of the first selected face */
    const bid = pipe(
        uid_selected_reference_faces,
        first,
        id,
        getFrom(uid_object3dIDToUid_object),
        id,
        getFrom(uid_reference_faces),
        x => x.onshape_body_id,
    );

    /** Additional vertices with the same body id as the selected face.
     * This is because ref STL models have no lattice structure,
     * so these objects will not have been included
     */
    const additional_vertices = uid_reference_vertices.values()
        .filter(rv => rv.onshape_body_id === bid)
        .map(rv => rv.uid_object.id);

    // Remove duplicates from all lists
    return [
        unique(faces),
        unique(edges),
        unique([...vertices, ...additional_vertices]),
    ];
}

export function sort_reference_face_vertices(fid) {
  var f = uid_reference_faces.getItem(fid);
  var face_vertices = new Array();
  f.edges.forEach(function(eid){
    var e = uid_reference_edges.getItem(eid);
    e.vertices.forEach(function(vid){
      if (face_vertices.indexOf(vid) == -1)
        face_vertices.push(vid);
    });
  });

  var sorted_vertices = new Array();
  var current_vertex = face_vertices[0];
  var last_edge = uid_reference_vertices.getItem(current_vertex).edges[0];
  while (face_vertices.length > 0) {
    sorted_vertices.push(current_vertex);
    face_vertices.splice(face_vertices.indexOf(current_vertex),1); // Remove vertex from array
    var v_edges = uid_reference_vertices.getItem(current_vertex).edges.slice();
    for (var i = v_edges.length-1; i >= 0; i--)
      if ( f.edges.indexOf(v_edges[i]) == -1)
        v_edges.splice(i, 1);

    if (last_edge == v_edges[0])
      last_edge = v_edges[1];
    else
      last_edge = v_edges[0];

    var last_edge_obj = uid_reference_edges.getItem(last_edge);

    var new_vertex1 =  last_edge_obj.vertices[0];
    var new_vertex2 = last_edge_obj.vertices[1];
    if (sorted_vertices.indexOf(new_vertex1) == -1)
      current_vertex = new_vertex1;
    else
      current_vertex = new_vertex2;
  }
  return sorted_vertices;
}

export function sort_reference_vertices_in_edge_list (edge_list, short_edge_list, adjusted_ref_edges_hash) {

  var list_vertices = new Array();
  edge_list.forEach(function(eid){
    var e = uid_reference_edges.getItem(eid);
    e.vertices.forEach(function(vid){
      if (list_vertices.indexOf(vid) == -1)
        list_vertices.push(vid);
    });
  });

  var sorted_reference_vertices = new Array();
  var current_vertex = list_vertices[0];
  var last_edge = uid_reference_vertices.getItem(current_vertex).edges[0];
  while (list_vertices.length > 0) {
    sorted_reference_vertices.push(current_vertex);
    list_vertices.splice(list_vertices.indexOf(current_vertex),1); // Remove vertex from array
    var v_edges = uid_reference_vertices.getItem(current_vertex).edges.slice();
    for (var i = v_edges.length - 1; i >= 0; i--)
      if (edge_list.indexOf(v_edges[i]) == -1)
        v_edges.splice(i, 1);

    if (last_edge == v_edges[0])
      last_edge = v_edges[1];
    else
      last_edge = v_edges[0];

    var last_edge_obj = uid_reference_edges.getItem(last_edge);

    var new_vertex1 =  last_edge_obj.vertices[0];
    var new_vertex2 = last_edge_obj.vertices[1];
    if (sorted_reference_vertices.indexOf(new_vertex1) == -1)
      current_vertex = new_vertex1;
    else
      current_vertex = new_vertex2;
  }

  // Fill in the adjusted_ref_edges_hash
  edge_list.forEach(function(eid){
    var e = uid_reference_edges.getItem(eid);
    var tmpre = new Uid_tmp_ref_edge(eid);
    e.vertices.forEach(function(vid){
      if (sorted_reference_vertices.indexOf(vid) != -1) {
        if (tmpre.v1id == 0)
          tmpre.v1id = vid;
        else
          tmpre.v2id = vid;
        }
    });
    e.points.forEach(function(p){
      tmpre.points.push(p);
    });
    adjusted_ref_edges_hash.setItem(eid, tmpre);
  });


  // Before returning the list of vertices, we look for any short edges. For any such edges we replace the one vertex with the previous vertex in the
  // sorted list, and we also remove that vertex from the sorted list.
  short_edge_list.forEach(function(reid){
    var re = uid_reference_edges.getItem(reid);
    var rv1 = uid_reference_vertices.getItem(re.vertices[0]);
    var rv2 = uid_reference_vertices.getItem(re.vertices[1]);
    var i1 = rv1.uid_object.id;
    var i2 = 0;
    // Find vertex adjacent to first vertex of this edge, and substitute it for the edge.
    for (var i = sorted_reference_vertices.length-1; i > 0; i--) {
      if (sorted_reference_vertices[i] == rv1.uid_object.id) {
        var next_index = i < sorted_reference_vertices.length-1 ? i+1 : 0;
        var prev_index = i > 0 ? i-1 : sorted_reference_vertices.length-1;
        if (sorted_reference_vertices[next_index] == rv2.uid_object.id) {
          i2 = sorted_reference_vertices[prev_index];
        }
        else if (sorted_reference_vertices[prev_index] == rv2.uid_object.id) {
          i2 = sorted_reference_vertices[next_index];
        }
        else {
          console_log_xml("ERROR in identifying vertex to replace for short reference edge");
          return;
        }
        // Find reference edge sharing i1 and i2, which is the good, adjacent edge.
        adjusted_ref_edges_hash.values().forEach(function(re) {
          if ((re.v1id == i1 && re.v2id == i2) || (re.v1id == i2 && re.v2id == i1)) {
            // Identified the adjacent edge, which has vertex ids i1 and i2. Must replace the one that is equal to rv1 with rv2
            if (re.v1id == rv1.uid_object.id) {
              re.v1id = rv2.uid_object.id;
            }
            else if (re.v2id == rv1.uid_object.id) {
              re.v2id = rv2.uid_object.id;
            }
            else {
              console_log_xml("ERROR fixing up reference edge");
            }
            // Must also fix samples probably XXX
          }
        });
        sorted_reference_vertices.splice(i,1);
        adjusted_ref_edges_hash.removeItem(reid);
      }
    }
  });

  return sorted_reference_vertices;
}

// ---------------------------------------------------
// Ray casting
// ---------------------------------------------------

export function raycaster_intersect_objects(raycaster, nodes) {
    //log_reset_timer("A");
    const intersects = raycaster.intersectObjects(nodes, true);
    //log_time("Time raycasting: ", "A");
    // In case we are clipping with planes, remove any intersections that are on currently clipped-out objects.
    // https://stackoverflow.com/questions/41002587/three-js-clipping-and-raycasting/41005143
    return renderer.clippingPlanes.length > 0
        ? intersects.filter((elem) =>
            renderer.clippingPlanes.every(elem2 =>
                elem2.distanceToPoint(elem.point) > 0))
        : intersects;
}

export function find_intersection_object(intersects, also_find_alternative_coincident_objects = false) {
  var uido;
  if (intersects.length == 0)
    return null;

  // First we want to check whether all of the objects hit are orthogonal planes. If so, we return the first one.
  // Same for symmetry plane or mirror plane
  if ((intersects.length >= 1) && (intersects.length <= 4)) {
    var hit_only_helper_planes = true;
    intersects.forEach(function(o) {
      if (!(o.object.name == "xy_axes_plane" ||
          o.object.name == "yz_axes_plane" ||
          o.object.name == "xz_axes_plane" ||
          o.object.name == "symmetry_plane" ||
          o.object.name == "mirror_plane"))
          hit_only_helper_planes = false;
    });

    if (hit_only_helper_planes) {
      // Before returning the closest plane, check whether we have hit both mirror / symmetry and other planes,
      // and sort accordingly
      let i0 = intersects[0];
      let i1 = intersects[1];
      let i2 = intersects[2];

      let thresh = uid_calculate_global_bounding_box(true, false, false).dimension / 300.0;

      if (i0 && i1 && i2 && maths.proximate(i0.distance, i2.distance, thresh)) {
          if (i1.object.name == "symmetry_plane")
            return i1;
          else if (i2.object.name == "symmetry_plane")
            return i2;
          else if (i1.object.name == "mirror_plane")
            return i1;
          else if (i2.object.name == "mirror_plane")
            return i2;
          else
            return i0;
      }
      else if (i0 && i1 && maths.proximate(i0.distance, i1.distance, thresh)) {
        if (i1.object.name == "symmetry_plane")
          return i1;
        else if (i1.object.name == "mirror_plane")
          return i1;
        else
          return i0
      }
      else
       return i0;
    }
  }

  // Otherwise we remove all orthogonal axis entries from the array of intersected objects
  for (var i = intersects.length - 1; i >= 0; i--) {
    if (intersects[i].object.name == "xy_axes_plane" ||
        intersects[i].object.name == "yz_axes_plane" ||
        intersects[i].object.name == "xz_axes_plane" ||
        intersects[i].object.name == "symmetry_plane" ||
        intersects[i].object.name == "mirror_plane") {
      intersects.splice(i, 1);
    }
  }

  // If we are left with nothing, return null
  if (intersects.length == 0)
    return null;

  if (uid_raynode == uid_manip_raynode) {
    // If a manipulator has been displayed, we simply return the first object we hit, excluding the sphere.
    // Otherwise we do something more clever to facilitate picking.

    var intersected_obj = null;
    var dist_first = 0;

    intersects.forEach(function(o) {
      if (o.object.geometry.name != "rigid_move_manip_sphere" && intersected_obj == null)
        intersected_obj = o;
      if (o.object.geometry.name.includes("rigid_move_manip_vectorx_rod") ||
          o.object.geometry.name.includes("rigid_move_manip_vectory_rod") ||
          o.object.geometry.name.includes("rigid_move_manip_vectorz_rod") ||
          o.object.geometry.name == "rigid_move_manip_fence_tube")
        intersected_obj = o;
    });
    return intersected_obj;
  }
  else if (uid_raynode == uid_sketch_plane_raynode) {
    // Do nothing
  }
  else if (App.state.offset_face_dragging) {
    // If offset face dragging we don't want the complex mechanism, just the closest object.
    return intersects[0];
  }
  else {
    var intersected_obj;
    var intersected_obj_type = "";
    var first_intersected_vertex = null;
    var first_intersected_edge = null;
    var dist_first = 0;
    var precision_factor = 0.1;
    var closest_intersected_vertex = null;
    var closest_intersected_vertex_distance = 0;
    var coincident_intersected_vertices = [];
    var ids_already_included = [];

    intersects.forEach(function(o) {
      if (dist_first == 0)
        dist_first = o.distance;

      var uido = uid_object3dIDToUid_object.getItem(o.object.id);
      var type = "";
      if (uido != null) {
        type = uid_object3dIDToUid_object.getItem(o.object.id).type;
      }

      if (type == "vertex") {
        if (closest_intersected_vertex_distance == 0)
          closest_intersected_vertex_distance = o.distance;
        if (o.distance <= closest_intersected_vertex_distance)
          closest_intersected_vertex = o;

        if (also_find_alternative_coincident_objects) {
          // Also add to list of candidate alternative coincident vertices.
          if (ids_already_included.indexOf(uido.id) == -1) {
            coincident_intersected_vertices.push([uido, o]);
            ids_already_included.push(uido.id);
          }
        }
      }

      if (App.state.hidden_edges_visible && type == "edge" && !first_intersected_edge) {
        // If we are seeing hidden edges and we hit an edge which is the first edge we hit, then intersect it
        // regardless of how far back it is.
        first_intersected_edge = o;
      }
      else if ( o.distance - dist_first < uid_raycaster.params.Line.threshold * precision_factor ) {
        if (uido == null) {
          // This is not a standard ui object, so we simply return the first intersection object and finish.
          return intersects[0];
        }

        if (type == "vertex" && !first_intersected_vertex) {
          first_intersected_vertex = o;
          if (also_find_alternative_coincident_objects) {
            // Also add to list of candidate alternative coincident vertices.
            if (ids_already_included.indexOf(uido.id) == -1) {
              coincident_intersected_vertices.push([uido, o]);
              ids_already_included.push(uido.id);
            }
          }
        }
        else if (type == "edge" && !first_intersected_edge)
          first_intersected_edge = o;
      }
    });

    if (first_intersected_vertex) {
      intersected_obj = first_intersected_vertex;
      intersected_obj_type = "vertex";
    }
    else if (first_intersected_edge) {
      intersected_obj = first_intersected_edge;
      intersected_obj_type = "edge";
    }
    else {
      intersected_obj = intersects[0];
    }

    // Do one last check: Have we intersected a vertex that lies on this intersected_obj (if it's an edge or a plane)?
    // If so, use the vertex instead of the object
    uido = uid_object3dIDToUid_object.getItem(intersected_obj.object.id);
    if (uido && uido.type == "edge" && closest_intersected_vertex != null) {
      var uidv = uid_object3dIDToUid_object.getItem(closest_intersected_vertex.object.id);
      if (uil_is_vertex_of_edge(uidv.id, uido.id)) {
        intersected_obj = closest_intersected_vertex;
        intersected_obj_type = "vertex";
      }
    }

    // Remember this intersection.
    last_intersection_coordinates.x = intersected_obj.point.x;
    last_intersection_coordinates.y = intersected_obj.point.y;
    last_intersection_coordinates.z = intersected_obj.point.z;

    if (also_find_alternative_coincident_objects && intersected_obj_type == "vertex"
        && coincident_intersected_vertices.length > 1) {

      // First we sort the array coincident_intersected_vertices based on their Ids
      coincident_intersected_vertices.sort(function(x, y) {
        if (x[0].id < y[0].id) {
          return -1;
        }
        if (x[0].id > y[0].id) {
          return 1;
        }
        return 0;
      });

      var bb = uid_calculate_global_bounding_box(true, true, false);
      var thresh = uid_vertex_diameter / 10.0;
      var io_uido = uid_object3dIDToUid_object.getItem(intersected_obj.object.id);
      // Check for other coincident vertices, remove duplicates and distant ones
      for (var i = coincident_intersected_vertices.length-1; i >= 0; i--) {
        var vo = coincident_intersected_vertices[i][0];
        if (!maths.proximate(v3.point_point_distance(io_uido.object3d.position, vo.object3d.position), 0, thresh)) {
          coincident_intersected_vertices.splice(i,1);
        }
        else
          ids_already_included.push(vo.id);
      }

      // We're now left with coincident vertices only
      // Check if the currently pointed vertex or any coincident are selected
      var selected_vertex_found = false;
      coincident_intersected_vertices.forEach(function(v){
        if (uid_selected_vertices.hasItem(v[0].id))
          selected_vertex_found = true;
      });

      if (coincident_intersected_vertices.length > 1 ) {
        if (!selected_vertex_found) {
          display_message_to_user(LOG.INFO, "There are more than one vertices here! To switch your selection, click twice. <button onclick='display_help_modal(`select_coinciding_vert`)'>Read more...</button>");
          // Perform switch to select the correct vertex
          // Static variable we'll use to decide which one to select
          if ( typeof find_intersection_object.vertex_selection_count == 'undefined' )
            find_intersection_object.vertex_selection_count = 0;

          if (find_intersection_object.vertex_selection_count > coincident_intersected_vertices.length-1)
            find_intersection_object.vertex_selection_count = 0;

          intersected_obj = coincident_intersected_vertices[find_intersection_object.vertex_selection_count][1];

          // Temporarily highlight corresponding edges.
          var es = uil_get_edges_of_vertex(coincident_intersected_vertices[find_intersection_object.vertex_selection_count][0].id);
          es.forEach(function(eid){
            uid_highlight_object(uid_edges.getItem(eid).object3d);
          });
          setTimeout(function() {
            uid_unhighlight_all_objects();
          }, 500);

          find_intersection_object.vertex_selection_count++;
        }
        else {
          // Must return the currently selected vertex so it will be deselected
          coincident_intersected_vertices.forEach(function(v_io) {
            var io_uido = uid_object3dIDToUid_object.getItem(v_io[1].object.id);
            if (uid_selected_vertices.hasItem(io_uido.id))
              intersected_obj = v_io[1];
          });
        }
      }
    }
    return intersected_obj;
  }
}

export function find_currently_pointed_object() {
  // Find normalized mouse coordinates and intersect scene from camera
  uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
  var intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
  var intersected_obj = find_intersection_object(intersects);

  var o3d = null;
  if (intersected_obj)
    o3d = uid_raynode.getObjectById(intersected_obj.object.id);
  var uido = null;
  if (o3d)
    uido = uid_object3dIDToUid_object.getItem(o3d.id);
  return uido;
}

export function find_all_intersection_edge_objects(intersects) {
  var intersected_objects = find_all_intersection_objects(intersects);
  for (var i = intersected_objects.length - 1; i >= 0; i--) {
    var uido = uid_object3dIDToUid_object.getItem(intersected_objects[i].object.id);
    if (uido.type != "edge")
      intersected_objects.splice(i, 1);
  }
  return intersected_objects;
}

export function find_all_intersection_edge_objects_relevant_to_face(intersects, face_obj) {
  var intersected_objects = find_all_intersection_objects_relevant_to_face(intersects, face_obj);
  for (var i = intersected_objects.length - 1; i >= 0; i--) {
    var uido = uid_object3dIDToUid_object.getItem(intersected_objects[i].object.id);
    if (uido.type != "edge")
      intersected_objects.splice(i, 1);
  }
  return intersected_objects;
}

export function find_all_intersection_objects(intersects) {
  var intersected_objects = new Array();
  var ids_of_objects_already_hit = [];

  intersects.forEach(function(o) {
    var uido = uid_object3dIDToUid_object.getItem(o.object.id);
    if ((uido) && (ids_of_objects_already_hit.indexOf(uido.id) == -1)) {
      intersected_objects.push(o);
      ids_of_objects_already_hit.push(uido.id);
    }
  });
  return intersected_objects;
}

export function find_all_intersection_objects_relevant_to_face(intersects, face_obj) {

  // Initialize array for returning objects
  var intersected_objects = new Array();

  var ids_of_objects_already_hit = [];
  var found_face = false;
  var found_edge = false;
  var found_vertex = false;
  intersects.forEach(function(o) {
    var uido = uid_object3dIDToUid_object.getItem(o.object.id);
    if (uido) {
      if (ids_of_objects_already_hit.indexOf(uido.id) == -1) {
        ids_of_objects_already_hit.push(uido.id);
        if ((face_obj == null) ||
            (face_obj && ((uido.type == "edge" && uil_is_edge_of_face(uido.id, face_obj.id)) ||
                          (uido.type == "vertex" && uil_is_vertex_of_face(uido.id, face_obj.id)) ||
                          (uido.type == "face" && (uido.id == face_obj.id))))) {
          intersected_objects.push(o);
          if (uido.type == "face")
            found_face = true;
          if (uido.type == "edge")
            found_edge = true;
          if (uido.type == "vertex")
            found_vertex = true;
        }
      }
    }
  });

  // One final check before returning: If we have both faces, edges and vertices in the list, remove the faces.
  if (found_face && (found_edge || found_vertex)) {
    for (var i = intersected_objects.length - 1; i >= 0; i--) {
      var uido = uid_object3dIDToUid_object.getItem(intersected_objects[i].object.id);
      if (uido.type == "face")
        intersected_objects.splice(i, 1);
    }
  }

  return intersected_objects;
}

export function object_with_id_has_been_intersected(intersects, obj_id) {
  var has_been_intersected = false;
  intersects.forEach(function(o) {
    var uido = uid_object3dIDToUid_object.getItem(o.object.id);
    if (uido && uido.id == obj_id)
      has_been_intersected = true;
  });
  return has_been_intersected;
}

export function find_intersection_phi_or_reference_object(intersects, consider_vertices = true, consider_edges = true, consider_faces = true) {

  // Remove all irrelevant objects from the list of intersects
  for ( var i = intersects.length - 1 ; i >= 0 ; i-- ) {
    var o = intersects[i];
    var uido = uid_object3dIDToUid_object.getItem(o.object.id);
    if (!uido ||
        (uido.type == "vertex" && !consider_vertices) ||
        (uido.type == "edge" && !consider_edges) ||
        (uido.type == "face" && !consider_faces))
      intersects.splice(i,1);
  }

  if (intersects.length == 0) {
    return null;
  }

  var intersected_obj = null;
  var intersected_obj_type = "";
  var first_intersected_vertex = null;
  var first_intersected_edge = null;
  var dist_first = 0;
  var precision_factor = 0.1;
  var closest_intersected_vertex = null;
  var closest_intersected_vertex_distance = 0;

  intersects.forEach(function(o) {
    if (dist_first == 0)
      dist_first = o.distance;

    var uido = uid_object3dIDToUid_object.getItem(o.object.id);
    var type = "";
    if (uido != null) {
      type = uid_object3dIDToUid_object.getItem(o.object.id).type;
    }

    if (type == "vertex") {
      if (closest_intersected_vertex_distance == 0)
        closest_intersected_vertex_distance = o.distance;
      if (o.distance <= closest_intersected_vertex_distance)
        closest_intersected_vertex = o;
    }

    if (App.state.hidden_edges_visible && type == "edge" && !first_intersected_edge) {
      // If we are seeing hidden edges and we hit an edge which is the first edge we hit, then intersect it
      // regardless of how far back it is.
      first_intersected_edge = o;
    }
    else if ( o.distance - dist_first < uid_raycaster.params.Line.threshold * precision_factor ) {
      if (uido == null) {
        // This is not a standard ui object, so we simply return the first intersection object and finish.
        return intersects[0];
      }

      if (type == "vertex" && !first_intersected_vertex) {
        first_intersected_vertex = o;
      }
      else if (type == "edge" && !first_intersected_edge)
        first_intersected_edge = o;
    }
  });

  if (first_intersected_vertex) {
    intersected_obj = first_intersected_vertex;
    intersected_obj_type = "vertex";
  }
  else if (first_intersected_edge) {
    intersected_obj = first_intersected_edge;
    intersected_obj_type = "edge";
  }
  else {
    intersected_obj = intersects[0];
  }

  // Do one last check: Have we intersected a vertex that lies on this intersected_obj (if it's an edge or a plane)?
  // If so, use the vertex instead of the object
  uido = uid_object3dIDToUid_object.getItem(intersected_obj.object.id);
  if (uido && uido.type == "edge" && closest_intersected_vertex != null) {
    var uidv = uid_object3dIDToUid_object.getItem(closest_intersected_vertex.object.id);
    if (uil_is_vertex_of_edge(uidv.id, uido.id)) {
      intersected_obj = closest_intersected_vertex;
      intersected_obj_type = "vertex";
    }
  }

  return intersected_obj;
}

export function find_intersection_face_object(intersects) {
  return find_intersection_phi_or_reference_object(intersects, false, false, true);

  /*
  // Look for the first face object, ignore edges and vertices
  var intersected_obj = null;
  intersects.forEach(function(o) {
    if (!intersected_obj) {
      // Check whether we've hit a regular or reference phi face:
      var uido = uid_object3dIDToUid_object.getItem(o.object.id);
      if (uido && uido.type == "face")
        intersected_obj = o;
    }
  });
  return intersected_obj;
  */
}

export function find_intersection_face_object_with_id(intersects, fid) {
  // Look for the face object that has the given id
  var intersected_obj = null;
  intersects.forEach(function(o) {
    var uido = uid_object3dIDToUid_object.getItem(o.object.id);
    if (uido && !intersected_obj && uido.type == "face" && uido.id == fid)
        intersected_obj = o;
  });
  return intersected_obj;
}

export function find_edge_closest_vertex_to_coordinates(eid, p) {
  if (!p)
    p = new THREE.Vector3(0,0,0);

  var vs = uil_get_vertices_of_edge(eid);
  if (uid_vertices.getItem(vs[0]).object3d.position.distanceTo(p) < uid_vertices.getItem(vs[1]).object3d.position.distanceTo(p))
    return vs[0];
  else
    return vs[1];
}

// ---------------------------------------------------
// Timers
// ---------------------------------------------------

export function log_reset_timer(timer) {
  if (timer == "A") tA0 = tA1 = performance.now();
  else if (timer == "B") tB0 = tB1 = performance.now();
  else if (timer == "C") tC0 = tC1 = performance.now();
  else {
    tA0 = tA1 = performance.now();
    tB0 = tB1 = performance.now();
    tC0 = tC1 = performance.now();
  }
}

export function log_time(log_statement, timer, also_display_for_user = false) {
  var t0, t1;
  if (timer == "A") {
    tA0 = tA1;
    tA1 = performance.now();
    t0 = tA0;
    t1 = tA1;
  } else if (timer == "B") {
    tB0 = tB1;
    tB1 = performance.now();
    t0 = tB0;
    t1 = tB1;
  } else if (timer == "C") {
    tC0 = tC1;
    tC1 = performance.now();
    t0 = tC0;
    t1 = tC1;
  } else {
    console_log_xml("!!! TIMER ERROR !!!");
  }

  if (t1 - t0 > 50) {
    console.log(log_statement + " : " + ((t1 - t0) / 1000).toFixed(3));
    if (also_display_for_user)
      display_message_to_user(LOG.INFO, log_statement + " : " + ((t1 - t0) / 1000).toFixed(3));
  } else {
    console.log(log_statement + " : " + ((t1 - t0) / 1000).toFixed(6));
    if (also_display_for_user)
      display_message_to_user(LOG.INFO, log_statement + " : " + ((t1 - t0) / 1000).toFixed(3));
  }
}

// ---------------------------------------------------
// Displaying messages
// ---------------------------------------------------

export function display_message_to_user_if_necessary() {
  if (uid_selected_vertices.length == 2 && uid_selected_edges.length == 0 && uid_selected_faces.length == 0 && App.state.dragging_type == "none")
    display_advanced_message_to_user("vertexDist");

  /*
  else if (uid_selected_vertices.length == 0 && uid_selected_edges.length == 1 && uid_selected_faces.length == 0 && App.state.dragging_type == "none")
    display_advanced_message_to_user("edgeSmoothness");
  */
}

/** Flag for suppressing user messages, and counting semaphore */
let uid_messages_suppressed_count = 0;

/** Semaphore for suppressing display of messages */
export function suppress_ui_messages() {
  uid_messages_suppressed(true);
  uid_messages_suppressed_count++;
}

export function unsuppress_ui_messages_if_ready() {
  if (uid_messages_suppressed_count > 0) {
    uid_messages_suppressed_count--;
    if (uid_messages_suppressed_count == 0)
      uid_messages_suppressed(false);
  }
}

export function ui_messages_are_suppressed() {
  return uid_messages_suppressed();
}

// ---------------------------------------------------
// Unit handling
// ---------------------------------------------------

export function get_unitless_value_from_display_unit(v) {
  // Returns the unitless value that correspond to this coordinate in the current display units
  if (uil_get_world_scaling_factor() == 0) {
    console_log_xml("Cannot convert coordinates")
    return 0;
  }
  return ( get_global_standard_distance_unit_from_display_unit(v) / uil_get_world_scaling_factor());
}

export function get_display_unit_from_unitless_value(v) {
  // Returns the display unit value that correspond to this coordinate in unitless value
  if (uil_get_world_scaling_factor() == 0) {
    console_log_xml("Cannot convert coordinates")
    return 0;
  }
  return ( get_display_unit_from_global_standard_distance_unit(v * uil_get_world_scaling_factor()) );
}

export function get_unitless_vector_from_display_vector(vec) {
  var vec1 = new THREE.Vector3();
  vec1.x = get_unitless_value_from_display_unit(vec.x);
  vec1.y = get_unitless_value_from_display_unit(vec.y);
  vec1.z = get_unitless_value_from_display_unit(vec.z);
  return(vec1);
}

export function get_display_vector_from_unitless_vector(vec) {
  var vec1 = new THREE.Vector3();
  vec1.x = get_display_unit_from_unitless_value(vec.x);
  vec1.y = get_display_unit_from_unitless_value(vec.y);
  vec1.z = get_display_unit_from_unitless_value(vec.z);
  return(vec1);
}

export function get_global_standard_distance_unit_from_display_unit(v) {
  if (App.state.display_units == "cm")
    return v;
  else if (App.state.display_units == "m")
      return v*100;
  else if (App.state.display_units == "mm")
      return v*0.1;
  else if (App.state.display_units == "ft")
    return v*30.48;
  else if (App.state.display_units == "in")
    return v*2.54;
  else if (App.state.display_units == "yd")
    return v*91.44;
  else {
    console_log_xml("Cannot convert coordinates")
    return 0;
  }
}

export function get_display_unit_from_global_standard_distance_unit(v) {
  if (App.state.display_units == "cm")
    return v;
  else if (App.state.display_units == "m")
      return v/100;
  else if (App.state.display_units == "mm")
      return v/0.1;
  else if (App.state.display_units == "ft")
    return v/30.48;
  else if (App.state.display_units == "in")
    return v/2.54;
  else if (App.state.display_units == "yd")
    return v/91.44;
  else {
    console_log_xml("Cannot convert coordinates")
    return 0;
  }
}

// TODO: this should ideally be in the currentoperation vue component, but it requires a significant refactor of uid_state
export function calculate_current_operation() {
  // Handle each case.
  if (App.state.offset_face_selecting_vertex)
    return "Click on vertex...";
  else if (App.state.offset_face_dragging)
    return "Now move mouse to change offset size, then click anywhere to complete operation.";
  else if (App.state.adding_vertex)
    return "Click on any edge to add a vertex.";
  else if (App.state.setting_mirror_plane)
    return "Define mirror plane (click on a coordinate plane, a face, or any three vertices).";
  else if (App.state.setting_symmetry_plane)
    return "Define symmetry plane (click on a coordinate plane, a face, or any three vertices).";
  else if (App.state.selecting_tangent_perpendicular_plane)
    return "Define plane (click on a coordinate or mirror plane, any face, or any three vertices).";
  else if (App.state.carving)
    return "Move mouse slowly over faces, making sure to cross edges. Hit V at any moment to add a vertex on the new edges.";
  else if (App.state.drawing_edge)
    return "Move mouse slowly over faces, making sure to cross edges. Hit V at any moment to add a vertex on the new edges.";
  else if (
    App.state.projecting_edges
    && global_edge_projection_data
    && global_edge_projection_data.projection_type == "plane"
    && global_edge_projection_data.direction == null
  )
    return "Click on an orthogonal plane to project along its normal, then click onto the 'Project' button again.";
  else if (
    App.state.projecting_edges
    && global_edge_projection_data
    && global_edge_projection_data.projection_type == "plane"
    && global_edge_projection_data.direction != null
  )
    return "Click onto the 'Project' button again."
  else if (
    App.state.projecting_edges
    && global_edge_projection_data
    && global_edge_projection_data.projection_type == "face"
  )
    return "Click on one or more faces to select them, then click onto the 'Project' button again.";
  else if (
    App.state.projecting_edges
    && global_edge_projection_data
    && global_edge_projection_data.projection_type == "view"
  )
    return "Rotate the view and hit project again. The projection will take place perpendicular to your screen.";
  else if (
    App.state.projecting_edges
    && global_edge_projection_data
    && global_edge_projection_data.projection_type == "edge"
  )
    return "Click on a linear edge to project along its direction, then click onto the 'Project' button again.";
  else if (App.state.snapping_to_object) {
    if (uid_selected_vertices.length == 1)
      return "Click on a vertex or an edge to snap onto it. Hold shift to move the entire body.";
    else if (uid_selected_edges.length == 1)
      return "Click on another edge to snap onto it. Holding shift will snap on a part of the target edge.";
    else if (uid_selected_faces.length == 1)
      return "Click on another face with the same number of edges around it to to snap onto it.";
  }

  else
    return '';
}
